// Puck Bundle - Webpack entry point that bundles Puck with our integration
import React from "react";
import ReactDOM from "react-dom/client";
import { Puck, Render } from "@puckeditor/core";
import "@puckeditor/core/puck.css";

// TipTap imports for rich text editing
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";

// Import utilities and fields from modular structure
import { AccessibilityWarning, applyAuthToButtons } from "./utils/index.js";
import {
  ColorPickerField,
  MediaField,
  LinkModal,
  VideoAltTextField,
  videoA11yProps
} from "./fields/index.js";
import {
  createWelcomeBlock,
  createContainerBlock
} from "./components/index.js";

// Swipe / Before-After comparison widget (Feature #9271) — ported design-system component.
// The CSS import goes through style-loader (injected into <head> for both the editor and
// public hub pages); piSwipeInit initializes the native-range slider after each render.
import { piSwipeInit } from "../swipe-comparison.js";
import "../../Content/swipe-comparison.css";

// Community Q&A widget runtime (AB#9284) — self-contained vanilla module + global CSS.
import { piQaInit } from "../qa-tool.js";
import { coreApiClient } from "../Modules/shared/services/CoreApiClient";
import "../../Content/qa-tool.css";

// Converts an HTML string to decoded plain text: strips tags AND decodes
// entities (&nbsp; &amp; &ndash; ...). Use this before rendering server HTML
// as a React text node — a bare regex tag-strip leaves entities as literal
// text on the page.
function htmlToPlainText(html) {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  // textContent joins adjacent blocks with no separator ("<p>a</p><p>b</p>"
  // would become "ab"); insert a space after each block-level element first.
  doc.body
    .querySelectorAll("p, br, div, li, h1, h2, h3, h4, h5, h6, tr")
    .forEach(function (el) {
      el.after(" ");
    });
  // Non-breaking spaces decode to U+00A0; normalize to regular spaces since
  // non-breaking behavior is meaningless in stripped/truncated card text,
  // then collapse whitespace runs introduced by the separators above.
  return (doc.body.textContent || "")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// DOMParser hoists head-eligible elements out of the body, so returning only
// doc.body.innerHTML silently drops a top-level <style> in an HtmlBlock.
// Re-serialize STYLE elements ahead of the body content.
//
// SECURITY: the STYLE-only allowlist is deliberate. Head-hoisted LINK
// (external stylesheet loads), SCRIPT, META, TITLE, and especially BASE
// (which would silently rewrite every relative URL on the page) remain
// dropped, exactly as before this fix. Do not widen this check without a
// security review.
function serializeParsedHtml(doc) {
  let headMarkup = "";
  Array.prototype.forEach.call(doc.head.children, function (el) {
    if (el.tagName === "STYLE") {
      headMarkup += el.outerHTML;
    }
  });
  return headMarkup + doc.body.innerHTML;
}

// Ensures any <iframe> in an HTML string has title and aria-label attributes.
// Applies to user-generated content (HtmlBlock, RichText) before dangerouslySetInnerHTML.
function ensureIframeTitles(html) {
  if (!html) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("iframe").forEach(function (iframe) {
    const existing =
      iframe.getAttribute("title") ||
      iframe.getAttribute("aria-label") ||
      "Embedded content";
    if (!iframe.getAttribute("title")) iframe.setAttribute("title", existing);
    if (!iframe.getAttribute("aria-label"))
      iframe.setAttribute("aria-label", existing);
  });
  return serializeParsedHtml(doc);
}

// Ensures any <img> in an HTML string has an alt attribute.
// Empty alt="" marks the image as decorative (valid per WCAG) and prevents missing-alt violations
// when admins embed images in rich text without writing alt text.
function ensureImageAlts(html) {
  if (!html) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("img:not([alt])").forEach(function (img) {
    img.setAttribute("alt", "");
  });
  return serializeParsedHtml(doc);
}

// Applies all WCAG accessibility fixes to user-generated HTML before rendering.
// Use this instead of bare dangerouslySetInnerHTML for any admin/user-authored content.
//
// NOTE: this performs a11y fixes only — it is NOT an XSS sanitizer. Every
// consumer renders ADMIN-AUTHORED content, and all of those surfaces are
// style-capable by design (an in-body <style> has always survived; the
// serializeParsedHtml change only stops a leading <style> from being
// accidentally dropped). Never route untrusted (public/LLM) input through
// this into dangerouslySetInnerHTML.
function sanitizeUserHtml(html) {
  if (!html) return html;
  return ensureImageAlts(ensureIframeTitles(html));
}

// Constants for magic numbers
const BOOTSTRAP_GRID_COLUMNS = 12;
const DEFAULT_ASPECT_RATIO = 3;
const DEFAULT_DESCRIPTION_MAX_LENGTH = 200;
const MIN_PARTICIPANT_COUNT_FOR_DISPLAY = 10;
const DEFAULT_FEATURED_PROJECTS_COUNT = 3;
const DEFAULT_EVENTS_COUNT = 5;
const MAX_EVENTS_COUNT = 10;
const DESCRIPTION_TRUNCATE_LENGTH = 150;
const HUB_ID_CHECK_INTERVAL_MS = 500;
const HUB_ID_TIMEOUT_MS = 2000;
const EVENTS_FETCH_TIMEOUT_MS = 10000;
const TAB_LOAD_DELAY_MS = 100;
const TAB_ACTIVATION_DELAY_MS = 50;
const MOBILE_VIEWPORT_WIDTH = 375;
const TABLET_VIEWPORT_WIDTH = 768;
const DESKTOP_VIEWPORT_WIDTH = 1152;
// Magic number constants for ESLint compliance
const OPACITY_70 = 0.7;
// Heading levels
const HEADING_LEVEL_MIN = 1;
const HEADING_LEVEL_MAX = 6;
const HEADING_LEVEL_2 = 2;
const HEADING_LEVEL_3 = 3;
const HEADING_LEVEL_4 = 4;
const HEADING_LEVEL_5 = 5;
// Table defaults
const DEFAULT_TABLE_ROWS = 3;
const DEFAULT_TABLE_COLS = 3;
// Number formatting
const MILLION_THRESHOLD = 999999;
const MILLION_DIVISOR = 1000000;
const DECIMAL_PLACES = 10;
const THOUSAND_THRESHOLD = 1000;
const THOUSAND_DIVISOR = 1000;
// parseInt radix
const PARSE_INT_RADIX_DECIMAL = 10;
// RGBA array indices
const RGBA_R_INDEX = 1;
const RGBA_G_INDEX = 2;
const RGBA_B_INDEX = 3;
const RGBA_A_INDEX = 4;
// Image sizes
const DEFAULT_IMAGE_SIZE_PX = 120;
const MAX_COMPACT_IMAGE_SIZE_PX = 80;
// Rem conversion (16px = 1rem)
const PX_TO_REM_BASE = 16;
// Padding/spacing
const IMAGE_OFFSET_PADDING_REM = 1.75;
// Random string generation
const RADIX_36 = 36;
const RANDOM_STRING_START = 2;
const RANDOM_STRING_LENGTH = 9;
// Hex color parsing offsets
const HEX_COLOR_START_INDEX = 0;
const HEX_COLOR_R_END_INDEX = 2;
const HEX_COLOR_G_START_INDEX = 2;
const HEX_COLOR_B_START_INDEX = 4;
const HEX_RADIX = 16;
// Font sizes in rem
const FONT_SIZE_REM_1_125_STR = "1.125rem"; // String version for inline styles
const FONT_SIZE_REM_1_75_STR = "1.75rem"; // String version for inline styles
const IMAGE_OFFSET_DIVISOR = 2;
// Video/image styling
const VIDEO_Z_INDEX = 0;
// Form and UI constants
const TEXTAREA_ROWS_DEFAULT = 10;
const PROGRESS_BAR_DEFAULT_VALUE = 50;
const PROGRESS_BAR_MAX_VALUE = 100;
const COLUMN_COUNT_1 = 1;
const COLUMN_COUNT_2 = 2;
const COLUMN_COUNT_3 = 3;
const COLUMN_COUNT_4 = 4;
const ASPECT_RATIO_UNSET = 3;
const ASPECT_RATIO_ONE_BY_ONE = 2;
const BOOTSTRAP_COL_MD_10 = 10;
const BOOTSTRAP_COL_SM_9 = 9;
const BOOTSTRAP_COL_12 = 12;
const DEFAULT_PROJECT_COUNT = 3;
const MAX_PROJECT_COUNT = 6;
// Additional magic number constants
const CALC_PADDING_PX = 8;
const OPACITY_075 = 0.75;
const OPACITY_05 = 0.5;
const FONT_SIZE_PX_12 = 12;
const BORDER_RADIUS_4_PX = 4;

/**
 * Re-export ContrastChecker for backward compatibility (if used elsewhere)
 * Note: ContrastChecker is available via AccessibilityWarning if needed
 */

/**
 * Title color field with accessibility warnings
 * Uses imported ColorPickerField and AccessibilityWarning
 */
function TitleColorFieldWithWarning({ field, onChange, value, ...props }) {
  /**
   * Try to get component data from various possible sources
   * Puck may pass component props or we can access via window/context
   */
  const [componentData, setComponentData] = React.useState(null);

  React.useEffect(function () {
    /**
     * Try to get current component data from Puck's internal state
     * This is a workaround - Puck doesn't directly expose this to field renderers
     */
    function checkForComponentData() {
      // Check if we can access Puck's editor state
      if (window.getPuckEditorData) {
        const data = window.getPuckEditorData();
        if (data && data.content) {
          // Find the Hero component
          const heroComponent = data.content.find(function (c) {
            return c.type === "Hero";
          });
          if (heroComponent && heroComponent.props) {
            setComponentData(heroComponent.props);
            return;
          }
        }
      }
      setComponentData(null);
    }

    checkForComponentData();

    // Listen for Puck data changes
    function handleDataChange() {
      checkForComponentData();
    }

    document.addEventListener("puck:dataChange", handleDataChange);
    return function () {
      document.removeEventListener("puck:dataChange", handleDataChange);
    };
  }, []);

  // Calculate effective background for contrast checking
  const gradient = componentData?.gradient || {};
  const image = componentData?.image || {};
  const video = componentData?.video || {};
  const backgroundType = componentData?.backgroundType || "none";
  const textOverlay = componentData?.textOverlay || {};

  // Helper function to ensure color is a string
  function ensureColorString(color) {
    if (!color) {
      return null;
    }
    if (typeof color === "string") {
      return color;
    }
    if (
      typeof color === "object" &&
      typeof color.r !== "undefined" &&
      typeof color.g !== "undefined" &&
      typeof color.b !== "undefined"
    ) {
      const a = typeof color.a !== "undefined" ? color.a : 1;
      return `rgba(${Math.round(color.r)}, ${Math.round(color.g)}, ${Math.round(
        color.b
      )}, ${a})`;
    }
    return String(color);
  }

  // Check if there's a video or image background without text overlay
  const hasImageBackground =
    backgroundType === "image" && image && image.url && image.mode === "bg";
  const hasVideoBackground = backgroundType === "video" && video && video.url;
  const hasBackgroundWithoutOverlay =
    (hasImageBackground || hasVideoBackground) &&
    (!textOverlay || !textOverlay.enabled);

  /**
   * Calculate background color fallback (gradient > background image overlay > white)
   * Overlay color will be passed separately and prioritized in AccessibilityWarning
   */
  let backgroundColor;
  if (gradient && gradient.enabled && gradient.color) {
    backgroundColor = ensureColorString(gradient.color);
  } else if (image && image.mode === "bg" && image.url) {
    backgroundColor = "rgba(0, 0, 0, 0.5)";
  } else {
    backgroundColor = "#ffffff";
  }

  // Overlay props for contrast checking
  const overlayEnabled = textOverlay && textOverlay.enabled;
  const overlayColor =
    overlayEnabled && textOverlay.backgroundColor
      ? ensureColorString(textOverlay.backgroundColor)
      : null;

  // Ensure textColor is also a string
  const textColorString = ensureColorString(value);

  // Get typography settings for title
  const titleTypography = componentData?.titleTypography || {
    fontSize: "40",
    fontWeight: "700"
  };

  return React.createElement(
    "div",
    {},
    React.createElement(ColorPickerField, { field, onChange, value, ...props }),
    hasBackgroundWithoutOverlay
      ? React.createElement(
          "div",
          {
            className: "alert alert-warning mt-2 mb-2",
            style: {
              fontSize: "0.875rem",
              padding: "0.5rem 0.75rem",
              display: "flex",
              alignItems: "flex-start",
              gap: "0.5rem"
            }
          },
          React.createElement("i", {
            className: "fas fa-exclamation-triangle",
            style: { marginTop: "0.125rem", flexShrink: 0 }
          }),
          React.createElement(
            "div",
            { style: { flex: 1 } },
            React.createElement(
              "strong",
              { style: { display: "block", marginBottom: "0.25rem" } },
              "Manual Contrast Check Required"
            ),
            React.createElement(
              "span",
              {},
              "With a background image or video and no text overlay box, we cannot automatically calculate contrast. ",
              "Please manually verify that your text color meets WCAG contrast requirements (4.5:1 for normal text, 3:1 for large text). ",
              "Consider using a text overlay box or ensuring sufficient contrast between your text and the background."
            )
          )
        )
      : React.createElement(AccessibilityWarning, {
          textColor: textColorString,
          backgroundColor: backgroundColor,
          overlayEnabled: overlayEnabled,
          overlayColor: overlayColor,
          fontSize: parseInt(titleTypography.fontSize || "40"),
          fontWeight: parseInt(titleTypography.fontWeight || "700"),
          fieldName: "title"
        })
  );
}

/**
 * Description color field with accessibility warnings
 * Uses imported ColorPickerField and AccessibilityWarning
 */
function DescriptionColorFieldWithWarning({
  field,
  onChange,
  value,
  ...props
}) {
  /**
   * Try to get component data from various possible sources
   * Puck may pass component props or we can access via window/context
   */
  const [componentData, setComponentData] = React.useState(null);

  React.useEffect(function () {
    /**
     * Try to get current component data from Puck's internal state
     * This is a workaround - Puck doesn't directly expose this to field renderers
     */
    function checkForComponentData() {
      // Check if we can access Puck's editor state
      if (window.getPuckEditorData) {
        const data = window.getPuckEditorData();
        if (data && data.content) {
          // Find the Hero component
          const heroComponent = data.content.find(function (c) {
            return c.type === "Hero";
          });
          if (heroComponent && heroComponent.props) {
            setComponentData(heroComponent.props);
            return;
          }
        }
      }
      setComponentData(null);
    }

    checkForComponentData();

    // Listen for Puck data changes
    function handleDataChange() {
      checkForComponentData();
    }

    document.addEventListener("puck:dataChange", handleDataChange);
    return function () {
      document.removeEventListener("puck:dataChange", handleDataChange);
    };
  }, []);

  // Calculate effective background for contrast checking
  const gradient = componentData?.gradient || {};
  const image = componentData?.image || {};
  const video = componentData?.video || {};
  const backgroundType = componentData?.backgroundType || "none";
  const textOverlay = componentData?.textOverlay || {};

  // Helper function to ensure color is a string
  function ensureColorString(color) {
    if (!color) {
      return null;
    }
    if (typeof color === "string") {
      return color;
    }
    if (
      typeof color === "object" &&
      typeof color.r !== "undefined" &&
      typeof color.g !== "undefined" &&
      typeof color.b !== "undefined"
    ) {
      const a = typeof color.a !== "undefined" ? color.a : 1;
      return `rgba(${Math.round(color.r)}, ${Math.round(color.g)}, ${Math.round(
        color.b
      )}, ${a})`;
    }
    return String(color);
  }

  // Check if there's a video or image background without text overlay
  const hasImageBackground =
    backgroundType === "image" && image && image.url && image.mode === "bg";
  const hasVideoBackground = backgroundType === "video" && video && video.url;
  const hasBackgroundWithoutOverlay =
    (hasImageBackground || hasVideoBackground) &&
    (!textOverlay || !textOverlay.enabled);

  /**
   * Calculate background color fallback (gradient > background image overlay > white)
   * Overlay color will be passed separately and prioritized in AccessibilityWarning
   */
  let backgroundColor;
  if (gradient && gradient.enabled && gradient.color) {
    backgroundColor = ensureColorString(gradient.color);
  } else if (image && image.mode === "bg" && image.url) {
    backgroundColor = "rgba(0, 0, 0, 0.5)";
  } else {
    backgroundColor = "#ffffff";
  }

  // Overlay props for contrast checking
  const overlayEnabled = textOverlay && textOverlay.enabled;
  const overlayColor =
    overlayEnabled && textOverlay.backgroundColor
      ? ensureColorString(textOverlay.backgroundColor)
      : null;

  // Ensure textColor is also a string
  const textColorString = ensureColorString(value);

  // Get typography settings for description
  const descTypography = componentData?.descriptionTypography || {
    fontSize: "18",
    fontWeight: "400"
  };

  return React.createElement(
    "div",
    {},
    React.createElement(ColorPickerField, { field, onChange, value, ...props }),
    hasBackgroundWithoutOverlay
      ? React.createElement(
          "div",
          {
            className: "alert alert-warning mt-2 mb-2",
            style: {
              fontSize: "0.875rem",
              padding: "0.5rem 0.75rem",
              display: "flex",
              alignItems: "flex-start",
              gap: "0.5rem"
            }
          },
          React.createElement("i", {
            className: "fas fa-exclamation-triangle",
            style: { marginTop: "0.125rem", flexShrink: 0 }
          }),
          React.createElement(
            "div",
            { style: { flex: 1 } },
            React.createElement(
              "strong",
              { style: { display: "block", marginBottom: "0.25rem" } },
              "Manual Contrast Check Required"
            ),
            React.createElement(
              "span",
              {},
              "With a background image or video and no text overlay box, we cannot automatically calculate contrast. ",
              "Please manually verify that your text color meets WCAG contrast requirements (4.5:1 for normal text, 3:1 for large text). ",
              "Consider using a text overlay box or ensuring sufficient contrast between your text and the background."
            )
          )
        )
      : React.createElement(AccessibilityWarning, {
          textColor: textColorString,
          backgroundColor: backgroundColor,
          overlayEnabled: overlayEnabled,
          overlayColor: overlayColor,
          fontSize: parseInt(descTypography.fontSize || "18"),
          fontWeight: parseInt(descTypography.fontWeight || "400"),
          fieldName: "description"
        })
  );
}

/**
 * Video field component with URL requirements
 */
function VideoField({ field, onChange, value }) {
  return React.createElement(
    "div",
    { className: "puck-field" },
    React.createElement(
      "label",
      { className: "puck-field-label" },
      field.label || "Video URL"
    ),
    React.createElement("input", {
      type: "text",
      value: value || "",
      onChange: (e) => onChange(e.target.value),
      className: "form-control",
      placeholder: "https://example.com/video.mp4",
      style: { marginBottom: "0.5rem" }
    }),
    React.createElement(
      "div",
      {
        className: "alert alert-info",
        style: {
          fontSize: "0.875rem",
          padding: "0.75rem",
          marginTop: "0.5rem",
          marginBottom: 0
        }
      },
      React.createElement("strong", {}, "Video URL Requirements:"),
      React.createElement(
        "ul",
        {
          style: {
            marginBottom: 0,
            paddingLeft: "1.25rem",
            marginTop: "0.5rem"
          }
        },
        React.createElement(
          "li",
          {},
          "Must be a direct link to a video file (MP4, WebM, or OGG format)"
        ),
        React.createElement(
          "li",
          {},
          "Video should be optimized for web (recommended: H.264 codec, max 10MB)"
        ),
        React.createElement(
          "li",
          {},
          "URL must be publicly accessible (no authentication required)"
        ),
        React.createElement("li", {}, "For best performance, use HTTPS URLs"),
        React.createElement(
          "li",
          {},
          "Recommended: MP4 format with H.264 codec for maximum browser compatibility"
        )
      )
    )
  );
}

/**
 * MediaField is now imported from fields/index.js
 * Keeping local reference for backward compatibility
 */
const MediaFieldLocal = MediaField;

/**
 * ColorPickerField is now imported from fields/index.js
 * Keeping local reference for backward compatibility
 */
const ColorPickerFieldLocal = ColorPickerField;

/**
 * LinkModal is now imported from fields/index.js
 * Keeping local reference for backward compatibility
 */
const LinkModalLocal = LinkModal;

/**
 * Rich text field component with TipTap
 * Note: RichTextField still defined here as it has complex TipTap integration
 * Can be moved to fields/richTextField.js in future refactoring
 */
function RichTextField({ field, onChange, value }) {
  const [isInTable, setIsInTable] = React.useState(false);
  const [showLinkModal, setShowLinkModal] = React.useState(false);
  const [linkModalData, setLinkModalData] = React.useState({
    url: "",
    target: "_self"
  });
  /* Track the last value we set from the editor to prevent circular updates */
  const lastEditorValueRef = React.useRef(value || "");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [
            HEADING_LEVEL_MIN,
            HEADING_LEVEL_2,
            HEADING_LEVEL_3,
            HEADING_LEVEL_4,
            HEADING_LEVEL_5,
            HEADING_LEVEL_MAX
          ]
        }
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: "_blank",
          rel: "noopener noreferrer"
        }
      }),
      Highlight.configure({
        multicolor: true
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"]
      }),
      Underline,
      TextStyle,
      Color,
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: "table table-bordered"
        }
      }),
      TableRow,
      TableHeader,
      TableCell
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "prose prose-sm focus:outline-none"
      }
    },
    onUpdate: function (updateData) {
      const html = updateData.editor.getHTML();
      lastEditorValueRef.current = html;
      onChange(html);
      setIsInTable(updateData.editor.isActive("table"));
    },
    onSelectionUpdate: function (selectionData) {
      setIsInTable(selectionData.editor.isActive("table"));
    },
    /* Enable paste from Word and other rich text sources */
    enablePasteRules: true,
    enableInputRules: true
  });

  React.useEffect(
    function () {
      /*
       * Only update editor content if:
       * 1. Editor exists
       * 2. Value prop is different from current editor content
       * 3. Value prop is different from what we last set from the editor (prevents circular updates)
       * 4. Editor is not focused (user is not actively typing)
       */
      if (
        editor &&
        value !== editor.getHTML() &&
        value !== lastEditorValueRef.current &&
        !editor.isFocused
      ) {
        editor.commands.setContent(value || "");
        lastEditorValueRef.current = value || "";
      }
    },
    [value, editor]
  );

  if (!editor) {
    return null;
  }

  const setLink = React.useCallback(
    function () {
      const attrs = editor.getAttributes("link");
      const previousUrl = attrs.href || "";
      const previousTarget = attrs.target || "_self";

      setLinkModalData({ url: previousUrl, target: previousTarget });
      setShowLinkModal(true);
    },
    [editor]
  );

  const handleLinkSave = React.useCallback(
    function (url, target) {
      if (!url) {
        editor.chain().focus().extendMarkRange("link").unsetLink().run();
      } else {
        editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .setLink({
            href: url,
            target: target || "_self",
            rel: target === "_blank" ? "noopener noreferrer" : void 0
          })
          .run();
      }
      setShowLinkModal(false);
    },
    [editor]
  );

  // Get tab info from the current tabs in the UI
  function getTabInfo() {
    // Try to get tabs from the unified tabs admin interface
    const tabs = document.querySelectorAll(
      ".unified-nav-tabs .nav-link, .nav-tabs .nav-link"
    );
    const tabInfo = [];

    tabs.forEach(function (tab) {
      // Skip the "Add Tab" button
      if (
        !tab.classList.contains("add-tab-btn") &&
        !tab.querySelector(".fa-plus")
      ) {
        // Get the tab text, excluding any close button
        const titleSpan = tab.querySelector(".tab-title");
        const title = titleSpan
          ? titleSpan.textContent.trim()
          : tab.textContent
              .trim()
              .replace(" ×", "")
              .replace(/\s*×\s*$/, "");

        // Get the href attribute to find the tab ID
        const href = tab.getAttribute("href");
        let tabId = "";
        if (href && href.startsWith("#")) {
          tabId = href.substring(1); // Remove the # prefix
        }

        if (title && tabId) {
          tabInfo.push({
            title: title,
            id: tabId
          });
        }
      }
    });

    return tabInfo;
  }

  return React.createElement(
    "div",
    { className: "rich-text-field" },
    React.createElement(
      "label",
      { className: "puck-field-label" },
      field.label || "Content"
    ),
    React.createElement(
      "div",
      { className: "rich-text-toolbar" },
      // Text formatting buttons
      React.createElement(
        "button",
        {
          type: "button",
          onClick: () => editor.chain().focus().toggleBold().run(),
          className: editor.isActive("bold") ? "is-active" : "",
          title: "Bold"
        },
        React.createElement("i", { className: "fas fa-bold" })
      ),
      React.createElement(
        "button",
        {
          type: "button",
          onClick: () => editor.chain().focus().toggleItalic().run(),
          className: editor.isActive("italic") ? "is-active" : "",
          title: "Italic"
        },
        React.createElement("i", { className: "fas fa-italic" })
      ),
      React.createElement(
        "button",
        {
          type: "button",
          onClick: () => editor.chain().focus().toggleUnderline().run(),
          className: editor.isActive("underline") ? "is-active" : "",
          title: "Underline"
        },
        React.createElement("i", { className: "fas fa-underline" })
      ),
      React.createElement(
        "button",
        {
          type: "button",
          onClick: () => editor.chain().focus().toggleStrike().run(),
          className: editor.isActive("strike") ? "is-active" : "",
          title: "Strikethrough"
        },
        React.createElement("i", { className: "fas fa-strikethrough" })
      ),

      // Separator
      React.createElement("span", { className: "separator" }),

      // Highlight
      React.createElement(
        "button",
        {
          type: "button",
          onClick: () =>
            editor.chain().focus().toggleHighlight({ color: "#fef08a" }).run(),
          className: editor.isActive("highlight") ? "is-active" : "",
          title: "Highlight"
        },
        React.createElement("i", { className: "fas fa-highlighter" })
      ),

      // Link
      React.createElement(
        "button",
        {
          type: "button",
          onClick: setLink,
          className: editor.isActive("link") ? "is-active" : "",
          title: "Link"
        },
        React.createElement("i", { className: "fas fa-link" })
      ),

      // Separator
      React.createElement("span", { className: "separator" }),

      // Headings
      React.createElement(
        "button",
        {
          type: "button",
          onClick: () =>
            editor
              .chain()
              .focus()
              .toggleHeading({ level: HEADING_LEVEL_2 })
              .run(),
          className: editor.isActive("heading", { level: HEADING_LEVEL_2 })
            ? "is-active"
            : "",
          title: "Heading 2"
        },
        "H2"
      ),
      React.createElement(
        "button",
        {
          type: "button",
          onClick: () =>
            editor
              .chain()
              .focus()
              .toggleHeading({ level: HEADING_LEVEL_3 })
              .run(),
          className: editor.isActive("heading", { level: HEADING_LEVEL_3 })
            ? "is-active"
            : "",
          title: "Heading 3"
        },
        "H3"
      ),

      // Separator
      React.createElement("span", { className: "separator" }),

      // Lists
      React.createElement(
        "button",
        {
          type: "button",
          onClick: () => editor.chain().focus().toggleBulletList().run(),
          className: editor.isActive("bulletList") ? "is-active" : "",
          title: "Bullet List"
        },
        React.createElement("i", { className: "fas fa-list-ul" })
      ),
      React.createElement(
        "button",
        {
          type: "button",
          onClick: () => editor.chain().focus().toggleOrderedList().run(),
          className: editor.isActive("orderedList") ? "is-active" : "",
          title: "Numbered List"
        },
        React.createElement("i", { className: "fas fa-list-ol" })
      ),

      // Separator
      React.createElement("span", { className: "separator" }),

      // Text alignment
      React.createElement(
        "button",
        {
          type: "button",
          onClick: () => editor.chain().focus().setTextAlign("left").run(),
          className: editor.isActive({ textAlign: "left" }) ? "is-active" : "",
          title: "Align Left"
        },
        React.createElement("i", { className: "fas fa-align-left" })
      ),
      React.createElement(
        "button",
        {
          type: "button",
          onClick: () => editor.chain().focus().setTextAlign("center").run(),
          className: editor.isActive({ textAlign: "center" })
            ? "is-active"
            : "",
          title: "Align Center"
        },
        React.createElement("i", { className: "fas fa-align-center" })
      ),
      React.createElement(
        "button",
        {
          type: "button",
          onClick: () => editor.chain().focus().setTextAlign("right").run(),
          className: editor.isActive({ textAlign: "right" }) ? "is-active" : "",
          title: "Align Right"
        },
        React.createElement("i", { className: "fas fa-align-right" })
      ),

      // Separator
      React.createElement("span", { className: "separator" }),

      // Table controls - Insert table button (always visible)
      React.createElement(
        "button",
        {
          type: "button",
          onClick: () =>
            editor
              .chain()
              .focus()
              .insertTable({
                rows: DEFAULT_TABLE_ROWS,
                cols: DEFAULT_TABLE_COLS,
                withHeaderRow: true
              })
              .run(),
          title: "Insert Table"
        },
        React.createElement("i", { className: "fas fa-table" })
      ),

      // Table editing controls (only show when cursor is in a table)
      ...(isInTable
        ? [
            React.createElement(
              "button",
              {
                key: "add-col",
                type: "button",
                onClick: () => editor.chain().focus().addColumnBefore().run(),
                disabled: !editor.can().addColumnBefore(),
                title: "Add Column Before"
              },
              React.createElement("i", { className: "fas fa-plus" }),
              " Col"
            ),
            React.createElement(
              "button",
              {
                key: "del-col",
                type: "button",
                onClick: () => editor.chain().focus().deleteColumn().run(),
                disabled: !editor.can().deleteColumn(),
                title: "Delete Column"
              },
              React.createElement("i", { className: "fas fa-minus" }),
              " Col"
            ),
            React.createElement(
              "button",
              {
                key: "add-row",
                type: "button",
                onClick: () => editor.chain().focus().addRowBefore().run(),
                disabled: !editor.can().addRowBefore(),
                title: "Add Row Before"
              },
              React.createElement("i", { className: "fas fa-plus" }),
              " Row"
            ),
            React.createElement(
              "button",
              {
                key: "del-row",
                type: "button",
                onClick: () => editor.chain().focus().deleteRow().run(),
                disabled: !editor.can().deleteRow(),
                title: "Delete Row"
              },
              React.createElement("i", { className: "fas fa-minus" }),
              " Row"
            ),
            React.createElement(
              "button",
              {
                key: "del-table",
                type: "button",
                onClick: () => editor.chain().focus().deleteTable().run(),
                disabled: !editor.can().deleteTable(),
                title: "Delete Table"
              },
              React.createElement("i", { className: "fas fa-trash" })
            )
          ]
        : []),

      // Separator
      React.createElement("span", { className: "separator" }),

      // Clear formatting
      React.createElement(
        "button",
        {
          type: "button",
          onClick: function () {
            editor.chain().focus().clearNodes().unsetAllMarks().run();
          },
          title: "Clear Formatting"
        },
        React.createElement("i", { className: "fas fa-remove-format" })
      )
    ),
    React.createElement(EditorContent, {
      editor: editor,
      className: "rich-text-editor-content"
    }),
    React.createElement(LinkModalLocal, {
      isOpen: showLinkModal,
      onClose: function () {
        setShowLinkModal(false);
      },
      onSave: handleLinkSave,
      initialUrl: linkModalData.url,
      initialTarget: linkModalData.target,
      tabInfo: getTabInfo()
    })
  );
}

/**
 * Stats Bar Field Component - for editing which stats to show
 */
function StatsBarField({ field, value, onChange }) {
  const [selectedStats, setSelectedStats] = React.useState(
    value || ["responses", "views", "subscribers"]
  );
  const availableStats = [
    { key: "responses", label: "Responses" },
    { key: "views", label: "Views" },
    { key: "subscribers", label: "Subscribers" },
    { key: "participants", label: "Participants" },
    { key: "comments", label: "Comments" },
    { key: "topics", label: "Topics" }
  ];
  const [draggedIndex, setDraggedIndex] = React.useState(null);

  React.useEffect(
    function () {
      setSelectedStats(value || ["responses", "views", "subscribers"]);
    },
    [value]
  );

  function handleToggleStat(statKey) {
    const newSelected = selectedStats.includes(statKey)
      ? selectedStats.filter(function (s) {
          return s !== statKey;
        })
      : [...selectedStats, statKey];
    setSelectedStats(newSelected);
    onChange(newSelected);
  }

  function handleDragStart(e, index) {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", index.toString());
    e.currentTarget.style.opacity = "0.5";
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    e.currentTarget.style.backgroundColor = "#e9ecef";
  }

  function handleDragLeave(e) {
    e.currentTarget.style.backgroundColor = "#fff";
  }

  function handleDrop(e, dropIndex) {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "#fff";

    const dragIndex = parseInt(e.dataTransfer.getData("text/plain"));
    if (isNaN(dragIndex) || dragIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newSelected = [...selectedStats];
    const draggedItem = newSelected[dragIndex];
    newSelected.splice(dragIndex, 1);
    newSelected.splice(dropIndex, 0, draggedItem);

    setSelectedStats(newSelected);
    onChange(newSelected);
    setDraggedIndex(null);
  }

  function handleDragEnd(e) {
    e.currentTarget.style.opacity = "1";
    setDraggedIndex(null);
  }

  function formatNumber(num) {
    if (num > MILLION_THRESHOLD) {
      return (
        Math.round((num / MILLION_DIVISOR) * DECIMAL_PLACES) / DECIMAL_PLACES +
        "M"
      );
    }
    if (num > THOUSAND_THRESHOLD) {
      return Math.round(num / THOUSAND_DIVISOR) + "K";
    }
    return num.toString();
  }

  // Get hubId from component props or window
  function getHubId() {
    if (window.getPuckEditorData) {
      const data = window.getPuckEditorData();
      if (data && data.content) {
        const statsComponent = data.content.find(function (c) {
          return c.type === "Stats";
        });
        if (
          statsComponent &&
          statsComponent.props &&
          statsComponent.props.hubId
        ) {
          return statsComponent.props.hubId;
        }
      }
      if (data && data.root && data.root.props && data.root.props.hubId) {
        return data.root.props.hubId;
      }
    }
    if (window.modelData && window.modelData.hubId) {
      return window.modelData.hubId;
    }
    if (window.modelData && window.modelData.hub) {
      const hub = window.modelData.hub;
      if (hub.id != null && hub.id > 0) {
        return hub.id;
      }
      if (hub.Id != null && hub.Id > 0) {
        return hub.Id;
      }
    }
    return 0;
  }

  // Get statColor from component props
  function getStatColor() {
    if (window.getPuckEditorData) {
      const data = window.getPuckEditorData();
      if (data && data.content) {
        const statsComponent = data.content.find((c) => {
          return c.type === "Stats";
        });
        if (
          statsComponent &&
          statsComponent.props &&
          statsComponent.props.statColor
        ) {
          return statsComponent.props.statColor;
        }
      }
    }
    return "#000000"; // Default to black
  }

  const [statsData, setStatsData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [statColor, setStatColor] = React.useState(getStatColor());

  React.useEffect(() => {
    const hubId = getHubId();
    if (hubId > 0) {
      setLoading(true);
      fetch(`/HubContentTab/GetHubStatistics?hubId=${hubId}`)
        .then((response) => response.json())
        .then((data) => {
          setStatsData(data);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }

    // Update statColor when component props change
    function updateStatColor() {
      const currentColor = getStatColor();
      if (currentColor !== statColor) {
        setStatColor(currentColor);
      }
    }

    updateStatColor();

    // Listen for Puck data changes to update color
    function handleDataChange() {
      updateStatColor();
    }

    document.addEventListener("puck:dataChange", handleDataChange);
    return function () {
      document.removeEventListener("puck:dataChange", handleDataChange);
    };
  }, [statColor]);

  return React.createElement(
    "div",
    { className: "puck-stats-bar-field" },
    React.createElement(
      "label",
      { className: "puck-field-label mb-2" },
      field.label
    ),

    // Available stats checkboxes
    React.createElement(
      "div",
      { className: "mb-3" },
      React.createElement(
        "small",
        { className: "text-muted d-block mb-2" },
        "Select stats to display:"
      ),
      React.createElement(
        "div",
        { className: "d-flex flex-wrap gap-2" },
        availableStats.map((stat) =>
          React.createElement(
            "div",
            { key: stat.key, className: "form-check" },
            React.createElement("input", {
              type: "checkbox",
              className: "form-check-input",
              id: `stat-${stat.key}`,
              checked: selectedStats.includes(stat.key),
              onChange: () => handleToggleStat(stat.key)
            }),
            React.createElement(
              "label",
              {
                className: "form-check-label",
                htmlFor: `stat-${stat.key}`,
                style: { cursor: "pointer" }
              },
              stat.label
            )
          )
        )
      )
    ),

    // Selected stats with drag and drop
    React.createElement(
      "div",
      { className: "mb-2" },
      React.createElement(
        "small",
        { className: "text-muted d-block mb-2" },
        "Drag to reorder:"
      ),
      React.createElement(
        "div",
        {
          className: "puck-stats-sortable",
          style: { minHeight: "100px" }
        },
        selectedStats.length === 0
          ? React.createElement(
              "div",
              { className: "text-muted p-2" },
              "No stats selected"
            )
          : selectedStats.map((statKey, index) => {
              const stat = availableStats.find((s) => s.key === statKey);
              const statValue = statsData ? statsData[statKey] || 0 : 0;
              const formattedValue = formatNumber(statValue);

              return React.createElement(
                "div",
                {
                  key: `${statKey}-${index}`,
                  draggable: true,
                  onDragStart: (e) => handleDragStart(e, index),
                  onDragOver: handleDragOver,
                  onDragLeave: handleDragLeave,
                  onDrop: (e) => handleDrop(e, index),
                  onDragEnd: handleDragEnd,
                  className: "puck-stat-item mb-2 p-2 border rounded",
                  style: {
                    cursor: "grab",
                    backgroundColor:
                      draggedIndex === index ? "#e9ecef" : "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    userSelect: "none",
                    transition: "background-color 0.2s"
                  }
                },
                React.createElement(
                  "div",
                  {
                    style: { display: "flex", alignItems: "center", gap: "8px" }
                  },
                  React.createElement("i", {
                    className: "fas fa-grip-vertical text-muted",
                    style: { cursor: "grab" }
                  }),
                  React.createElement(
                    "span",
                    { style: { fontWeight: "500" } },
                    stat ? stat.label : statKey
                  ),
                  loading &&
                    React.createElement("i", {
                      className: "fas fa-spinner fa-spin ms-2 text-muted"
                    })
                ),
                React.createElement(
                  "div",
                  {
                    style: {
                      fontWeight: "bold",
                      color: statColor || "#000000",
                      fontSize: "1.1rem"
                    }
                  },
                  loading ? "..." : formattedValue
                )
              );
            })
      )
    )
  );
}

/**
 * Stats Bar Renderer Component - for displaying the stats bar
 */
function StatsBarRenderer({ hubId, selectedStats, statColor, puck }) {
  const [statsData, setStatsData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  function getHubIdToUse() {
    if (hubId && hubId > 0) {
      return hubId;
    }
    if (puck && puck.rootProps && puck.rootProps.hubId) {
      return puck.rootProps.hubId;
    }
    if (window.modelData && window.modelData.hubId) {
      return window.modelData.hubId;
    }
    if (window.modelData && window.modelData.hub) {
      const hub = window.modelData.hub;
      if (typeof hub.id !== "undefined" && hub.id !== null && hub.id > 0) {
        return hub.id;
      }
      if (typeof hub.Id !== "undefined" && hub.Id !== null && hub.Id > 0) {
        return hub.Id;
      }
    }
    return 0;
  }

  function formatNumber(num) {
    if (num > MILLION_THRESHOLD) {
      return (
        Math.round((num / MILLION_DIVISOR) * DECIMAL_PLACES) / DECIMAL_PLACES +
        "M"
      );
    }
    if (num > THOUSAND_THRESHOLD) {
      return Math.round(num / THOUSAND_DIVISOR) + "K";
    }
    return num.toString();
  }

  // Use statColor prop, default to black
  const finalStatColor = statColor || "#000000";

  React.useEffect(() => {
    const hubIdToUse = getHubIdToUse();
    if (hubIdToUse > 0) {
      setLoading(true);
      fetch(`/HubContentTab/GetHubStatistics?hubId=${hubIdToUse}`)
        .then((response) => response.json())
        .then((data) => {
          setStatsData(data);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [hubId]);

  const availableStats = {
    responses: "Responses",
    views: "Views",
    subscribers: "Subscribers",
    participants: "Participants",
    comments: "Comments",
    topics: "Topics"
  };

  // Ensure selectedStats is an array
  const statsToShow =
    Array.isArray(selectedStats) && selectedStats.length > 0
      ? selectedStats
      : ["responses", "views", "subscribers"];

  if (loading) {
    return React.createElement(
      "div",
      {
        style: {
          textAlign: "center",
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        }
      },
      React.createElement("i", { className: "fas fa-spinner fa-spin m-r-sm" }),
      " Loading stats..."
    );
  }

  // Inject responsive styles for stats bar if not already added
  if (!document.getElementById("puck-stats-bar-responsive-styles")) {
    const style = document.createElement("style");
    style.id = "puck-stats-bar-responsive-styles";
    style.textContent = `
      @media (max-width: 768px) {
        .puck-stats-bar-container {
          flex-wrap: wrap !important;
        }
        .puck-stats-bar-container .statInfo {
          flex: 1 1 calc(50% - ${CALC_PADDING_PX}px) !important;
          max-width: calc(50% - ${CALC_PADDING_PX}px) !important;
          min-width: calc(50% - ${CALC_PADDING_PX}px) !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  return React.createElement(
    "div",
    {
      className: "puck-stats-bar-container",
      style: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "1rem",
        marginBottom: "1rem",
        width: "100%",
        maxWidth: "100%",
        overflow: "visible",
        boxSizing: "border-box"
      }
    },
    statsToShow.map(function (statKey, idx) {
      const statValue = statsData ? statsData[statKey] || 0 : 0;
      const formattedValue = formatNumber(statValue);
      const statLabel = availableStats[statKey] || statKey;

      return React.createElement(
        "div",
        {
          key: `${statKey}-${idx}`,
          className: "statInfo",
          style: {
            textAlign: "center",
            padding: "0.5rem 1rem",
            flex: "1 1 0",
            minWidth: "0",
            maxWidth: "100%",
            minHeight: "90px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center"
          }
        },
        React.createElement(
          "div",
          {
            className: "statText",
            style: {
              textTransform: "uppercase",
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: "700",
              fontSize: "32px",
              lineHeight: "48px",
              textAlign: "center",
              color: finalStatColor,
              marginBottom: "0.25rem"
            }
          },
          formattedValue
        ),
        React.createElement(
          "div",
          {
            className: "statHeader",
            style: {
              textTransform: "uppercase",
              fontFamily: '"Libre Franklin", sans-serif',
              fontWeight: "400",
              fontSize: "14px",
              lineHeight: "20px",
              textAlign: "center",
              color: "rgba(58, 58, 58, 0.68)"
            }
          },
          statLabel
        )
      );
    })
  );
}

const HEADING_LEVEL_OPTIONS = [
  { label: "H2", value: "h2" },
  { label: "H3", value: "h3" },
  { label: "H4", value: "h4" },
  { label: "H5", value: "h5" },
  { label: "H6", value: "h6" }
];

function headingLevelField(label) {
  return {
    type: "select",
    label: label || "Heading Level",
    options: HEADING_LEVEL_OPTIONS
  };
}

function subHeadingLevel(level) {
  const parsed = parseInt(String(level || "h3").slice(1), 10);
  const n = Number.isNaN(parsed) ? 3 : parsed;
  return "h" + Math.min(n + 1, 6);
}

// Enhanced Puck configuration with media field
const puckConfig = {
  root: {
    title: "Tab",
    fields: {
      title: {
        type: "text",
        label: "Tab Display Title"
      },
      hubId: {
        type: "custom",
        label: "# Hub ID (auto-detected)",
        render: ({ field, value, onChange }) => {
          // Auto-detect hubId from window.modelData
          function getAutoHubId() {
            if (window.modelData && window.modelData.hubId) {
              const id = window.modelData.hubId;
              if (id && id > 0) {
                return id;
              }
            }
            if (window.modelData && window.modelData.hub) {
              const hub = window.modelData.hub;
              if (
                typeof hub.id !== "undefined" &&
                hub.id !== null &&
                hub.id > 0
              ) {
                return hub.id;
              }
              if (
                typeof hub.Id !== "undefined" &&
                hub.Id !== null &&
                hub.Id > 0
              ) {
                return hub.Id;
              }
            }
            return value || 0;
          }

          const autoHubId = getAutoHubId();

          // Update the value if it's different and valid (only once)
          const hasUpdatedRef = React.useRef(false);
          React.useEffect(
            function () {
              if (
                !hasUpdatedRef.current &&
                autoHubId > 0 &&
                autoHubId !== value
              ) {
                onChange(autoHubId);
                hasUpdatedRef.current = true;
              }
            },
            [autoHubId, value, onChange]
          );

          return React.createElement(
            "div",
            { className: "puck-field" },
            React.createElement(
              "label",
              { className: "puck-field-label" },
              field.label
            ),
            React.createElement("input", {
              type: "text",
              value: autoHubId > 0 ? autoHubId : "Auto-detecting...",
              className: "form-control",
              readOnly: true,
              disabled: true,
              style: { backgroundColor: "#e9ecef", cursor: "not-allowed" }
            }),
            autoHubId === 0 &&
              React.createElement(
                "small",
                {
                  className: "form-text text-warning"
                },
                "Hub ID will be auto-detected when available"
              )
          );
        }
      }
    }
  },
  categories: {
    layout: {
      title: "LAYOUT",
      components: [
        "Columns",
        "CardContainer",
        "Container",
        "VerticalSpace",
        "Accordion",
        "TabsBlock"
      ]
    },
    typography: {
      title: "TYPOGRAPHY",
      components: ["HeadingBlock", "TextBlock", "Quote"]
    },
    actions: {
      title: "ACTIONS",
      components: ["ButtonBlock", "ButtonGroup"]
    },
    media: {
      title: "MEDIA",
      components: ["ImageBlock", "Swipe", "VideoEmbed", "Icon"]
    },
    content: {
      title: "CONTENT",
      components: [
        "Hero",
        "Card",
        "Testimonial",
        "Profile",
        "Timeline",
        "Stats",
        "Logos",
        "FeaturedProjects",
        "UpcomingEvents",
        "CommunityQA",
        "WelcomeBlock",
        "SignupBlock"
      ]
    },
    ui: {
      title: "UI ELEMENTS",
      components: ["Alert", "ProgressBar", "Divider", "List"]
    },
    custom: {
      title: "CUSTOM",
      components: ["HtmlBlock"]
    }
  },
  components: {
    HeadingBlock: {
      label: "Heading",
      fields: {
        text: {
          type: "textarea",
          label: "Text",
          contentEditable: true
        },
        level: {
          type: "select",
          label: "Level",
          options: [
            { label: "H1", value: "h1" },
            { label: "H2", value: "h2" },
            { label: "H3", value: "h3" },
            { label: "H4", value: "h4" },
            { label: "H5", value: "h5" },
            { label: "H6", value: "h6" }
          ]
        },
        align: {
          type: "select",
          label: "Alignment",
          options: [
            { label: "Left", value: "left" },
            { label: "Center", value: "center" },
            { label: "Right", value: "right" }
          ]
        }
      },
      defaultProps: { text: "Heading", level: "h2", align: "left" },
      render: ({ text, level, align }) => {
        const Level = level || "h2";
        return React.createElement(
          Level,
          {
            style: { textAlign: align }
          },
          text
        );
      }
    },
    TextBlock: {
      label: "Text",
      fields: {
        content: {
          type: "custom",
          label: "Content",
          render: RichTextField
        },
        size: {
          type: "select",
          label: "Size",
          options: [
            { label: "Small", value: "small" },
            { label: "Normal", value: "normal" },
            { label: "Large", value: "large" }
          ]
        }
      },
      defaultProps: { content: "<p>Enter your text here</p>", size: "normal" },
      render: function ({ content, size }) {
        const fontSize =
          size === "small" ? "0.875rem" : size === "large" ? "1.25rem" : "1rem";
        return React.createElement("div", {
          className: "text-block-content",
          style: { fontSize: fontSize },
          dangerouslySetInnerHTML: { __html: sanitizeUserHtml(content || "") }
        });
      }
    },
    HtmlBlock: {
      label: "HTML/Embed",
      fields: {
        htmlCode: {
          type: "textarea",
          label: "HTML Code",
          rows: TEXTAREA_ROWS_DEFAULT
        },
        description: {
          type: "text",
          label: "Description (optional)",
          placeholder: "e.g., YouTube video, Twitter embed, etc."
        }
      },
      defaultProps: {
        htmlCode:
          '<!-- Paste your embed code here -->\n<div style="padding: 2rem; background: #f8f9fa; border: 2px dashed #dee2e6; text-align: center;">\n  <p>Add your custom HTML or embed code</p>\n</div>',
        description: ""
      },
      render: ({ htmlCode, description }) => {
        return React.createElement(
          "div",
          {
            className: "html-block-wrapper",
            style: { marginBottom: "1rem" }
          },
          description &&
            React.createElement(
              "div",
              {
                className: "html-block-description",
                style: {
                  fontSize: "0.875rem",
                  color: "#6c757d",
                  marginBottom: "0.5rem",
                  fontStyle: "italic"
                }
              },
              description
            ),
          React.createElement("div", {
            className: "html-block-content",
            dangerouslySetInnerHTML: {
              __html: sanitizeUserHtml(htmlCode || "")
            }
          })
        );
      }
    },
    ImageBlock: {
      label: "Image",
      fields: {
        src: {
          type: "custom",
          label: "Image",
          render: MediaField
        },
        alt: { type: "text", label: "Alt Text" },
        alignment: {
          type: "select",
          label: "Alignment",
          options: [
            { label: "Left", value: "left" },
            { label: "Center", value: "center" },
            { label: "Right", value: "right" },
            { label: "Full Width", value: "full" }
          ]
        },
        borderRadius: {
          type: "select",
          label: "Border Radius",
          options: [
            { label: "None (0px)", value: "0" },
            { label: "Small (4px)", value: "4" },
            { label: "Medium (8px)", value: "8" },
            { label: "Large (12px)", value: "12" },
            { label: "XL (16px)", value: "16" },
            { label: "XXL (24px)", value: "24" },
            { label: "Round (50px)", value: "50" }
          ]
        },
        shadow: {
          type: "radio",
          label: "Shadow",
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false }
          ]
        }
      },
      defaultProps: {
        src: "",
        alt: "Image description",
        alignment: "center",
        borderRadius: "0",
        shadow: false
      },
      render: ({ src, alt, alignment, borderRadius, shadow }) => {
        if (!src) {
          return React.createElement(
            "div",
            {
              style: {
                padding: "40px",
                backgroundColor: "#f8f9fa",
                textAlign: "center",
                border: "2px dashed #dee2e6",
                borderRadius: `${BORDER_RADIUS_4_PX}px`
              }
            },
            React.createElement("i", {
              className: "fas fa-image fa-3x",
              style: { color: "#6c757d" }
            }),
            React.createElement(
              "p",
              {
                className: "mt-2 mb-0",
                style: { color: "#6c757d" }
              },
              "Click edit to select an image"
            )
          );
        }

        const containerStyle = {
          textAlign:
            alignment === "center"
              ? "center"
              : alignment === "right"
              ? "right"
              : "left",
          marginBottom: "1rem"
        };

        const imgStyle = {
          // min() keeps the 600px cap but never lets the image outgrow its
          // container (a Columns track, or a phone screen). This is an inline
          // style, so a stylesheet max-width rule cannot override it.
          maxWidth: alignment === "full" ? "100%" : "min(600px, 100%)",
          width: alignment === "full" ? "100%" : "auto",
          height: "auto",
          display: alignment === "center" ? "inline-block" : "block",
          marginLeft:
            alignment === "right"
              ? "auto"
              : alignment === "center"
              ? "auto"
              : "0",
          marginRight:
            alignment === "left"
              ? "auto"
              : alignment === "center"
              ? "auto"
              : "0",
          borderRadius: borderRadius ? `${borderRadius}px` : "0",
          boxShadow: shadow
            ? "0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)"
            : "none"
        };

        return React.createElement(
          "div",
          { style: containerStyle },
          React.createElement("img", {
            src: src,
            alt: alt,
            style: imgStyle
          })
        );
      }
    },
    Swipe: {
      label: "Before/After Swipe",
      fields: {
        beforeImage: {
          type: "custom",
          label: "Before (existing) image",
          render: MediaField
        },
        beforeAlt: { type: "text", label: "Before image — alt text" },
        afterImage: {
          type: "custom",
          label: "After (proposed) image",
          render: MediaField
        },
        afterAlt: { type: "text", label: "After image — alt text" },
        beforeLabel: { type: "text", label: "Before label" },
        afterLabel: { type: "text", label: "After label" },
        caption: { type: "text", label: "Caption (optional)" },
        ratio: {
          type: "select",
          label: "Frame shape",
          options: [
            { label: "16:9 (widescreen)", value: "16/9" },
            { label: "4:3", value: "4/3" },
            { label: "3:2", value: "3/2" },
            { label: "1:1 (square)", value: "1/1" }
          ]
        },
        fit: {
          type: "select",
          label: "Image fit",
          options: [
            { label: "Crop to fill (recommended)", value: "cover" },
            { label: "Fit whole image (letterbox)", value: "contain" }
          ]
        },
        startPosition: {
          type: "number",
          label: "Starting divider position (%)",
          min: 0,
          max: 100
        }
      },
      defaultProps: {
        beforeImage: "",
        beforeAlt: "Existing conditions",
        afterImage: "",
        afterAlt: "Proposed design",
        beforeLabel: "Existing",
        afterLabel: "Proposed",
        caption: "",
        ratio: "16/9",
        fit: "cover",
        startPosition: 25
      },
      render: ({
        beforeImage,
        beforeAlt,
        afterImage,
        afterAlt,
        beforeLabel,
        afterLabel,
        caption,
        ratio,
        fit,
        startPosition,
        puck
      }) => {
        // Hooks must run unconditionally, before any early return.
        const wrapperRef = React.useRef(null);
        const hasImages = !!(beforeImage && afterImage);
        // In the Puck editor the widget must be a PURE render: no imperative
        // init and no interactive <input> — the full-stage range swallows the
        // editor's selection clicks and the init's DOM mutations destabilize
        // the canvas (components become unselectable / render duplicated).
        const isEditing = !!(puck && puck.isEditing);

        let start = parseFloat(startPosition);
        if (isNaN(start)) start = 25;
        start = Math.max(0, Math.min(100, start));

        React.useEffect(
          function () {
            if (!isEditing && hasImages && wrapperRef.current) {
              piSwipeInit(wrapperRef.current);
            }
          },
          [isEditing, hasImages]
        );

        if (!hasImages) {
          return React.createElement(
            "div",
            { className: "pi-swipe__placeholder" },
            React.createElement("i", {
              className:
                "fas fa-arrows-left-right fa-3x pi-swipe__placeholder-icon",
              "aria-hidden": "true"
            }),
            React.createElement(
              "p",
              { className: "mt-2 mb-0" },
              "Add a before (existing) and an after (proposed) image to build the comparison."
            )
          );
        }

        const ratioClass =
          ratio === "4/3"
            ? " pi-swipe--4x3"
            : ratio === "3/2"
            ? " pi-swipe--3x2"
            : ratio === "1/1"
            ? " pi-swipe--1x1"
            : "";
        const fitClass = fit === "contain" ? " pi-swipe--fit-contain" : "";

        const chevron = function (d) {
          return React.createElement(
            "svg",
            {
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              strokeWidth: "2.6",
              strokeLinecap: "round",
              strokeLinejoin: "round"
            },
            React.createElement("path", { d: d })
          );
        };

        const stage = React.createElement(
          "div",
          { className: "pi-swipe__stage" },
          React.createElement(
            "div",
            { className: "pi-swipe__pane pi-swipe__pane--after" },
            React.createElement("img", {
              className: "pi-swipe__img",
              src: afterImage,
              alt: afterAlt || afterLabel || "After image"
            }),
            afterLabel
              ? React.createElement(
                  "span",
                  { className: "pi-swipe__label pi-swipe__label--after" },
                  afterLabel
                )
              : null
          ),
          React.createElement(
            "div",
            { className: "pi-swipe__pane pi-swipe__pane--before" },
            React.createElement("img", {
              className: "pi-swipe__img",
              src: beforeImage,
              alt: beforeAlt || beforeLabel || "Before image"
            }),
            beforeLabel
              ? React.createElement(
                  "span",
                  { className: "pi-swipe__label pi-swipe__label--before" },
                  beforeLabel
                )
              : null
          ),
          // The interactive range only exists on the public render; in the
          // editor it would swallow Puck's click-to-select on the whole stage.
          isEditing
            ? null
            : React.createElement("input", {
                className: "pi-swipe__range",
                type: "range",
                min: "0",
                max: "100",
                step: "1",
                defaultValue: String(start),
                "aria-valuetext":
                  Math.round(start) + "% of the after image shown",
                "aria-label":
                  "Comparison slider. Reveal the " +
                  (afterLabel || "after") +
                  " image. Drag, or use arrow keys; Home and End jump to each end."
              }),
          React.createElement(
            "div",
            { className: "pi-swipe__divider", "aria-hidden": "true" },
            React.createElement(
              "span",
              { className: "pi-swipe__grip" },
              chevron("M14 7l-5 5 5 5"),
              chevron("M10 7l5 5-5 5")
            )
          )
        );

        // Editor: statically apply the enhanced classes and divider position
        // (inline CSS var is a dynamic runtime value) so the admin sees the
        // real composition at the configured start position, inert.
        const figureProps = {
          className:
            "pi-swipe" +
            ratioClass +
            fitClass +
            (isEditing ? " pi-swipe--ready pi-swipe--editor" : ""),
          "data-pi-swipe-start": String(start),
          "data-pi-swipe-ratio": ratio
        };
        if (isEditing) {
          figureProps.style = { "--pi-swipe-pos": start };
        } else {
          figureProps["data-pi-swipe"] = "";
        }

        return React.createElement(
          "div",
          { ref: wrapperRef, className: "pi-swipe-widget" },
          React.createElement(
            "figure",
            figureProps,
            caption
              ? React.createElement(
                  "figcaption",
                  { className: "pi-swipe__caption" },
                  caption
                )
              : null,
            stage
          )
        );
      }
    },
    ButtonBlock: {
      label: "Button",
      fields: {
        text: {
          type: "text",
          label: "Text",
          contentEditable: true
        },
        href: { type: "text", label: "Link URL" },
        variant: {
          type: "select",
          label: "Style",
          options: [
            { label: "Primary", value: "primary" },
            { label: "Secondary", value: "secondary" },
            { label: "Success", value: "success" },
            { label: "Danger", value: "danger" },
            { label: "Warning", value: "warning" },
            { label: "Info", value: "info" },
            { label: "Light", value: "light" },
            { label: "Dark", value: "dark" }
          ]
        },
        size: {
          type: "select",
          label: "Size",
          options: [
            { label: "Small", value: "sm" },
            { label: "Normal", value: "" },
            { label: "Large", value: "lg" }
          ]
        },
        align: {
          type: "select",
          label: "Alignment",
          options: [
            { label: "Left", value: "left" },
            { label: "Center", value: "center" },
            { label: "Right", value: "right" }
          ]
        }
      },
      defaultProps: {
        text: "Click me",
        href: "#",
        variant: "primary",
        size: "",
        align: "left"
      },
      render: ({ text, href, variant, size, align }) => {
        const classNames = ["btn", `btn-${variant}`];
        if (size) {
          classNames.push(`btn-${size}`);
        }

        return React.createElement(
          "div",
          {
            style: { textAlign: align, marginBottom: "1rem" }
          },
          React.createElement(
            "a",
            {
              href: href,
              className: classNames.join(" "),
              role: "button"
            },
            text
          )
        );
      }
    },
    VerticalSpace: {
      label: "Vertical Space",
      fields: {
        size: {
          type: "select",
          label: "Size",
          options: [
            { label: "Small (16px)", value: "16" },
            { label: "Medium (32px)", value: "32" },
            { label: "Large (64px)", value: "64" },
            { label: "Extra Large (96px)", value: "96" }
          ]
        }
      },
      defaultProps: { size: "32" },
      render: ({ size }) => {
        const sizeValue = size && !isNaN(parseFloat(size)) ? size : "32";
        return React.createElement("div", {
          style: { height: `${sizeValue}px` }
        });
      }
    },
    Columns: {
      label: "Columns",
      fields: {
        distribution: {
          type: "select",
          label: "Distribution",
          options: [
            { label: "Equal (50/50)", value: "equal" },
            { label: "Left Heavy (66/33)", value: "left" },
            { label: "Right Heavy (33/66)", value: "right" },
            { label: "3 Even Columns", value: "3-even" },
            { label: "4 Even Columns", value: "4-even" }
          ]
        }
      },
      defaultProps: {
        distribution: "equal"
      },
      zones: {
        "column-left": { title: "Left Column" },
        "column-right": { title: "Right Column" },
        "column-1": { title: "Column 1" },
        "column-2": { title: "Column 2" },
        "column-3": { title: "Column 3" },
        "column-4": { title: "Column 4" }
      },
      render: function ({ distribution, puck }) {
        const { renderDropZone } = puck || {};

        // Use zone prop for each drop zone to prevent duplication
        if (!renderDropZone) {
          return React.createElement(
            "div",
            {
              style: {
                padding: "2rem",
                backgroundColor: "#f8f9fa",
                border: "2px dashed #dee2e6",
                borderRadius: `${BORDER_RADIUS_4_PX}px`,
                textAlign: "center"
              }
            },
            "Drop zones not available"
          );
        }

        // Each track is minmax(0, Nfr) rather than a bare Nfr. A bare fr
        // track resolves to minmax(auto, Nfr), whose auto minimum refuses to
        // shrink below the min-content width of its contents -- so a column
        // holding a wide child (e.g. an embedded image) blows past its share
        // and breaks the distribution. minmax(0, Nfr) keeps the ratio exact.
        let gridTemplateColumns = "minmax(0, 1fr) minmax(0, 1fr)";
        let columnZones = ["column-left", "column-right"];

        if (distribution === "left") {
          gridTemplateColumns = "minmax(0, 2fr) minmax(0, 1fr)";
        } else if (distribution === "right") {
          gridTemplateColumns = "minmax(0, 1fr) minmax(0, 2fr)";
        } else if (distribution === "3-even") {
          gridTemplateColumns =
            "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)";
          columnZones = ["column-1", "column-2", "column-3"];
        } else if (distribution === "4-even") {
          gridTemplateColumns =
            "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)";
          columnZones = ["column-1", "column-2", "column-3", "column-4"];
        }

        // Inject responsive styles if not already added
        if (!document.getElementById("puck-columns-responsive-styles")) {
          const style = document.createElement("style");
          style.id = "puck-columns-responsive-styles";
          style.textContent = `
            /* Let each column shrink to its track instead of forcing the
               grid wider than its share. */
            .puck-columns-container > * {
              min-width: 0;
            }
            /* Keep embedded media inside its column rather than overflowing
               and clipping with no horizontal scroll. */
            .puck-columns-container img,
            .puck-columns-container video,
            .puck-columns-container iframe,
            .puck-columns-container embed,
            .puck-columns-container object {
              max-width: 100%;
            }
            .puck-columns-container img,
            .puck-columns-container video {
              height: auto;
            }
            @media (max-width: 768px) {
              .puck-columns-container {
                grid-template-columns: 1fr !important;
              }
            }
          `;
          document.head.appendChild(style);
        }

        return React.createElement(
          "div",
          {
            className: "puck-columns-container",
            style: {
              display: "grid",
              gridTemplateColumns: gridTemplateColumns,
              gap: "16px",
              marginBottom: "1rem"
            }
          },
          columnZones.map(function (zone) {
            return React.createElement(
              "div",
              {
                key: zone, // Use zone name as key for stability
                style: {
                  minHeight: "100px",
                  position: "relative"
                }
              },
              renderDropZone({ zone: zone })
            );
          })
        );
      }
    },
    Card: {
      label: "Basic Card",
      fields: {
        title: {
          type: "text",
          label: "Title",
          contentEditable: true
        },
        description: {
          type: "textarea",
          label: "Description",
          contentEditable: true
        },
        headingLevel: headingLevelField("Title Level"),
        padding: {
          type: "select",
          label: "Padding",
          options: [
            { label: "Small", value: "16" },
            { label: "Medium", value: "24" },
            { label: "Large", value: "32" }
          ]
        }
      },
      defaultProps: {
        title: "Card Title",
        description: "Card description goes here.",
        headingLevel: "h3",
        padding: "24"
      },
      render: function ({ title, description, headingLevel, padding }) {
        return React.createElement(
          "div",
          {
            className: "card",
            style: {
              padding: `${padding}px`,
              marginBottom: "1rem",
              border: "1px solid #dee2e6",
              borderRadius: "0.375rem",
              backgroundColor: "#fff"
            }
          },
          title &&
            React.createElement(
              headingLevel || "h3",
              {
                className: "card-title h3",
                style: { marginTop: 0, marginBottom: "0.5rem" }
              },
              title
            ),
          description &&
            React.createElement(
              "p",
              {
                className: "card-text",
                style: { marginBottom: 0 }
              },
              description
            )
        );
      }
    },
    Divider: {
      label: "Divider",
      fields: {
        style: {
          type: "select",
          label: "Style",
          options: [
            { label: "Solid", value: "solid" },
            { label: "Dashed", value: "dashed" },
            { label: "Dotted", value: "dotted" }
          ]
        }
      },
      defaultProps: { style: "solid" },
      render: ({ style }) => {
        return React.createElement("hr", {
          style: {
            borderStyle: style,
            borderWidth: "1px 0 0 0",
            borderColor: "#dee2e6",
            margin: "1rem 0"
          }
        });
      }
    },
    Quote: {
      label: "Quote",
      fields: {
        text: {
          type: "custom",
          label: "Quote Text",
          render: RichTextField
        },
        author: {
          type: "text",
          label: "Author"
        },
        role: {
          type: "text",
          label: "Role/Title (optional)"
        },
        size: {
          type: "select",
          label: "Size",
          options: [
            { label: "Small", value: "small" },
            { label: "Medium", value: "medium" },
            { label: "Large", value: "large" }
          ]
        }
      },
      defaultProps: {
        text: "<p>This is a quote</p>",
        author: "",
        role: "",
        size: "medium"
      },
      render: ({ text, author, role, size }) => {
        const fontSize =
          size === "small" ? "1rem" : size === "large" ? "1.5rem" : "1.25rem";
        const padding =
          size === "small"
            ? "1rem"
            : size === "large"
            ? "2rem 2.5rem"
            : "1.5rem 2rem";

        return React.createElement(
          "blockquote",
          {
            className: "blockquote",
            style: {
              padding: padding,
              borderLeft: "4px solid #0d6efd",
              marginBottom: "1rem",
              fontStyle: "italic",
              backgroundColor: "#f8f9fa",
              fontSize: fontSize
            }
          },
          React.createElement("div", {
            className: "quote-text",
            style: {
              marginBottom: author || role ? "1rem" : 0
            },
            dangerouslySetInnerHTML: { __html: sanitizeUserHtml(text || "") }
          }),
          (author || role) &&
            React.createElement(
              "footer",
              {
                className: "blockquote-footer",
                style: {
                  fontSize: size === "large" ? "1rem" : "0.875rem",
                  color: "#6c757d",
                  fontStyle: "normal"
                }
              },
              author &&
                React.createElement(
                  "cite",
                  {
                    style: { fontStyle: "normal" }
                  },
                  author
                ),
              role && (author ? `, ${role}` : role)
            )
        );
      }
    },
    List: {
      label: "List",
      fields: {
        items: {
          type: "array",
          label: "Items",
          arrayFields: {
            text: { type: "text", label: "Text" }
          }
        },
        type: {
          type: "select",
          label: "Type",
          options: [
            { label: "Bulleted", value: "ul" },
            { label: "Numbered", value: "ol" }
          ]
        }
      },
      defaultProps: {
        items: [
          { text: "First item" },
          { text: "Second item" },
          { text: "Third item" }
        ],
        type: "ul"
      },
      render: ({ items, type }) => {
        const ListTag = type || "ul";
        return React.createElement(
          ListTag,
          {
            style: { marginBottom: "1rem" }
          },
          items.map((item, idx) =>
            React.createElement("li", { key: idx }, item.text)
          )
        );
      }
    },
    Hero: {
      label: "Hero",
      fields: {
        title: {
          type: "text",
          label: "Title"
        },
        headingLevel: headingLevelField("Title Level"),
        titleTypography: {
          type: "object",
          label: "Title Typography",
          objectFields: {
            fontSize: {
              type: "select",
              label: "Font Size",
              options: [
                { label: "Small (1.5rem / 24px)", value: "24" },
                { label: "Medium (2rem / 32px)", value: "32" },
                { label: "Large (2.5rem / 40px)", value: "40" },
                { label: "Extra Large (3rem / 48px)", value: "48" },
                { label: "XXL (3.5rem / 56px)", value: "56" },
                { label: "XXXL (4rem / 64px)", value: "64" }
              ]
            },
            fontWeight: {
              type: "select",
              label: "Font Weight",
              options: [
                { label: "Light (300)", value: "300" },
                { label: "Normal (400)", value: "400" },
                { label: "Medium (500)", value: "500" },
                { label: "Semi-Bold (600)", value: "600" },
                { label: "Bold (700)", value: "700" },
                { label: "Extra Bold (800)", value: "800" }
              ]
            },
            titleColor: {
              type: "custom",
              label: "Title Color",
              render: ({ field, onChange, value, ...props }) => {
                return React.createElement(TitleColorFieldWithWarning, {
                  field,
                  onChange,
                  value,
                  ...props
                });
              }
            }
          }
        },
        description: {
          type: "textarea",
          label: "Description"
        },
        descriptionTypography: {
          type: "object",
          label: "Description Typography",
          objectFields: {
            fontSize: {
              type: "select",
              label: "Font Size",
              options: [
                { label: "Small (0.875rem / 14px)", value: "14" },
                { label: "Normal (1rem / 16px)", value: "16" },
                { label: "Large (1.125rem / 18px)", value: "18" },
                { label: "XL (1.25rem / 20px)", value: "20" },
                { label: "XXL (1.5rem / 24px)", value: "24" }
              ]
            },
            fontWeight: {
              type: "select",
              label: "Font Weight",
              options: [
                { label: "Light (300)", value: "300" },
                { label: "Normal (400)", value: "400" },
                { label: "Medium (500)", value: "500" },
                { label: "Semi-Bold (600)", value: "600" },
                { label: "Bold (700)", value: "700" }
              ]
            },
            descriptionColor: {
              type: "custom",
              label: "Description Color",
              render: ({ field, onChange, value, ...props }) => {
                return React.createElement(DescriptionColorFieldWithWarning, {
                  field,
                  onChange,
                  value,
                  ...props
                });
              }
            }
          }
        },
        buttons: {
          type: "array",
          label: "Buttons",
          getItemSummary: (item, index) => {
            // Return the button label if available, otherwise a default name
            if (item && item.label && item.label.trim()) {
              return item.label.trim();
            }
            return `Button ${index + 1}`;
          },
          arrayFields: {
            label: { type: "text", label: "Button Text" },
            href: { type: "text", label: "Link URL" },
            variant: {
              type: "select",
              label: "Button Style",
              options: [
                { label: "Primary", value: "primary" },
                { label: "Secondary", value: "secondary" },
                { label: "Success", value: "success" },
                { label: "Danger", value: "danger" },
                { label: "Warning", value: "warning" },
                { label: "Info", value: "info" },
                { label: "Light", value: "light" },
                { label: "Dark", value: "dark" },
                { label: "Outline Primary", value: "outline-primary" },
                { label: "Outline Secondary", value: "outline-secondary" },
                { label: "Outline Light", value: "outline-light" }
              ]
            },
            visibility: {
              type: "select",
              label: "Visibility (editor preview shows signed-out state)",
              options: [
                { label: "Always show", value: "always" },
                { label: "Only when signed out", value: "signedOut" },
                { label: "Only when signed in", value: "signedIn" }
              ]
            }
          },
          defaultItemProps: {
            label: "Learn More",
            href: "#",
            variant: "primary",
            visibility: "always"
          }
        },
        buttonAlign: {
          type: "select",
          label: "Button Alignment",
          options: [
            { label: "Left", value: "left" },
            { label: "Center", value: "center" },
            { label: "Right", value: "right" }
          ]
        },
        backgroundType: {
          type: "select",
          label: "Background Type",
          options: [
            { label: "None", value: "none" },
            { label: "Image", value: "image" },
            { label: "Video", value: "video" }
          ]
        },
        image: {
          type: "custom",
          label: "Image",
          render: ({ field, value, onChange }) => {
            // Get current component props to check backgroundType
            function getBackgroundType() {
              if (window.getPuckEditorData) {
                const data = window.getPuckEditorData();
                if (data && data.content) {
                  const heroComponent = data.content.find(function (c) {
                    return c.type === "Hero";
                  });
                  if (
                    heroComponent &&
                    heroComponent.props &&
                    heroComponent.props.backgroundType
                  ) {
                    return heroComponent.props.backgroundType;
                  }
                }
              }
              return "none";
            }

            const [backgroundType, setBackgroundType] = React.useState(
              getBackgroundType()
            );

            React.useEffect(
              function () {
                function checkBackgroundType() {
                  const currentType = getBackgroundType();
                  if (currentType !== backgroundType) {
                    setBackgroundType(currentType);
                  }
                }

                checkBackgroundType();
                document.addEventListener(
                  "puck:dataChange",
                  checkBackgroundType
                );
                return function () {
                  document.removeEventListener(
                    "puck:dataChange",
                    checkBackgroundType
                  );
                };
              },
              [backgroundType]
            );

            // Only render if backgroundType is 'image'
            if (backgroundType !== "image") {
              return null;
            }

            // Render the image fields
            return React.createElement(
              "div",
              { className: "puck-field-group" },
              React.createElement(
                "label",
                { className: "puck-field-label" },
                field.label || "Image"
              ),
              React.createElement(
                "div",
                { className: "puck-field" },
                React.createElement(
                  "label",
                  { className: "puck-field-label" },
                  "URL"
                ),
                React.createElement(MediaFieldLocal, {
                  field: { label: "URL" },
                  value: value?.url || "",
                  onChange: (url) => onChange({ ...value, url })
                })
              ),
              React.createElement(
                "div",
                { className: "puck-field" },
                React.createElement(
                  "label",
                  { className: "puck-field-label" },
                  "Mode"
                ),
                React.createElement(
                  "div",
                  { className: "btn-group", role: "group" },
                  ["inline", "bg", "custom"].map((mode) =>
                    React.createElement(
                      "button",
                      {
                        key: mode,
                        type: "button",
                        className: `btn btn-sm ${
                          (value?.mode || "inline") === mode
                            ? "btn-primary"
                            : "btn-outline-secondary"
                        }`,
                        onClick: () => onChange({ ...value, mode })
                      },
                      mode === "inline"
                        ? "Inline"
                        : mode === "bg"
                        ? "Background"
                        : "Custom"
                    )
                  )
                )
              )
            );
          }
        },
        video: {
          type: "custom",
          label: "Video Background",
          render: ({ field, value, onChange }) => {
            // Get current component props to check backgroundType
            function getBackgroundType() {
              if (window.getPuckEditorData) {
                const data = window.getPuckEditorData();
                if (data && data.content) {
                  const heroComponent = data.content.find(function (c) {
                    return c.type === "Hero";
                  });
                  if (
                    heroComponent &&
                    heroComponent.props &&
                    heroComponent.props.backgroundType
                  ) {
                    return heroComponent.props.backgroundType;
                  }
                }
              }
              return "none";
            }

            const [backgroundType, setBackgroundType] = React.useState(
              getBackgroundType()
            );

            React.useEffect(
              function () {
                function checkBackgroundType() {
                  const currentType = getBackgroundType();
                  if (currentType !== backgroundType) {
                    setBackgroundType(currentType);
                  }
                }

                checkBackgroundType();
                document.addEventListener(
                  "puck:dataChange",
                  checkBackgroundType
                );
                return function () {
                  document.removeEventListener(
                    "puck:dataChange",
                    checkBackgroundType
                  );
                };
              },
              [backgroundType]
            );

            // Only render if backgroundType is 'video'
            if (backgroundType !== "video") {
              return null;
            }

            // Render the video fields
            return React.createElement(
              "div",
              { className: "puck-field-group" },
              React.createElement(
                "label",
                { className: "puck-field-label" },
                field.label || "Video Background"
              ),
              React.createElement(
                "div",
                { className: "puck-field", style: { marginBottom: "1rem" } },
                React.createElement(VideoField, {
                  field: { label: "Video URL" },
                  value: value?.url || "",
                  onChange: (url) => onChange({ ...value, url })
                })
              ),
              React.createElement(
                "div",
                { className: "puck-field", style: { marginBottom: "1rem" } },
                React.createElement(MediaFieldLocal, {
                  field: { label: "Poster Image (fallback/thumbnail)" },
                  value: value?.poster || "",
                  onChange: (poster) => onChange({ ...value, poster })
                })
              ),
              React.createElement(
                "div",
                { className: "puck-field", style: { marginBottom: "1rem" } },
                React.createElement(
                  "label",
                  { className: "puck-field-label" },
                  "Autoplay"
                ),
                React.createElement(
                  "div",
                  { className: "btn-group", role: "group" },
                  React.createElement(
                    "button",
                    {
                      type: "button",
                      className: `btn btn-sm ${
                        value?.autoplay !== false
                          ? "btn-primary"
                          : "btn-outline-secondary"
                      }`,
                      onClick: () => onChange({ ...value, autoplay: true })
                    },
                    "Yes"
                  ),
                  React.createElement(
                    "button",
                    {
                      type: "button",
                      className: `btn btn-sm ${
                        value?.autoplay === false
                          ? "btn-primary"
                          : "btn-outline-secondary"
                      }`,
                      onClick: () => onChange({ ...value, autoplay: false })
                    },
                    "No"
                  )
                )
              ),
              React.createElement(
                "div",
                { className: "puck-field", style: { marginBottom: "1rem" } },
                React.createElement(
                  "label",
                  { className: "puck-field-label" },
                  "Loop"
                ),
                React.createElement(
                  "div",
                  { className: "btn-group", role: "group" },
                  React.createElement(
                    "button",
                    {
                      type: "button",
                      className: `btn btn-sm ${
                        value?.loop !== false
                          ? "btn-primary"
                          : "btn-outline-secondary"
                      }`,
                      onClick: () => onChange({ ...value, loop: true })
                    },
                    "Yes"
                  ),
                  React.createElement(
                    "button",
                    {
                      type: "button",
                      className: `btn btn-sm ${
                        value?.loop === false
                          ? "btn-primary"
                          : "btn-outline-secondary"
                      }`,
                      onClick: () => onChange({ ...value, loop: false })
                    },
                    "No"
                  )
                )
              ),
              React.createElement(
                "div",
                { className: "puck-field" },
                React.createElement(
                  "label",
                  { className: "puck-field-label" },
                  "Muted"
                ),
                React.createElement(
                  "div",
                  { className: "btn-group", role: "group" },
                  React.createElement(
                    "button",
                    {
                      type: "button",
                      className: `btn btn-sm ${
                        value?.muted !== false
                          ? "btn-primary"
                          : "btn-outline-secondary"
                      }`,
                      onClick: () => onChange({ ...value, muted: true })
                    },
                    "Yes"
                  ),
                  React.createElement(
                    "button",
                    {
                      type: "button",
                      className: `btn btn-sm ${
                        value?.muted === false
                          ? "btn-primary"
                          : "btn-outline-secondary"
                      }`,
                      onClick: () => onChange({ ...value, muted: false })
                    },
                    "No"
                  )
                )
              ),
              React.createElement(VideoAltTextField, { value, onChange })
            );
          }
        },
        padding: {
          type: "select",
          label: "Padding",
          options: [
            { label: "Small (2rem / 32px)", value: "32px" },
            { label: "Medium (4rem / 64px)", value: "64px" },
            { label: "Large (6rem / 96px)", value: "96px" },
            { label: "Extra Large (8rem / 128px)", value: "128px" },
            { label: "XXL (10rem / 160px)", value: "160px" },
            { label: "XXXL (12rem / 192px)", value: "192px" }
          ]
        },
        gradient: {
          type: "object",
          label: "Gradient Overlay",
          objectFields: {
            enabled: {
              type: "radio",
              label: "Enable Gradient",
              options: [
                { label: "On", value: true },
                { label: "Off", value: false }
              ]
            },
            color: {
              type: "custom",
              label: "Overlay Color",
              render: (props) =>
                React.createElement(ColorPickerField, {
                  ...props,
                  showAlpha: true
                })
            },
            type: {
              type: "select",
              label: "Overlay Type",
              options: [
                { label: "Gradient", value: "gradient" },
                { label: "No gradient - solid", value: "solid" }
              ]
            },
            direction: {
              type: "select",
              label: "Gradient Direction",
              options: [
                { label: "To Left", value: "to left" },
                { label: "To Right", value: "to right" },
                { label: "To Top", value: "to top" },
                { label: "To Bottom", value: "to bottom" },
                { label: "To Top Left", value: "to top left" },
                { label: "To Top Right", value: "to top right" },
                { label: "To Bottom Left", value: "to bottom left" },
                { label: "To Bottom Right", value: "to bottom right" }
              ]
            }
          }
        },
        textOverlay: {
          type: "object",
          label: "Text Overlay Box",
          objectFields: {
            enabled: {
              type: "radio",
              label: "Enable Text Overlay Box",
              options: [
                { label: "On", value: true },
                { label: "Off", value: false }
              ]
            },
            backgroundColor: {
              type: "custom",
              label: "Background Color",
              render: (props) =>
                React.createElement(ColorPickerField, {
                  ...props,
                  showAlpha: true
                })
            },
            borderRadius: {
              type: "select",
              label: "Border Radius",
              options: [
                { label: "None (0px)", value: "0" },
                { label: "Small (4px)", value: "4" },
                { label: "Medium (8px)", value: "8" },
                { label: "Large (12px)", value: "12" },
                { label: "XL (16px)", value: "16" },
                { label: "XXL (24px)", value: "24" },
                { label: "Round (50px)", value: "50" }
              ]
            },
            shadow: {
              type: "radio",
              label: "Shadow",
              options: [
                { label: "On", value: true },
                { label: "Off", value: false }
              ]
            },
            padding: {
              type: "select",
              label: "Padding",
              options: [
                { label: "Small (1rem)", value: "1rem" },
                { label: "Medium (1.5rem)", value: "1.5rem" },
                { label: "Large (2rem)", value: "2rem" },
                { label: "XL (2.5rem)", value: "2.5rem" },
                { label: "XXL (3rem)", value: "3rem" }
              ]
            },
            overlayAlign: {
              type: "select",
              label: "Overlay Box Alignment",
              options: [
                { label: "Left", value: "left" },
                { label: "Center", value: "center" },
                { label: "Right", value: "right" }
              ]
            },
            overlayWidth: {
              type: "select",
              label: "Overlay Box Width (Desktop)",
              options: [
                { label: "Full Width", value: "full" },
                { label: "Narrow (400px)", value: "narrow" },
                { label: "Medium (600px)", value: "medium" },
                { label: "Wide (800px)", value: "wide" },
                { label: "Extra Wide (1000px)", value: "xwide" },
                { label: "Auto (Natural)", value: "auto" }
              ]
            },
            align: {
              type: "radio",
              label: "Alignment within hero",
              options: [
                { label: "Left", value: "left" },
                { label: "Center", value: "center" }
              ]
            }
          }
        }
      },
      defaultProps: {
        title: "Welcome to Our Community",
        headingLevel: "h2",
        subtitle:
          "<p>Engage with your local government and make your voice heard</p>",
        buttons: [
          {
            label: "Visit GitHub",
            href: "https://github.com",
            variant: "primary"
          },
          { label: "Edit this page", href: "#", variant: "secondary" }
        ],
        buttonAlign: "left",
        backgroundType: "none",
        image: {
          url: "",
          mode: "inline"
        },
        video: {
          url: "",
          poster: "",
          autoplay: true,
          loop: true,
          muted: true,
          alt: ""
        },
        padding: "128px",
        titleTypography: {
          fontSize: "40",
          fontWeight: "700",
          titleColor: "#000000"
        },
        descriptionTypography: {
          fontSize: "18",
          fontWeight: "400",
          descriptionColor: "#000000"
        },
        gradient: {
          enabled: false,
          color: "rgba(255, 255, 255, 1)",
          type: "gradient",
          direction: "to left"
        },
        textOverlay: {
          enabled: false,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          borderRadius: "8",
          shadow: true,
          padding: "1.5rem",
          overlayAlign: "left",
          overlayWidth: "full",
          align: "left"
        }
      },
      render: ({
        title,
        headingLevel,
        description,
        buttons: configuredButtons,
        buttonAlign,
        backgroundType,
        image,
        video,
        titleTypography,
        descriptionTypography,
        gradient,
        textOverlay
      }) => {
        const titleLevel = headingLevel || "h2";
        // Hide signed-out-only buttons (e.g. "Sign in") for signed-in visitors
        // and swap in a name/profile button. See utils/authButtons.js.
        const buttons = applyAuthToButtons(configuredButtons);
        // Get align from textOverlay, default to 'left'
        const align = textOverlay?.align || "left";
        // Only use image/video if backgroundType matches
        const shouldUseImage = backgroundType === "image";
        const shouldUseVideo = backgroundType === "video";

        const isInline = shouldUseImage && image && image.mode === "inline";
        const isBg = shouldUseImage && image && image.mode === "bg";
        const imageUrl = shouldUseImage && image && image.url;
        const videoUrl = shouldUseVideo && video && video.url;
        const videoPoster = shouldUseVideo && video && video.poster;
        const isVideoBg = shouldUseVideo && videoUrl && videoUrl.trim() !== "";

        /**
         * Use separate colors for title and description
         */
        const titleColor = titleTypography?.titleColor || "#000000";
        const descriptionColor =
          descriptionTypography?.descriptionColor || "#000000";

        /**
         * Get typography settings with defaults
         */
        const titleSize = titleTypography?.fontSize
          ? `${titleTypography.fontSize}px`
          : "40px";
        const titleWeight = titleTypography?.fontWeight || "700";
        const descSize = descriptionTypography?.fontSize
          ? `${descriptionTypography.fontSize}px`
          : "18px";
        const descWeight = descriptionTypography?.fontWeight || "400";

        /**
         * Calculate effective background color for contrast checking
         * Priority: textOverlay > gradient > white
         */
        /**
         * const effectiveBgColor =
         *   textOverlay && textOverlay.enabled && textOverlay.backgroundColor
         *     ? textOverlay.backgroundColor
         *     : gradient && gradient.enabled && gradient.color
         *     ? gradient.color
         *     : "#ffffff"; // Default white background
         */

        // Responsive padding: smaller on mobile using clamp for fluid scaling
        const containerStyle = {
          paddingTop: "clamp(3rem, 8vw, 8rem)",
          paddingBottom: "clamp(3rem, 8vw, 8rem)",
          paddingLeft: "clamp(1rem, 4vw, 1.5rem)",
          paddingRight: "clamp(1rem, 4vw, 1.5rem)",
          position: "relative",
          boxSizing: "border-box"
        };

        if (isBg && imageUrl) {
          containerStyle.backgroundImage = `url(${imageUrl})`;
          containerStyle.backgroundSize = "cover";
          containerStyle.backgroundPosition = "center";
          // Keep custom text color even in bg mode
        }

        const contentWrapperStyle = {
          display: "flex",
          flexDirection: align === "center" ? "column" : "row",
          alignItems: align === "center" ? "center" : "flex-start",
          gap: "clamp(1rem, 4vw, 2rem)",
          maxWidth: "1200px",
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
          width: "100%",
          boxSizing: "border-box"
        };

        const textContentStyle = {
          flex: isInline && imageUrl ? "1" : "none",
          textAlign: align === "center" ? "center" : "left",
          maxWidth: align === "center" ? "min(800px, 100%)" : "100%",
          width: "100%",
          boxSizing: "border-box",
          minWidth: "0" // Prevent flex overflow
        };

        // Get overlay alignment (defaults to 'left' if not set)
        const overlayAlign = textOverlay?.overlayAlign || "left";

        // Convert padding to responsive padding for overlay box
        function getOverlayPadding() {
          const defaultPadding = textOverlay?.padding || "1.5rem";
          // If it's a rem value, convert to responsive clamp
          const remMatch = defaultPadding.match(/([\d.]+)rem/);
          if (remMatch) {
            const remValue = parseFloat(remMatch[1]);
            const MIN_REM_VALUE = OPACITY_075;
            const REM_MULTIPLIER_MIN = OPACITY_05;
            const REM_MULTIPLIER_MAX = OPACITY_075;
            return `clamp(${Math.max(
              MIN_REM_VALUE,
              remValue * REM_MULTIPLIER_MIN
            )}rem, ${remValue * REM_MULTIPLIER_MAX}vw, ${remValue}rem)`;
          }
          // If it's px, convert to responsive
          const pxMatch = defaultPadding.match(/(\d+)px/);
          if (pxMatch) {
            const pxValue = parseInt(pxMatch[1], PARSE_INT_RADIX_DECIMAL);
            const MIN_PX_VALUE = FONT_SIZE_PX_12;
            const PX_MULTIPLIER_MIN = OPACITY_05;
            const PX_MULTIPLIER_MAX = OPACITY_075;
            return `clamp(${Math.max(
              MIN_PX_VALUE,
              pxValue * PX_MULTIPLIER_MIN
            )}px, ${pxValue * PX_MULTIPLIER_MAX}vw, ${pxValue}px)`;
          }
          return defaultPadding;
        }

        // Determine overlay width based on setting
        const overlayWidth = textOverlay?.overlayWidth || "full";

        // Width mapping for desktop
        const widthMap = {
          full: "100%",
          narrow: "400px",
          medium: "600px",
          wide: "800px",
          xwide: "1000px",
          auto: "auto"
        };

        // Style for overlay box container based on alignment and width
        const overlayBoxStyle = {
          backgroundColor:
            textOverlay.backgroundColor || "rgba(255, 255, 255, 0.9)",
          borderRadius: textOverlay.borderRadius
            ? `${textOverlay.borderRadius}px`
            : "8px",
          padding: getOverlayPadding(),
          boxShadow: textOverlay.shadow
            ? "0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)"
            : "none",
          display: "inline-block",
          // Mobile: always full width
          width: "100%",
          maxWidth: "100%",
          minWidth: "0", // Prevent overflow
          boxSizing: "border-box",
          textAlign:
            overlayAlign === "center"
              ? "center"
              : overlayAlign === "right"
              ? "right"
              : "left",
          wordWrap: "break-word",
          overflowWrap: "break-word"
        };

        // Generate className for desktop width styling
        const overlayWidthClass = `hero-overlay-width-${overlayWidth}`;

        // Create style tag for desktop width media query if not full width
        let widthStyleTag = null;
        if (overlayWidth !== "full") {
          const desktopWidth = widthMap[overlayWidth] || "100%";
          const desktopMaxWidth =
            overlayWidth === "auto"
              ? "none"
              : `min(${desktopWidth}, calc(100% - 2rem))`;

          const styleId = `hero-overlay-width-style-${overlayWidth}`;
          widthStyleTag = React.createElement("style", {
            key: styleId,
            dangerouslySetInnerHTML: {
              __html: `
                                @media (min-width: 992px) {
                                    .${overlayWidthClass} {
                                        width: ${
                                          overlayWidth === "auto"
                                            ? "auto"
                                            : desktopWidth
                                        } !important;
                                        max-width: ${desktopMaxWidth} !important;
                                    }
                                }
                            `
            }
          });
        }

        // Container style for overlay box alignment
        const overlayContainerStyle = {
          width: "100%",
          display: "flex",
          justifyContent:
            overlayAlign === "center"
              ? "center"
              : overlayAlign === "right"
              ? "flex-end"
              : "flex-start",
          paddingLeft: "clamp(0.5rem, 2vw, 1rem)",
          paddingRight: "clamp(0.5rem, 2vw, 1rem)",
          boxSizing: "border-box"
        };

        return React.createElement(
          React.Fragment,
          {},
          // Add style tag for desktop width if needed
          widthStyleTag,
          React.createElement(
            "div",
            {
              style: containerStyle
            },
            // Video background (takes precedence over image background)
            isVideoBg &&
              React.createElement(
                "video",
                {
                  autoPlay: video.autoplay !== false,
                  loop: video.loop !== false,
                  muted: video.muted !== false,
                  playsInline: true,
                  ...videoA11yProps(video),
                  poster: videoPoster || imageUrl || void 0,
                  style: {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    zIndex: VIDEO_Z_INDEX,
                    pointerEvents: "none"
                  }
                },
                React.createElement("source", {
                  src: videoUrl,
                  type: "video/mp4"
                }),
                React.createElement("source", {
                  src: videoUrl,
                  type: "video/webm"
                }),
                React.createElement("source", {
                  src: videoUrl,
                  type: "video/ogg"
                })
              ),
            // Gradient/overlay overlay (only show if enabled)
            gradient &&
              gradient.enabled &&
              (() => {
                const overlayStyle = {
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  pointerEvents: "none",
                  zIndex: 0
                };

                if (gradient.type === "solid") {
                  // Solid color overlay
                  overlayStyle.backgroundColor =
                    gradient.color || "rgba(255, 255, 255, 1)";
                } else {
                  // Gradient overlay with configurable direction and transparency
                  const gradientColor =
                    gradient.color || "rgba(255, 255, 255, 1)";
                  const direction = gradient.direction || "to left";

                  // Check if color is rgba format
                  const rgbaMatch = gradientColor.match(
                    /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/
                  );
                  if (rgbaMatch) {
                    const r = rgbaMatch[RGBA_R_INDEX];
                    const g = rgbaMatch[RGBA_G_INDEX];
                    const b = rgbaMatch[RGBA_B_INDEX];
                    const a =
                      typeof rgbaMatch[RGBA_A_INDEX] !== "undefined"
                        ? parseFloat(rgbaMatch[RGBA_A_INDEX])
                        : 1;
                    // Create gradient from transparent to opaque
                    overlayStyle.background = `linear-gradient(${direction}, rgba(${r}, ${g}, ${b}, 0) 0%, rgba(${r}, ${g}, ${b}, ${a}) 100%)`;
                  } else {
                    // Fallback for hex colors - convert to rgba for transparency control
                    const hex = gradientColor.replace("#", "");
                    const r = parseInt(
                      hex.substr(HEX_COLOR_START_INDEX, HEX_COLOR_R_END_INDEX),
                      HEX_RADIX
                    );
                    const g = parseInt(
                      hex.substr(
                        HEX_COLOR_G_START_INDEX,
                        HEX_COLOR_R_END_INDEX
                      ),
                      HEX_RADIX
                    );
                    const b = parseInt(
                      hex.substr(
                        HEX_COLOR_B_START_INDEX,
                        HEX_COLOR_R_END_INDEX
                      ),
                      HEX_RADIX
                    );
                    overlayStyle.background = `linear-gradient(${direction}, rgba(${r}, ${g}, ${b}, 0) 0%, rgba(${r}, ${g}, ${b}, 1) 100%)`;
                  }
                }

                return React.createElement("div", { style: overlayStyle });
              })(),
            React.createElement(
              "div",
              {
                style: contentWrapperStyle
              },
              React.createElement(
                "div",
                {
                  style: textContentStyle
                },
                // Wrap text content in overlay box if enabled
                textOverlay && textOverlay.enabled
                  ? React.createElement(
                      "div",
                      {
                        style: overlayContainerStyle
                      },
                      React.createElement(
                        "div",
                        {
                          className: overlayWidthClass,
                          style: overlayBoxStyle
                        },
                        React.createElement(
                          titleLevel,
                          {
                            style: {
                              fontSize: titleSize,
                              fontWeight: titleWeight,
                              marginBottom: "1rem",
                              marginTop: 0,
                              color: titleColor,
                              textShadow: "none" // No text shadow when in overlay box
                            }
                          },
                          title
                        ),
                        React.createElement(
                          "p",
                          {
                            style: {
                              fontSize: descSize,
                              fontWeight: descWeight,
                              lineHeight: "1.6",
                              marginBottom:
                                buttons && buttons.length > 0 ? "2rem" : 0,
                              color: descriptionColor,
                              textShadow: "none" // No text shadow when in overlay box
                            }
                          },
                          description
                        ),
                        buttons &&
                          buttons.length > 0 &&
                          React.createElement(
                            "div",
                            {
                              style: {
                                display: "flex",
                                gap: "clamp(0.5rem, 2vw, 1rem)",
                                flexWrap: "wrap",
                                justifyContent:
                                  buttonAlign === "center"
                                    ? "center"
                                    : buttonAlign === "right"
                                    ? "flex-end"
                                    : "flex-start",
                                width: "100%",
                                boxSizing: "border-box"
                              }
                            },
                            buttons.map((button, idx) => {
                              const btnClasses = [
                                "btn",
                                `btn-${button.variant || "primary"}`,
                                "btn-lg"
                              ];

                              return React.createElement(
                                "a",
                                {
                                  key: idx,
                                  href: button.href || "#",
                                  className: btnClasses.join(" "),
                                  role: "button",
                                  style: {
                                    textDecoration: "none"
                                  }
                                },
                                button.label || "Button"
                              );
                            })
                          )
                      )
                    )
                  : React.createElement(
                      React.Fragment,
                      {},
                      React.createElement(
                        titleLevel,
                        {
                          style: {
                            fontSize: titleSize,
                            fontWeight: titleWeight,
                            marginBottom: "1rem",
                            marginTop: 0,
                            color: titleColor,
                            // Apply text shadow only if there's a background image/video, text is white, and no overlay is enabled
                            textShadow:
                              (isBg || isVideoBg) &&
                              titleColor === "#ffffff" &&
                              !(gradient && gradient.enabled)
                                ? "0 2px 4px rgba(0,0,0,0.5)"
                                : "none"
                          }
                        },
                        title
                      ),
                      React.createElement(
                        "p",
                        {
                          style: {
                            fontSize: descSize,
                            fontWeight: descWeight,
                            lineHeight: "1.6",
                            marginBottom:
                              buttons && buttons.length > 0 ? "2rem" : 0,
                            color: descriptionColor,
                            // Apply text shadow only if there's a background image/video, text is white, and no overlay is enabled
                            textShadow:
                              (isBg || isVideoBg) &&
                              descriptionColor === "#ffffff" &&
                              !(gradient && gradient.enabled)
                                ? "0 1px 2px rgba(0,0,0,0.5)"
                                : "none"
                          }
                        },
                        description
                      ),
                      buttons &&
                        buttons.length > 0 &&
                        React.createElement(
                          "div",
                          {
                            style: {
                              display: "flex",
                              gap: "clamp(0.5rem, 2vw, 1rem)",
                              flexWrap: "wrap",
                              justifyContent:
                                buttonAlign === "center"
                                  ? "center"
                                  : buttonAlign === "right"
                                  ? "flex-end"
                                  : "flex-start",
                              width: "100%",
                              boxSizing: "border-box"
                            }
                          },
                          buttons.map((button, idx) => {
                            const btnClasses = [
                              "btn",
                              `btn-${button.variant || "primary"}`,
                              "btn-lg"
                            ];

                            return React.createElement(
                              "a",
                              {
                                key: idx,
                                href: button.href || "#",
                                className: btnClasses.join(" "),
                                role: "button",
                                style: {
                                  textDecoration: "none"
                                }
                              },
                              button.label || "Button"
                            );
                          })
                        )
                    )
              ),
              isInline &&
                imageUrl &&
                React.createElement(
                  "div",
                  {
                    style: {
                      flex: "1",
                      minWidth: "clamp(250px, 40vw, 300px)",
                      maxWidth: "100%",
                      boxSizing: "border-box"
                    }
                  },
                  React.createElement("img", {
                    src: imageUrl,
                    alt: title,
                    style: {
                      width: "100%",
                      height: "auto",
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                    }
                  })
                )
            )
          )
        );
      }
    },
    CardContainer: {
      label: "Card Container",
      fields: {
        padding: {
          type: "select",
          label: "Padding",
          options: [
            { label: "None (0px)", value: "0" },
            { label: "Small (8px)", value: "8" },
            { label: "Medium (16px)", value: "16" },
            { label: "Large (24px)", value: "24" },
            { label: "Extra Large (32px)", value: "32" },
            { label: "XXL (48px)", value: "48" }
          ]
        },
        shadow: {
          type: "radio",
          label: "Shadow",
          options: [
            { label: "On", value: true },
            { label: "Off", value: false }
          ]
        }
      },
      defaultProps: {
        padding: "24",
        shadow: true
      },
      zones: {
        "card-content": { title: "Card Content" }
      },
      render: function ({ padding, shadow, puck }) {
        const { renderDropZone } = puck || {};

        if (!renderDropZone) {
          return React.createElement(
            "div",
            {
              style: {
                padding: "2rem",
                backgroundColor: "#f8f9fa",
                border: "2px dashed #dee2e6",
                borderRadius: `${BORDER_RADIUS_4_PX}px`,
                textAlign: "center"
              }
            },
            "Drop zones not available"
          );
        }

        const paddingValue = padding ? `${padding}px` : "24px";
        const shadowStyle = shadow
          ? "0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)"
          : "none";

        return React.createElement(
          "div",
          {
            className: "card-container",
            style: {
              padding: paddingValue,
              marginBottom: "1rem",
              border: "1px solid #dee2e6",
              borderRadius: "0.375rem",
              backgroundColor: "#fff",
              boxShadow: shadowStyle,
              minHeight: "100px",
              position: "relative"
            }
          },
          renderDropZone({ zone: "card-content" })
        );
      }
    },
    Container: createContainerBlock(
      ColorPickerField,
      MediaField,
      VideoField,
      () => {
        if (window.getPuckEditorData) {
          return window.getPuckEditorData();
        }
        return null;
      }
    ),
    Stats: {
      label: "Stats Bar",
      fields: {
        hubId: {
          type: "custom",
          label: "# Hub ID (auto-detected)",
          render: ({ field, value, onChange }) => {
            function getAutoHubId() {
              if (window.modelData && window.modelData.hubId) {
                const id = window.modelData.hubId;
                if (id && id > 0) {
                  return id;
                }
              }
              if (window.modelData && window.modelData.hub) {
                const hub = window.modelData.hub;
                if (
                  typeof hub.id !== "undefined" &&
                  hub.id !== null &&
                  hub.id > 0
                ) {
                  return hub.id;
                }
                if (
                  typeof hub.Id !== "undefined" &&
                  hub.Id !== null &&
                  hub.Id > 0
                ) {
                  return hub.Id;
                }
              }
              return value || 0;
            }

            const autoHubId = getAutoHubId();
            const hasUpdatedRef = React.useRef(false);

            React.useEffect(() => {
              if (
                !hasUpdatedRef.current &&
                autoHubId > 0 &&
                autoHubId !== value
              ) {
                onChange(autoHubId);
                hasUpdatedRef.current = true;
              }
            }, [autoHubId, value, onChange]);

            return React.createElement(
              "div",
              { className: "puck-field" },
              React.createElement(
                "label",
                { className: "puck-field-label" },
                field.label
              ),
              React.createElement("input", {
                type: "text",
                value: autoHubId > 0 ? autoHubId : "Auto-detecting...",
                className: "form-control",
                readOnly: true,
                disabled: true,
                style: { backgroundColor: "#e9ecef", cursor: "not-allowed" }
              }),
              autoHubId === 0 &&
                React.createElement(
                  "small",
                  {
                    className: "form-text text-warning"
                  },
                  "Hub ID will be auto-detected when available"
                )
            );
          }
        },
        selectedStats: {
          type: "custom",
          label: "Select and Order Stats",
          render: ({ field, value, onChange }) => {
            return React.createElement(StatsBarField, {
              field,
              value,
              onChange
            });
          }
        },
        statColor: {
          type: "custom",
          label: "Stat Color",
          render: ({ field, value, onChange }) => {
            return React.createElement(ColorPickerFieldLocal, {
              field,
              value,
              onChange,
              showAlpha: false
            });
          }
        }
      },
      defaultProps: {
        hubId: 0,
        selectedStats: ["responses", "views", "subscribers"],
        statColor: "#000000"
      },
      render: ({ hubId, selectedStats, statColor, puck }) => {
        return React.createElement(StatsBarRenderer, {
          hubId,
          selectedStats,
          statColor,
          puck
        });
      }
    },
    Logos: {
      label: "Logos",
      fields: {
        items: {
          type: "array",
          label: "Logos",
          arrayFields: {
            src: {
              type: "custom",
              label: "Logo Image",
              render: MediaFieldLocal
            },
            alt: { type: "text", label: "Alt Text" }
          },
          defaultItemProps: {
            src: "",
            alt: "Logo"
          }
        }
      },
      defaultProps: {
        items: []
      },
      render: ({ items }) => {
        return React.createElement(
          "div",
          {
            style: {
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              alignItems: "center",
              gap: "2rem",
              padding: "2rem",
              marginBottom: "1rem"
            }
          },
          items.map((item, idx) =>
            item.src
              ? React.createElement("img", {
                  key: idx,
                  src: item.src,
                  alt: item.alt || "Logo",
                  style: {
                    maxHeight: "60px",
                    maxWidth: "150px",
                    objectFit: "contain",
                    filter: "grayscale(100%)",
                    opacity: OPACITY_70
                  }
                })
              : React.createElement(
                  "div",
                  {
                    key: idx,
                    style: {
                      width: "150px",
                      height: "60px",
                      backgroundColor: "#f8f9fa",
                      border: "2px dashed #dee2e6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#6c757d",
                      fontSize: "0.75rem"
                    }
                  },
                  "Add Logo"
                )
          )
        );
      }
    },
    ButtonGroup: {
      label: "Button Group",
      fields: {
        buttons: {
          type: "array",
          label: "Buttons",
          arrayFields: {
            text: { type: "text", label: "Text" },
            href: { type: "text", label: "Link URL" },
            variant: {
              type: "select",
              label: "Style",
              options: [
                { label: "Primary", value: "primary" },
                { label: "Secondary", value: "secondary" },
                { label: "Success", value: "success" },
                { label: "Danger", value: "danger" },
                { label: "Warning", value: "warning" },
                { label: "Info", value: "info" },
                { label: "Light", value: "light" },
                { label: "Dark", value: "dark" },
                { label: "Outline Primary", value: "outline-primary" },
                { label: "Outline Secondary", value: "outline-secondary" }
              ]
            }
          },
          defaultItemProps: {
            text: "Button",
            href: "#",
            variant: "primary"
          }
        },
        size: {
          type: "select",
          label: "Size",
          options: [
            { label: "Small", value: "sm" },
            { label: "Normal", value: "" },
            { label: "Large", value: "lg" }
          ]
        },
        orientation: {
          type: "select",
          label: "Orientation",
          options: [
            { label: "Horizontal", value: "horizontal" },
            { label: "Vertical", value: "vertical" }
          ]
        },
        align: {
          type: "select",
          label: "Alignment",
          options: [
            { label: "Left", value: "left" },
            { label: "Center", value: "center" },
            { label: "Right", value: "right" }
          ]
        }
      },
      defaultProps: {
        buttons: [
          { text: "Button 1", href: "#", variant: "primary" },
          { text: "Button 2", href: "#", variant: "secondary" }
        ],
        size: "",
        orientation: "horizontal",
        align: "left"
      },
      render: ({ buttons, size, orientation, align }) => {
        const groupClasses = ["btn-group"];
        if (orientation === "vertical") {
          groupClasses.push("btn-group-vertical");
        }
        if (size) {
          groupClasses.push(`btn-group-${size}`);
        }

        return React.createElement(
          "div",
          {
            style: { textAlign: align, marginBottom: "1rem" },
            role: "group"
          },
          React.createElement(
            "div",
            {
              className: groupClasses.join(" "),
              role: "group",
              "aria-label": "Button group"
            },
            buttons.map((button, idx) => {
              const btnClasses = ["btn", `btn-${button.variant || "primary"}`];
              if (size) {
                btnClasses.push(`btn-${size}`);
              }

              return React.createElement(
                "a",
                {
                  key: idx,
                  href: button.href || "#",
                  className: btnClasses.join(" "),
                  role: "button"
                },
                button.text || "Button"
              );
            })
          )
        );
      }
    },
    Testimonial: {
      label: "Testimonial",
      fields: {
        quote: {
          type: "textarea",
          label: "Quote"
        },
        author: {
          type: "text",
          label: "Author Name"
        },
        role: {
          type: "text",
          label: "Role/Title"
        },
        company: {
          type: "text",
          label: "Company"
        },
        image: {
          type: "custom",
          label: "Author Image",
          render: MediaFieldLocal
        },
        imagePosition: {
          type: "select",
          label: "Image Position",
          options: [
            { label: "Left", value: "left" },
            { label: "Right", value: "right" },
            { label: "Top", value: "top" }
          ]
        }
      },
      defaultProps: {
        quote: "This is an amazing community that our family loves!",
        author: "John Doe",
        role: "Resident",
        company: "City of Alpine",
        image: "",
        imagePosition: "left"
      },
      render: function ({
        quote,
        author,
        role,
        company,
        image,
        imagePosition
      }) {
        // Safely extract string values (handle objects that might be passed)
        function getStringValue(value, fieldName) {
          // Handle null/undefined
          if (value == null) {
            return "";
          }

          // Handle primitives
          if (typeof value === "string") {
            return value.trim();
          }
          if (typeof value === "number") {
            return String(value);
          }
          if (typeof value === "boolean") {
            return String(value);
          }

          // Handle objects
          if (typeof value === "object") {
            // Handle arrays
            if (Array.isArray(value)) {
              const result = value
                .map(function (v) {
                  return getStringValue(v, fieldName);
                })
                .filter(function (v) {
                  return v;
                })
                .join(" ");
              return result || "";
            }

            // Try common object properties that Puck might use (checking for truthy values)
            if (
              typeof value.text !== "undefined" &&
              value.text !== null &&
              value.text !== ""
            ) {
              const text = getStringValue(value.text, fieldName);
              if (text) {
                return text;
              }
            }
            if (
              typeof value.value !== "undefined" &&
              value.value !== null &&
              value.value !== ""
            ) {
              const val = getStringValue(value.value, fieldName);
              if (val) {
                return val;
              }
            }
            if (
              typeof value.content !== "undefined" &&
              value.content !== null &&
              value.content !== ""
            ) {
              const content = getStringValue(value.content, fieldName);
              if (content) {
                return content;
              }
            }
            if (
              typeof value.label !== "undefined" &&
              value.label !== null &&
              value.label !== ""
            ) {
              const label = getStringValue(value.label, fieldName);
              if (label) {
                return label;
              }
            }

            // Check if it's a React element or similar
            if (value.props) {
              if (typeof value.props.children !== "undefined") {
                const children = value.props.children;
                if (typeof children === "string") {
                  return children.trim();
                }
                if (Array.isArray(children)) {
                  const result = children
                    .map(function (c) {
                      if (typeof c === "string") {
                        return c;
                      }
                      if (
                        c &&
                        typeof c === "object" &&
                        c.props &&
                        c.props.children
                      ) {
                        return getStringValue(c.props.children, fieldName);
                      }
                      return "";
                    })
                    .filter(function (c) {
                      return c;
                    })
                    .join("");
                  if (result) {
                    return result;
                  }
                }
              }
              // Check other props that might contain the value
              if (
                typeof value.props.value !== "undefined" &&
                value.props.value !== null
              ) {
                const propVal = getStringValue(value.props.value, fieldName);
                if (propVal) {
                  return propVal;
                }
              }
              if (
                typeof value.props.text !== "undefined" &&
                value.props.text !== null
              ) {
                const propText = getStringValue(value.props.text, fieldName);
                if (propText) {
                  return propText;
                }
              }
            }

            // Check for toString method (but avoid [object Object])
            if (value.toString && typeof value.toString === "function") {
              try {
                const str = value.toString();
                if (
                  str &&
                  str !== "[object Object]" &&
                  str !== "[object Array]"
                ) {
                  return str;
                }
              } catch (e) {
                // Ignore errors
              }
            }

            // Last resort: try to extract any string-like property (including empty strings)
            for (const key in value) {
              if (Object.prototype.hasOwnProperty.call(value, key)) {
                const prop = value[key];
                if (typeof prop === "string" && prop !== "") {
                  return prop.trim();
                }
                // Recursively check nested objects
                if (
                  typeof prop === "object" &&
                  prop !== null &&
                  !Array.isArray(prop)
                ) {
                  const nested = getStringValue(prop, fieldName);
                  if (nested) {
                    return nested;
                  }
                }
              }
            }

            // If all else fails, return empty string instead of [object Object]
            return "";
          }

          // Fallback: try to convert to string
          try {
            const str = String(value);
            return str !== "[object Object]" && str !== "[object Array]"
              ? str
              : "";
          } catch (e) {
            return "";
          }
        }

        const quoteText = getStringValue(quote, "quote");
        const authorText = getStringValue(author, "author");
        const roleText = getStringValue(role, "role");
        const companyText = getStringValue(company, "company");

        const isHorizontal =
          imagePosition === "left" || imagePosition === "right";

        const imageElement =
          image &&
          React.createElement(
            "div",
            {
              className: isHorizontal ? "col-md-3" : "text-center mb-3",
              style: isHorizontal ? {} : { padding: "1rem" }
            },
            React.createElement("img", {
              src: image,
              alt: authorText,
              className: "rounded-circle",
              style: {
                width: isHorizontal ? "100px" : "80px",
                height: isHorizontal ? "100px" : "80px",
                objectFit: "cover"
              }
            })
          );

        const contentElement = React.createElement(
          "div",
          {
            className: isHorizontal ? "col-md-9" : "",
            style: {
              display: "flex",
              flexDirection: "column",
              justifyContent: "center"
            }
          },
          React.createElement(
            "blockquote",
            {
              className: "mb-3",
              style: {
                fontSize: "1.1rem",
                fontStyle: "italic",
                color: "#495057"
              }
            },
            quoteText ? `"${quoteText}"` : ""
          ),
          React.createElement(
            "div",
            { className: "testimonial-author" },
            authorText &&
              React.createElement(
                "strong",
                { style: { display: "block", marginBottom: "0.25rem" } },
                authorText
              ),
            (roleText || companyText) &&
              React.createElement(
                "span",
                {
                  style: { color: "#6c757d", fontSize: "0.9rem" }
                },
                roleText + (companyText ? `, ${companyText}` : "")
              )
          )
        );

        const children =
          imagePosition === "right"
            ? [contentElement, imageElement]
            : imagePosition === "top"
            ? [imageElement, contentElement]
            : [imageElement, contentElement];

        return React.createElement(
          "div",
          {
            className: "card testimonial-card",
            style: {
              padding: "2rem",
              marginBottom: "1rem",
              border: "1px solid #dee2e6",
              borderRadius: "0.375rem",
              backgroundColor: "#fff"
            }
          },
          React.createElement(
            "div",
            {
              className: isHorizontal ? "row align-items-center" : ""
            },
            ...children.filter(Boolean)
          )
        );
      }
    },
    Profile: {
      label: "Profile",
      fields: {
        name: {
          type: "text",
          label: "Name",
          required: true
        },
        role: {
          type: "text",
          label: "Role",
          required: true
        },
        organization: {
          type: "text",
          label: "Organization"
        },
        phone: {
          type: "text",
          label: "Phone"
        },
        email: {
          type: "text",
          label: "Email"
        },
        image: {
          type: "custom",
          label: "Profile Image",
          render: MediaFieldLocal
        },
        bio: {
          type: "custom",
          label: "Bio",
          render: RichTextField
        },
        layout: {
          type: "select",
          label: "Layout",
          options: [
            { label: "Horizontal", value: "horizontal" },
            { label: "Vertical", value: "vertical" },
            { label: "Centered Top", value: "centered-top" },
            { label: "Compact Horizontal", value: "compact-horizontal" }
          ]
        },
        headingLevel: headingLevelField("Name Level"),
        imageSize: {
          type: "select",
          label: "Image Size",
          options: [
            { label: "Small (80px)", value: "80" },
            { label: "Medium (120px)", value: "120" },
            { label: "Large (150px)", value: "150" },
            { label: "Extra Large (200px)", value: "200" }
          ]
        }
      },
      defaultProps: {
        name: "John Smith",
        role: "Communications Officer",
        organization: "Local Council",
        phone: "",
        email: "",
        image: "",
        bio: "<p>Type something</p>",
        headingLevel: "h3",
        layout: "horizontal",
        imageSize: String(DEFAULT_IMAGE_SIZE_PX)
      },
      render: function ({
        name: personName,
        role,
        organization,
        phone,
        email,
        image,
        bio,
        headingLevel,
        layout,
        imageSize
      }) {
        const nameLevel = headingLevel || "h3";
        // Safely extract string values
        function getStringValue(value) {
          if (value == null) {
            return "";
          }
          if (typeof value === "string") {
            return value.trim();
          }
          if (typeof value === "number") {
            return String(value);
          }
          if (typeof value === "boolean") {
            return String(value);
          }
          if (typeof value === "object") {
            if (Array.isArray(value)) {
              return value
                .map((v) => getStringValue(v))
                .filter((v) => v)
                .join(" ");
            }
            if (
              typeof value.text !== "undefined" &&
              value.text !== null &&
              value.text !== ""
            ) {
              return getStringValue(value.text);
            }
            if (
              typeof value.value !== "undefined" &&
              value.value !== null &&
              value.value !== ""
            ) {
              return getStringValue(value.value);
            }
            if (
              typeof value.content !== "undefined" &&
              value.content !== null &&
              value.content !== ""
            ) {
              return getStringValue(value.content);
            }
          }
          return "";
        }

        const nameText = getStringValue(personName);
        const roleText = getStringValue(role);
        const organizationText = getStringValue(organization);
        const phoneText = getStringValue(phone);
        const emailText = getStringValue(email);
        const imageUrl = image || "";
        // Ensure imageSizeNum is always a valid number, defaulting to 120 if invalid
        const parsedImageSize = parseInt(
          imageSize || String(DEFAULT_IMAGE_SIZE_PX),
          PARSE_INT_RADIX_DECIMAL
        );
        const imageSizeNum =
          isNaN(parsedImageSize) || parsedImageSize <= 0
            ? DEFAULT_IMAGE_SIZE_PX
            : parsedImageSize;

        const isHorizontal = layout === "horizontal";
        const isCenteredTop = layout === "centered-top";
        const isCompactHorizontal = layout === "compact-horizontal";

        // Create image element with appropriate styling for each layout
        function createImageElement(containerStyle, imageStyle) {
          return React.createElement(
            "div",
            {
              style: containerStyle
            },
            imageUrl
              ? React.createElement("img", {
                  src: imageUrl,
                  alt: nameText || "Profile picture",
                  className: "rounded-circle",
                  style: imageStyle
                })
              : React.createElement(
                  "div",
                  {
                    className:
                      "rounded-circle d-inline-flex align-items-center justify-content-center",
                    style: {
                      ...imageStyle,
                      backgroundColor: "#e9ecef",
                      color: "#6c757d"
                    }
                  },
                  React.createElement("i", {
                    className: "fas fa-camera fa-2x"
                  })
                )
          );
        }

        /**
         * Create content element with appropriate styling
         * WCAG 2.2 AAA: Normal text minimum 18px (1.125rem), Bold text minimum 18px, Small text minimum 16px (1rem)
         */
        function createContentElement(containerStyle, textAlign) {
          const align = textAlign == null ? "left" : textAlign;
          return React.createElement(
            "div",
            {
              style: containerStyle
            },
            nameText &&
              React.createElement(
                nameLevel,
                {
                  style: {
                    marginBottom: "0.5rem",
                    fontSize: isCompactHorizontal
                      ? FONT_SIZE_REM_1_125_STR
                      : FONT_SIZE_REM_1_75_STR, // WCAG AAA: Bold text min 18px (1.125rem)
                    fontWeight: "700",
                    color: "#0d6efd",
                    fontFamily: "sans-serif",
                    lineHeight: "1.2",
                    textAlign: align
                  }
                },
                nameText
              ),
            roleText &&
              React.createElement(
                "p",
                {
                  style: {
                    marginBottom: "0.25rem",
                    fontSize: FONT_SIZE_REM_1_125_STR, // WCAG AAA: Normal text min 18px (1.125rem)
                    fontWeight: "400",
                    color: "#495057",
                    fontFamily: "sans-serif",
                    lineHeight: "1.4",
                    textAlign: align
                  }
                },
                roleText
              ),
            organizationText &&
              React.createElement(
                "p",
                {
                  style: {
                    marginBottom:
                      emailText || phoneText
                        ? isCompactHorizontal
                          ? "0.5rem"
                          : "1rem"
                        : "0",
                    fontSize: isCompactHorizontal
                      ? "1rem"
                      : FONT_SIZE_REM_1_125_STR, // WCAG AAA: Normal text min 18px (1.125rem), compact uses 16px (1rem) as small text
                    fontWeight: "400",
                    color: "#495057",
                    fontFamily: "sans-serif",
                    lineHeight: "1.4",
                    textAlign: align
                  }
                },
                organizationText
              ),
            (phoneText || emailText) &&
              React.createElement(
                "div",
                {
                  className: "profile-contact",
                  style: {
                    fontSize: isCompactHorizontal
                      ? "1rem"
                      : FONT_SIZE_REM_1_125_STR, // WCAG AAA: Normal text min 18px (1.125rem), compact uses 16px (1rem)
                    fontFamily: "sans-serif",
                    textAlign: align
                  }
                },
                phoneText &&
                  React.createElement(
                    "div",
                    {
                      style: { marginBottom: "0.5rem" }
                    },
                    React.createElement(
                      "strong",
                      {
                        style: {
                          color: "#495057",
                          fontWeight: "600",
                          fontSize: FONT_SIZE_REM_1_125_STR // WCAG AAA: Bold text min 18px
                        }
                      },
                      "Phone: "
                    ),
                    React.createElement(
                      "a",
                      {
                        href: `tel:${phoneText}`,
                        style: {
                          color: "#0d6efd",
                          textDecoration: "none",
                          fontSize: FONT_SIZE_REM_1_125_STR // WCAG AAA: Normal text min 18px
                        }
                      },
                      phoneText
                    )
                  ),
                emailText &&
                  React.createElement(
                    "div",
                    {},
                    React.createElement(
                      "strong",
                      {
                        style: {
                          color: "#495057",
                          fontWeight: "600",
                          fontSize: FONT_SIZE_REM_1_125_STR // WCAG AAA: Bold text min 18px
                        }
                      },
                      "email:"
                    ),
                    React.createElement(
                      "a",
                      {
                        href: `mailto:${emailText}`,
                        style: {
                          color: "#0d6efd",
                          textDecoration: "none",
                          fontSize: FONT_SIZE_REM_1_125_STR // WCAG AAA: Normal text min 18px
                        }
                      },
                      emailText
                    )
                  )
              ),
            bio &&
              React.createElement("div", {
                className: "profile-bio",
                style: {
                  marginTop: isCompactHorizontal ? "0.5rem" : "1rem",
                  fontSize: isCompactHorizontal ? "1rem" : "1.125rem", // WCAG AAA: Normal text min 18px (1.125rem), compact uses 16px (1rem)
                  lineHeight: "1.6",
                  color: "#495057",
                  fontFamily: "sans-serif",
                  textAlign: align
                },
                dangerouslySetInnerHTML: { __html: sanitizeUserHtml(bio || "") }
              })
          );
        }

        // Centered Top Layout: Image centered at top (slightly overlapping), text centered below
        if (isCenteredTop) {
          const rawImageOffset = imageSizeNum / IMAGE_OFFSET_DIVISOR; // pixels
          const rawImageOffsetRem = rawImageOffset / PX_TO_REM_BASE; // convert to rem (assuming 16px = 1rem)
          // Ensure calculations are valid numbers, defaulting to safe values if NaN
          const imageOffset = isNaN(rawImageOffset) ? 60 : rawImageOffset;
          const imageOffsetRem = isNaN(rawImageOffsetRem)
            ? 3.75
            : rawImageOffsetRem;
          return React.createElement(
            "div",
            {
              className: "profile-card",
              style: {
                padding: "1rem 1.5rem",
                paddingTop: `${imageOffsetRem + IMAGE_OFFSET_PADDING_REM}rem`,
                marginTop: `${imageOffset}px`,
                marginBottom: "1rem",
                backgroundColor: "#ffffff",
                fontFamily: "sans-serif",
                borderRadius: "0.375rem",
                position: "relative"
              }
            },
            createImageElement(
              {
                position: "absolute",
                top: `-${imageOffset}px`,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 1
              },
              {
                width: `${imageSizeNum}px`,
                height: `${imageSizeNum}px`,
                objectFit: "cover",
                border: "3px solid #ffffff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
              }
            ),
            React.createElement(
              "div",
              {
                style: {
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center"
                }
              },
              nameText &&
                React.createElement(
                  nameLevel,
                  {
                    style: {
                      marginBottom: "0.25rem",
                      fontSize: FONT_SIZE_REM_1_75_STR, // WCAG AAA: Bold text - already compliant (28px > 18px)
                      fontWeight: "700",
                      color: "#0d6efd",
                      fontFamily: "sans-serif",
                      lineHeight: "1.2",
                      textAlign: "center"
                    }
                  },
                  nameText
                ),
              roleText &&
                React.createElement(
                  "p",
                  {
                    style: {
                      marginBottom: "0.125rem",
                      fontSize: FONT_SIZE_REM_1_125_STR, // WCAG AAA: Normal text min 18px (1.125rem)
                      fontWeight: "400",
                      color: "#495057",
                      fontFamily: "sans-serif",
                      lineHeight: "1.4",
                      textAlign: "center"
                    }
                  },
                  roleText
                ),
              roleText &&
                React.createElement("hr", {
                  style: {
                    width: "50px",
                    margin: "0.5rem auto",
                    border: "none",
                    borderTop: "1px solid #dee2e6"
                  }
                }),
              organizationText &&
                React.createElement(
                  "p",
                  {
                    style: {
                      marginBottom:
                        emailText || phoneText
                          ? "0.75rem"
                          : bio
                          ? "0.25rem"
                          : "0",
                      fontSize: FONT_SIZE_REM_1_125_STR, // WCAG AAA: Normal text min 18px (1.125rem)
                      fontWeight: "400",
                      color: "#495057",
                      fontFamily: "sans-serif",
                      lineHeight: "1.4",
                      textAlign: "center"
                    }
                  },
                  organizationText
                ),
              (phoneText || emailText) &&
                React.createElement(
                  "div",
                  {
                    className: "profile-contact",
                    style: {
                      fontSize: FONT_SIZE_REM_1_125_STR, // WCAG AAA: Normal text min 18px (1.125rem)
                      fontFamily: "sans-serif",
                      textAlign: "center",
                      marginBottom: bio ? "0.25rem" : "0"
                    }
                  },
                  phoneText &&
                    React.createElement(
                      "div",
                      {
                        style: { marginBottom: "0.25rem" }
                      },
                      React.createElement(
                        "strong",
                        {
                          style: {
                            color: "#495057",
                            fontWeight: "600",
                            fontSize: FONT_SIZE_REM_1_125_STR // WCAG AAA: Bold text min 18px
                          }
                        },
                        "Phone: "
                      ),
                      React.createElement(
                        "a",
                        {
                          href: `tel:${phoneText}`,
                          style: {
                            color: "#0d6efd",
                            textDecoration: "none",
                            fontSize: FONT_SIZE_REM_1_125_STR // WCAG AAA: Normal text min 18px
                          }
                        },
                        phoneText
                      )
                    ),
                  emailText &&
                    React.createElement(
                      "div",
                      {},
                      React.createElement(
                        "strong",
                        {
                          style: {
                            color: "#495057",
                            fontWeight: "600",
                            fontSize: FONT_SIZE_REM_1_125_STR // WCAG AAA: Bold text min 18px
                          }
                        },
                        "email:"
                      ),
                      React.createElement(
                        "a",
                        {
                          href: `mailto:${emailText}`,
                          style: {
                            color: "#0d6efd",
                            textDecoration: "none",
                            fontSize: FONT_SIZE_REM_1_125_STR // WCAG AAA: Normal text min 18px
                          }
                        },
                        emailText
                      )
                    )
                ),
              bio &&
                React.createElement("div", {
                  className: "profile-bio",
                  style: {
                    marginTop: "0.25rem",
                    fontSize: FONT_SIZE_REM_1_125_STR, // WCAG AAA: Normal text min 18px (1.125rem)
                    lineHeight: "1.6",
                    color: "#495057",
                    fontFamily: "sans-serif",
                    textAlign: "center"
                  },
                  dangerouslySetInnerHTML: {
                    __html: sanitizeUserHtml(bio || "")
                  }
                })
            )
          );
        }

        // Compact Horizontal Layout: More compact version of horizontal
        if (isCompactHorizontal) {
          const imageEl = createImageElement(
            {
              flexShrink: 0,
              marginRight: "1rem"
            },
            {
              width: `${Math.min(imageSizeNum, MAX_COMPACT_IMAGE_SIZE_PX)}px`,
              height: `${Math.min(imageSizeNum, MAX_COMPACT_IMAGE_SIZE_PX)}px`,
              objectFit: "cover",
              border: "2px solid #ffffff",
              boxShadow: "0 0 0 1px rgba(0,0,0,0.1)"
            }
          );

          const contentEl = createContentElement({
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center"
          });

          return React.createElement(
            "div",
            {
              className: "profile-card",
              style: {
                padding: "1rem 1.5rem",
                marginBottom: "1rem",
                backgroundColor: "#ffffff",
                fontFamily: "sans-serif",
                borderRadius: "0.375rem",
                display: "flex",
                alignItems: "flex-start"
              }
            },
            imageEl,
            contentEl
          );
        }

        // Horizontal Layout: Text left, image right
        if (isHorizontal) {
          const imageEl = createImageElement(
            {
              paddingLeft: "2rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            },
            {
              width: `${imageSizeNum}px`,
              height: `${imageSizeNum}px`,
              objectFit: "cover",
              border: "2px solid #ffffff",
              boxShadow: "0 0 0 1px rgba(0,0,0,0.1)"
            }
          );

          const contentEl = createContentElement({
            display: "flex",
            flexDirection: "column",
            justifyContent: "center"
          });

          return React.createElement(
            "div",
            {
              className: "profile-card",
              style: {
                padding: "2rem",
                marginBottom: "1rem",
                backgroundColor: "#ffffff",
                fontFamily: "sans-serif"
              }
            },
            React.createElement(
              "div",
              {
                className: "row align-items-center",
                style: { margin: 0 }
              },
              React.createElement("div", { className: "col-md" }, contentEl),
              React.createElement("div", { className: "col-md-auto" }, imageEl)
            )
          );
        }

        // Vertical Layout: Image top, text below
        const imageEl = createImageElement(
          {
            textAlign: "center",
            marginBottom: "2rem",
            padding: "1rem"
          },
          {
            width: `${imageSizeNum}px`,
            height: `${imageSizeNum}px`,
            objectFit: "cover",
            border: "2px solid #ffffff",
            boxShadow: "0 0 0 1px rgba(0,0,0,0.1)"
          }
        );

        const contentEl = createContentElement({
          display: "flex",
          flexDirection: "column",
          justifyContent: "center"
        });

        return React.createElement(
          "div",
          {
            className: "profile-card",
            style: {
              padding: "2rem",
              marginBottom: "1rem",
              backgroundColor: "#ffffff",
              fontFamily: "sans-serif"
            }
          },
          imageEl,
          contentEl
        );
      }
    },
    Accordion: {
      label: "Accordion",
      fields: {
        items: {
          type: "array",
          label: "Accordion Items",
          arrayFields: {
            title: { type: "text", label: "Title" },
            content: {
              type: "custom",
              label: "Content",
              render: RichTextField
            }
          },
          defaultItemProps: {
            title: "Accordion Item",
            content: "<p>Accordion content goes here</p>"
          }
        },
        headingLevel: headingLevelField("Item Heading Level"),
        allowMultiple: {
          type: "radio",
          label: "Allow Multiple Open",
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false }
          ]
        }
      },
      defaultProps: {
        items: [
          { title: "First Item", content: "<p>Content for the first item</p>" },
          {
            title: "Second Item",
            content: "<p>Content for the second item</p>"
          },
          { title: "Third Item", content: "<p>Content for the third item</p>" }
        ],
        headingLevel: "h2",
        allowMultiple: false
      },
      render: function ({ items, headingLevel, allowMultiple }) {
        const itemLevel = headingLevel || "h2";
        const accordionId = `accordion-${Math.random()
          .toString(RADIX_36)
          .substr(RANDOM_STRING_START, RANDOM_STRING_LENGTH)}`;

        return React.createElement(
          "div",
          {
            className: "accordion",
            id: accordionId,
            style: { marginBottom: "1rem" }
          },
          items.map(function (item, idx) {
            const headingId = `${accordionId}-heading-${idx}`;
            const collapseId = `${accordionId}-collapse-${idx}`;

            return React.createElement(
              "div",
              {
                key: idx,
                className: "accordion-item"
              },
              React.createElement(
                itemLevel,
                {
                  className: "accordion-header",
                  id: headingId
                },
                React.createElement(
                  "button",
                  {
                    className: `accordion-button${
                      idx === 0 ? "" : " collapsed"
                    }`,
                    type: "button",
                    "data-bs-toggle": "collapse",
                    "data-bs-target": `#${collapseId}`,
                    "aria-expanded": idx === 0 ? "true" : "false",
                    "aria-controls": collapseId
                  },
                  item.title
                )
              ),
              React.createElement(
                "div",
                {
                  id: collapseId,
                  className: `accordion-collapse collapse${
                    idx === 0 ? " show" : ""
                  }`,
                  "aria-labelledby": headingId,
                  "data-bs-parent": allowMultiple ? void 0 : `#${accordionId}`
                },
                React.createElement("div", {
                  className: "accordion-body",
                  dangerouslySetInnerHTML: {
                    __html: sanitizeUserHtml(item.content || "")
                  }
                })
              )
            );
          })
        );
      }
    },
    TabsBlock: {
      label: "Tabs",
      fields: {
        tabs: {
          type: "array",
          label: "Tabs",
          arrayFields: {
            label: { type: "text", label: "Tab Label" },
            content: {
              type: "custom",
              label: "Tab Content",
              render: RichTextField
            }
          },
          defaultItemProps: {
            label: "Tab",
            content: "<p>Tab content goes here</p>"
          }
        },
        style: {
          type: "select",
          label: "Style",
          options: [
            { label: "Tabs", value: "tabs" },
            { label: "Pills", value: "pills" }
          ]
        }
      },
      defaultProps: {
        tabs: [
          { label: "First Tab", content: "<p>Content for the first tab</p>" },
          { label: "Second Tab", content: "<p>Content for the second tab</p>" },
          { label: "Third Tab", content: "<p>Content for the third tab</p>" }
        ],
        style: "tabs"
      },
      render: ({ tabs, style }) => {
        const tabsId = `tabs-${Math.random()
          .toString(RADIX_36)
          .substr(RANDOM_STRING_START, RANDOM_STRING_LENGTH)}`;

        return React.createElement(
          "div",
          {
            style: { marginBottom: "1rem" }
          },
          React.createElement(
            "ul",
            {
              className: `nav nav-${style}`,
              id: tabsId,
              role: "tablist"
            },
            tabs.map((tab, idx) => {
              const tabId = `${tabsId}-tab-${idx}`;
              const paneId = `${tabsId}-pane-${idx}`;

              return React.createElement(
                "li",
                {
                  key: idx,
                  className: "nav-item",
                  role: "presentation"
                },
                React.createElement(
                  "button",
                  {
                    className: `nav-link${idx === 0 ? " active" : ""}`,
                    id: tabId,
                    "data-bs-toggle": "tab",
                    "data-bs-target": `#${paneId}`,
                    type: "button",
                    role: "tab",
                    "aria-controls": paneId,
                    "aria-selected": idx === 0 ? "true" : "false"
                  },
                  tab.label
                )
              );
            })
          ),
          React.createElement(
            "div",
            {
              className: "tab-content",
              style: {
                padding: "1rem",
                border: "1px solid #dee2e6",
                borderTop: "none"
              }
            },
            tabs.map((tab, idx) => {
              const tabId = `${tabsId}-tab-${idx}`;
              const paneId = `${tabsId}-pane-${idx}`;

              return React.createElement("div", {
                key: idx,
                className: `tab-pane fade${idx === 0 ? " show active" : ""}`,
                id: paneId,
                role: "tabpanel",
                "aria-labelledby": tabId,
                dangerouslySetInnerHTML: {
                  __html: sanitizeUserHtml(tab.content || "")
                }
              });
            })
          )
        );
      }
    },
    VideoEmbed: {
      label: "Video Embed",
      fields: {
        url: {
          type: "text",
          label: "Video URL",
          placeholder: "YouTube or Vimeo URL"
        },
        aspectRatio: {
          type: "select",
          label: "Aspect Ratio",
          options: [
            { label: "16:9", value: "16x9" },
            { label: "4:3", value: "4x3" },
            { label: "21:9", value: "21x9" },
            { label: "1:1", value: "1x1" }
          ]
        },
        autoplay: {
          type: "radio",
          label: "Autoplay",
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false }
          ]
        }
      },
      defaultProps: {
        url: "",
        aspectRatio: "16x9",
        autoplay: false
      },
      render: ({ url, aspectRatio, autoplay }) => {
        if (!url) {
          return React.createElement(
            "div",
            {
              style: {
                padding: "3rem",
                backgroundColor: "#f8f9fa",
                textAlign: "center",
                border: "2px dashed #dee2e6",
                borderRadius: `${BORDER_RADIUS_4_PX}px`,
                marginBottom: "1rem"
              }
            },
            React.createElement("i", {
              className: "fas fa-video fa-3x",
              style: { color: "#6c757d", marginBottom: "1rem" }
            }),
            React.createElement(
              "p",
              {
                className: "mb-0",
                style: { color: "#6c757d" }
              },
              "Enter a YouTube or Vimeo URL"
            )
          );
        }

        // Convert URL to embed URL
        let embedUrl = url;

        // YouTube
        if (url.includes("youtube.com") || url.includes("youtu.be")) {
          let videoId = "";
          if (url.includes("youtu.be/")) {
            videoId = url.split("youtu.be/")[1].split("?")[0];
          } else if (url.includes("watch?v=")) {
            videoId = url.split("watch?v=")[1].split("&")[0];
          }
          if (videoId) {
            embedUrl = `https://www.youtube.com/embed/${videoId}${
              autoplay ? "?autoplay=1" : ""
            }`;
          }
        }
        // Vimeo
        else if (url.includes("vimeo.com")) {
          const videoId = url.split("vimeo.com/")[1].split("?")[0];
          if (videoId) {
            embedUrl = `https://player.vimeo.com/video/${videoId}${
              autoplay ? "?autoplay=1" : ""
            }`;
          }
        }

        return React.createElement(
          "div",
          {
            className: `ratio ratio-${aspectRatio}`,
            style: { marginBottom: "1rem" }
          },
          React.createElement("iframe", {
            src: embedUrl,
            title: "Video player",
            frameBorder: "0",
            allow:
              "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
            allowFullScreen: true
          })
        );
      }
    },
    Icon: {
      label: "Icon",
      fields: {
        icon: {
          type: "text",
          label: "Font Awesome Icon Class",
          placeholder: "e.g., fas fa-heart"
        },
        size: {
          type: "select",
          label: "Size",
          options: [
            { label: "Small", value: "fa-sm" },
            { label: "Normal", value: "" },
            { label: "Large", value: "fa-lg" },
            { label: "2x", value: "fa-2x" },
            { label: "3x", value: "fa-3x" },
            { label: "4x", value: "fa-4x" },
            { label: "5x", value: "fa-5x" }
          ]
        },
        color: {
          type: "text",
          label: "Color (hex or name)",
          placeholder: "#0d6efd or blue"
        },
        align: {
          type: "select",
          label: "Alignment",
          options: [
            { label: "Left", value: "left" },
            { label: "Center", value: "center" },
            { label: "Right", value: "right" }
          ]
        }
      },
      defaultProps: {
        icon: "fas fa-star",
        size: "fa-3x",
        color: "#0d6efd",
        align: "center"
      },
      render: ({ icon, size, color, align }) => {
        const classes = [icon || "fas fa-star"];
        if (size) {
          classes.push(size);
        }

        return React.createElement(
          "div",
          {
            style: {
              textAlign: align,
              marginBottom: "1rem"
            }
          },
          React.createElement("i", {
            className: classes.join(" "),
            style: { color: color || "#0d6efd" }
          })
        );
      }
    },
    Alert: {
      label: "Alert",
      fields: {
        message: {
          type: "custom",
          label: "Message",
          render: RichTextField
        },
        type: {
          type: "select",
          label: "Type",
          options: [
            { label: "Primary", value: "primary" },
            { label: "Secondary", value: "secondary" },
            { label: "Success", value: "success" },
            { label: "Danger", value: "danger" },
            { label: "Warning", value: "warning" },
            { label: "Info", value: "info" },
            { label: "Light", value: "light" },
            { label: "Dark", value: "dark" }
          ]
        },
        dismissible: {
          type: "radio",
          label: "Dismissible",
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false }
          ]
        }
      },
      defaultProps: {
        message: "<p>This is an alert message!</p>",
        type: "info",
        dismissible: false
      },
      render: ({ message, type, dismissible }) => {
        const classes = ["alert", `alert-${type}`];
        if (dismissible) {
          classes.push("alert-dismissible fade show");
        }

        return React.createElement(
          "div",
          {
            className: classes.join(" "),
            role: "alert",
            style: { marginBottom: "1rem" }
          },
          React.createElement("div", {
            dangerouslySetInnerHTML: { __html: sanitizeUserHtml(message || "") }
          }),
          dismissible &&
            React.createElement("button", {
              type: "button",
              className: "btn-close",
              "data-bs-dismiss": "alert",
              "aria-label": "Close"
            })
        );
      }
    },
    ProgressBar: {
      label: "Progress Bar",
      fields: {
        value: {
          type: "number",
          label: "Progress (%)",
          min: 0,
          max: PROGRESS_BAR_MAX_VALUE
        },
        label: {
          type: "text",
          label: "Label (optional)"
        },
        variant: {
          type: "select",
          label: "Color",
          options: [
            { label: "Primary", value: "primary" },
            { label: "Secondary", value: "secondary" },
            { label: "Success", value: "success" },
            { label: "Danger", value: "danger" },
            { label: "Warning", value: "warning" },
            { label: "Info", value: "info" }
          ]
        },
        striped: {
          type: "radio",
          label: "Striped",
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false }
          ]
        },
        animated: {
          type: "radio",
          label: "Animated",
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false }
          ]
        },
        height: {
          type: "select",
          label: "Height",
          options: [
            { label: "Small", value: "10" },
            { label: "Medium", value: "20" },
            { label: "Large", value: "30" }
          ]
        }
      },
      defaultProps: {
        value: PROGRESS_BAR_DEFAULT_VALUE,
        label: "",
        variant: "primary",
        striped: false,
        animated: false,
        height: "20"
      },
      render: ({ value, label, variant, striped, animated, height }) => {
        const progressClasses = ["progress-bar", `bg-${variant}`];
        if (striped) {
          progressClasses.push("progress-bar-striped");
        }
        if (animated) {
          progressClasses.push("progress-bar-animated");
        }

        const progressValue = Math.min(
          PROGRESS_BAR_MAX_VALUE,
          Math.max(0, value || 0)
        );
        const heightValue =
          height && !isNaN(parseFloat(height)) ? height : "20";

        return React.createElement(
          "div",
          {
            style: { marginBottom: "1rem" }
          },
          label &&
            React.createElement(
              "div",
              {
                style: {
                  marginBottom: "0.5rem",
                  fontSize: "0.875rem",
                  color: "#6c757d"
                }
              },
              label
            ),
          React.createElement(
            "div",
            {
              className: "progress",
              style: { height: `${heightValue}px` }
            },
            React.createElement(
              "div",
              {
                className: progressClasses.join(" "),
                role: "progressbar",
                style: { width: `${progressValue}%` },
                "aria-valuenow": progressValue,
                "aria-valuemin": "0",
                "aria-valuemax": String(PROGRESS_BAR_MAX_VALUE)
              },
              `${progressValue}%`
            )
          )
        );
      }
    },
    Timeline: {
      label: "Timeline",
      fields: {
        events: {
          type: "array",
          label: "Events",
          arrayFields: {
            title: { type: "text", label: "Event Title" },
            date: { type: "text", label: "Date/Time" },
            description: {
              type: "custom",
              label: "Description",
              render: RichTextField
            }
          },
          defaultItemProps: {
            title: "Event Title",
            date: "2024",
            description: "<p>Event description</p>"
          }
        },
        orientation: {
          type: "select",
          label: "Orientation",
          options: [
            { label: "Vertical", value: "vertical" },
            { label: "Horizontal", value: "horizontal" }
          ]
        }
      },
      defaultProps: {
        events: [
          {
            title: "First Event",
            date: "January 2024",
            description: "<p>Description of the first event</p>"
          },
          {
            title: "Second Event",
            date: "March 2024",
            description: "<p>Description of the second event</p>"
          },
          {
            title: "Third Event",
            date: "June 2024",
            description: "<p>Description of the third event</p>"
          }
        ],
        orientation: "vertical"
      },
      render: ({ events, orientation }) => {
        if (orientation === "horizontal") {
          return React.createElement(
            "div",
            {
              style: {
                display: "flex",
                overflowX: "auto",
                padding: "2rem 0",
                marginBottom: "1rem",
                position: "relative"
              }
            },
            events.map(function (eventItem, idx) {
              return React.createElement(
                "div",
                {
                  key: idx,
                  style: {
                    minWidth: "250px",
                    paddingRight: idx < events.length - 1 ? "2rem" : "0",
                    position: "relative"
                  }
                },
                React.createElement("div", {
                  style: {
                    width: "12px",
                    height: "12px",
                    backgroundColor: "#0d6efd",
                    borderRadius: "50%",
                    margin: "0 auto 1rem"
                  }
                }),
                React.createElement(
                  "div",
                  {
                    className: "card",
                    style: { padding: "1rem" }
                  },
                  React.createElement(
                    "strong",
                    {
                      style: { display: "block", marginBottom: "0.5rem" }
                    },
                    eventItem.title
                  ),
                  React.createElement(
                    "small",
                    {
                      style: {
                        display: "block",
                        color: "#6c757d",
                        marginBottom: "0.5rem"
                      }
                    },
                    eventItem.date
                  ),
                  React.createElement("div", {
                    dangerouslySetInnerHTML: {
                      __html: sanitizeUserHtml(eventItem.description || "")
                    }
                  })
                )
              );
            })
          );
        }

        // Vertical timeline
        return React.createElement(
          "div",
          {
            style: {
              position: "relative",
              padding: "2rem 0",
              marginBottom: "1rem"
            }
          },
          React.createElement("div", {
            style: {
              position: "absolute",
              left: "20px",
              top: "0",
              bottom: "0",
              width: "2px",
              backgroundColor: "#dee2e6"
            }
          }),
          events.map(function (eventItem, idx) {
            return React.createElement(
              "div",
              {
                key: idx,
                style: {
                  position: "relative",
                  paddingLeft: "50px",
                  marginBottom: "2rem"
                }
              },
              React.createElement("div", {
                style: {
                  position: "absolute",
                  left: "14px",
                  top: "5px",
                  width: "14px",
                  height: "14px",
                  backgroundColor: "#0d6efd",
                  borderRadius: "50%",
                  border: "2px solid #fff"
                }
              }),
              React.createElement(
                "div",
                {
                  className: "card",
                  style: { padding: "1rem" }
                },
                React.createElement(
                  "strong",
                  {
                    style: { display: "block", marginBottom: "0.5rem" }
                  },
                  eventItem.title
                ),
                React.createElement(
                  "small",
                  {
                    style: {
                      display: "block",
                      color: "#6c757d",
                      marginBottom: "0.5rem"
                    }
                  },
                  eventItem.date
                ),
                React.createElement("div", {
                  dangerouslySetInnerHTML: {
                    __html: sanitizeUserHtml(eventItem.description || "")
                  }
                })
              )
            );
          })
        );
      }
    },
    FeaturedProjects: {
      label: "Featured Projects",
      fields: {
        title: {
          type: "text",
          label: "Section Title",
          defaultValue: "Surveys & Projects"
        },
        headingLevel: headingLevelField("Section Title Level"),
        count: {
          type: "number",
          label: "Number of Projects",
          defaultValue: DEFAULT_PROJECT_COUNT,
          min: COLUMN_COUNT_1,
          max: MAX_PROJECT_COUNT
        },
        showViewAllButton: {
          type: "radio",
          label: "Show 'View All' Button",
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false }
          ]
        },
        showProjectLogo: {
          type: "radio",
          label: "Show project logo if present",
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false }
          ]
        },
        projectListingColumns: {
          type: "select",
          label: "Number of Columns",
          options: [
            { label: "1 Column", value: COLUMN_COUNT_1 },
            { label: "2 Columns", value: COLUMN_COUNT_2 },
            { label: "3 Columns", value: COLUMN_COUNT_3 },
            { label: "4 Columns", value: COLUMN_COUNT_4 }
          ]
        },
        projectImageAspectRatio: {
          type: "select",
          label: "Image Aspect Ratio",
          options: [
            { label: "Unset (Default)", value: ASPECT_RATIO_UNSET },
            { label: "2:1", value: 0 },
            { label: "16:9", value: 1 },
            { label: "1:1 (Square)", value: 2 }
          ]
        },
        hubId: {
          type: "custom",
          label: "# Hub ID (auto-detected)",
          render: ({ field, value, onChange }) => {
            /**
             * Auto-detect hubId from window.modelData (read-only display only)
             */
            function getAutoHubId() {
              if (window.modelData && window.modelData.hubId) {
                const id = window.modelData.hubId;
                if (id && id > 0) {
                  return id;
                }
              }
              if (window.modelData && window.modelData.hub) {
                const hub = window.modelData.hub;
                if (
                  typeof hub.id !== "undefined" &&
                  hub.id !== null &&
                  hub.id > 0
                ) {
                  return hub.id;
                }
                if (
                  typeof hub.Id !== "undefined" &&
                  hub.Id !== null &&
                  hub.Id > 0
                ) {
                  return hub.Id;
                }
              }
              return value || 0;
            }

            const autoHubId = getAutoHubId();

            /**
             * Only update once on mount if we have a valid hubId and the value is 0 or unset
             * Use a ref to track if we've already attempted to update
             */
            const hasUpdatedRef = React.useRef(false);
            const initialValueRef = React.useRef(value);

            React.useEffect(function () {
              /**
               * Only update if:
               * 1. We haven't updated before
               * 2. We have a valid autoHubId
               * 3. The current value is 0/null/undefined (unset)
               * 4. The initial value was also unset (to prevent updates when value changes externally)
               */
              if (
                !hasUpdatedRef.current &&
                autoHubId > 0 &&
                (value === 0 ||
                  value === null ||
                  typeof value === "undefined") &&
                (initialValueRef.current === 0 ||
                  initialValueRef.current === null ||
                  typeof initialValueRef.current === "undefined")
              ) {
                onChange(autoHubId);
                hasUpdatedRef.current = true;
              }
            }, []); // Empty deps - only run once on mount

            return React.createElement(
              "div",
              { className: "puck-field" },
              React.createElement(
                "label",
                { className: "puck-field-label" },
                field.label
              ),
              React.createElement("input", {
                type: "text",
                value: autoHubId > 0 ? autoHubId : "Auto-detecting...",
                className: "form-control",
                readOnly: true,
                disabled: true,
                style: { backgroundColor: "#e9ecef", cursor: "not-allowed" }
              }),
              autoHubId === 0 &&
                React.createElement(
                  "small",
                  {
                    className: "form-text text-warning"
                  },
                  "Hub ID will be auto-detected when available"
                )
            );
          }
        }
      },
      defaultProps: {
        title: "Surveys & Projects",
        headingLevel: "h3",
        count: DEFAULT_PROJECT_COUNT,
        showViewAllButton: true,
        showProjectLogo: false,
        projectListingColumns: COLUMN_COUNT_1,
        projectImageAspectRatio: ASPECT_RATIO_UNSET
      },
      render: function ({
        title,
        headingLevel,
        count,
        showViewAllButton,
        showProjectLogo,
        projectListingColumns,
        projectImageAspectRatio,
        hubId,
        puck
      }) {
        const sectionLevel = headingLevel || "h3";
        // Project names sit under the section title; derive so the outline never skips.
        const projectLevel = subHeadingLevel(sectionLevel);
        // ProjectCard component for individual projects
        function ProjectCard({
          project,
          showProjectLogo: showLogo,
          columns,
          aspectRatio
        }) {
          // Only show icon column if showProjectLogo is true AND icon exists and should be shown
          const shouldShowIcon = showLogo && project.iconColShown;
          const colClass = shouldShowIcon
            ? `col-md-${BOOTSTRAP_COL_MD_10} col-sm-${BOOTSTRAP_COL_SM_9}`
            : `col-${BOOTSTRAP_COL_12}`;

          // Calculate Bootstrap column classes for grid layout
          const gridColClass =
            columns && columns > 1
              ? `col-sm-${Math.floor(
                  BOOTSTRAP_GRID_COLUMNS / columns
                )} col-xs-12`
              : "";

          // Calculate aspect ratio class for featured images
          let aspectRatioClass = "";
          if (
            typeof aspectRatio !== "undefined" &&
            aspectRatio !== null &&
            aspectRatio !== DEFAULT_ASPECT_RATIO &&
            project.featuredImageUrl
          ) {
            switch (aspectRatio) {
              case 0: // TwoByOne
                aspectRatioClass = "project-image-aspect-2-1";
                break;
              case 1: // SixteenByNine
                aspectRatioClass = "project-image-aspect-16-9";
                break;
              case ASPECT_RATIO_ONE_BY_ONE: // OneByOne
                aspectRatioClass = "project-image-aspect-1-1";
                break;
              default:
                aspectRatioClass = "";
                break;
            }
          }

          // Helper function to format text to paragraphs (simplified version)
          function formatTextToParagraphs(text) {
            if (!text) {
              return "";
            }
            // Split by double newlines or single newlines, then wrap in <p> tags
            const paragraphs = text.split(/\n\s*\n/).filter((p) => p.trim());
            return paragraphs
              .map((p) => `<p>${p.trim().replace(/\n/g, "<br>")}</p>`)
              .join("");
          }

          // Helper function to ellipsis text (simplified version)
          function ellipsis(text, maxLength) {
            if (!text) {
              return "";
            }
            if (text.length <= maxLength) {
              return text;
            }
            return text.substring(0, maxLength) + "...";
          }

          // Format description similar to CSHTML
          const formattedDescription = project.description
            ? formatTextToParagraphs(
                ellipsis(project.description, DEFAULT_DESCRIPTION_MAX_LENGTH)
              )
            : null;

          // Create the card element
          const cardElement = React.createElement(
            "a",
            {
              href: project.publicUrl,
              className: `d-block project card mb-3 mt-3 text-decoration-none rounded-4 overflow-hidden ${
                project.isFeatured ? "has-featured-img featured" : ""
              }`
            },
            project.featuredImageUrl &&
              React.createElement(
                "div",
                {
                  className: `featured-image m-b-md ${aspectRatioClass}`
                },
                React.createElement("img", {
                  src: project.featuredImageUrl,
                  alt: `Featured image for ${project.name}`
                })
              ),
            React.createElement(
              "div",
              { className: "row no-gutter" },
              shouldShowIcon &&
                React.createElement(
                  "div",
                  {
                    className:
                      "col-md-2 col-sm-3 headerContent text-center icon-column py-4"
                  },
                  project.iconExists &&
                    React.createElement("img", {
                      className: "thumb",
                      src: project.logoUrl,
                      alt: `Icon image for ${project.name}`
                    })
                ),
              React.createElement(
                "div",
                {
                  className: `${colClass} headerContent title-column py-4 px-5`
                },
                React.createElement(
                  projectLevel,
                  {
                    // `h3` utility keeps the original type scale after the tag change.
                    className: "projName searchable xs-text-center h3"
                  },
                  project.name
                ),
                formattedDescription
                  ? React.createElement("div", {
                      className: "projBlurb searchable",
                      dangerouslySetInnerHTML: {
                        __html: sanitizeUserHtml(formattedDescription || "")
                      }
                    })
                  : React.createElement(
                      "div",
                      {
                        className: "projBlurb project-description-empty"
                      },
                      `Click ${project.ctaText} to learn more.`
                    ),
                formattedDescription &&
                  React.createElement(
                    "div",
                    {
                      className: "searchable hidden-description",
                      style: { display: "none" }
                    },
                    project.description
                  ),
                project.categories &&
                  project.categories.length > 0 &&
                  React.createElement(
                    "div",
                    {
                      className:
                        "project-category-wrapper d-flex gap-2 flex-wrap"
                    },
                    React.createElement("input", {
                      type: "hidden",
                      name: "ProjectCategories",
                      className: "project-category",
                      id: "",
                      value: project.categories.map((c) => c.name).join(",")
                    }),
                    project.categories.map((category, idx) =>
                      React.createElement(
                        "span",
                        {
                          key: idx,
                          className: `label searchable project-category${
                            category.textCategoryId > 0 ? " taxonomic" : ""
                          }`,
                          "data-taxonomy-id": category.textCategoryId
                        },
                        category.name
                      )
                    )
                  ),
                React.createElement(
                  "div",
                  {
                    className: "row no-gutter mt-4"
                  },
                  React.createElement(
                    "div",
                    {
                      className: "action col-md-12 col-lg-6 col-sm-6 col-12"
                    },
                    project.participantCount >
                      MIN_PARTICIPANT_COUNT_FOR_DISPLAY &&
                      React.createElement(
                        "div",
                        {
                          className: "info"
                        },
                        React.createElement(
                          "span",
                          {
                            className: `body-small ${
                              project.participantCount > 0 ? "me-sm-2" : ""
                            }`,
                            "data-bs-toggle": "tooltip",
                            title: `${project.participantCount.toLocaleString()} Participants`
                          },
                          React.createElement("i", {
                            className: "fa-light fa-user"
                          }),
                          ` ${project.participantCount.toLocaleString()} `
                        )
                      ),
                    project.commentCount > 0 &&
                      React.createElement(
                        "div",
                        {
                          className: "info"
                        },
                        React.createElement(
                          "span",
                          {
                            className: `body-small ${
                              project.commentCount > 0 ? "me-sm-2" : " hide"
                            }`,
                            "data-bs-toggle": "tooltip",
                            title: `${project.commentCount.toLocaleString()} Comments`
                          },
                          React.createElement("i", {
                            className: "fa-light fa-comment"
                          }),
                          ` ${project.commentCount.toLocaleString()} `
                        )
                      )
                  ),
                  React.createElement(
                    "div",
                    {
                      className:
                        "action col-md-12 text-end col-lg-6 col-sm-6 col-12"
                    },
                    React.createElement(
                      "div",
                      { className: "xs-full-btn me-0 test-6" },
                      React.createElement(
                        "button",
                        {
                          className: "btn btn-primary pi-btn-primary",
                          "aria-label": `${project.ctaText} for ${project.name}`
                        },
                        project.ctaText
                      )
                    )
                  )
                )
              )
            )
          );

          // Wrap in column div if in grid layout, otherwise return card directly
          if (gridColClass) {
            return React.createElement(
              "div",
              {
                className: gridColClass
              },
              cardElement
            );
          }

          return cardElement;
        }

        /**
         * FeaturedProjects component
         */
        function FeaturedProjectsComponent() {
          // Initialize global cache for shared data
          if (!window._featuredProjectsCache) {
            window._featuredProjectsCache = {};
          }
          if (!window._featuredProjectsFetching) {
            window._featuredProjectsFetching = new Set();
          }
          if (!window._featuredProjectsListeners) {
            window._featuredProjectsListeners = {};
          }

          /**
           * Get hubId from multiple sources with fallbacks
           * Priority: window.modelData > Puck root props > component props
           */
          function getHubId() {
            // 1. Check window.modelData.hubId first (most reliable source)
            if (window.modelData && window.modelData.hubId) {
              const id = window.modelData.hubId;
              if (id && id > 0) {
                return id;
              }
            }

            // 2. Check window.modelData.hub.id (case-sensitive)
            if (window.modelData && window.modelData.hub) {
              const hub = window.modelData.hub;
              if (
                typeof hub.id !== "undefined" &&
                hub.id !== null &&
                hub.id > 0
              ) {
                return hub.id;
              }
              if (
                typeof hub.Id !== "undefined" &&
                hub.Id !== null &&
                hub.Id > 0
              ) {
                return hub.Id;
              }
            }

            // 3. Check root props (from puck root.hubId field) - Puck provides rootProps via puck prop
            if (puck && puck.rootProps) {
              const rootHubId = puck.rootProps.hubId;
              if (rootHubId && rootHubId > 0) {
                return rootHubId;
              }
            }

            // 4. Check currentData (fallback for when puck prop isn't available)
            if (window.getPuckEditorData) {
              const data = window.getPuckEditorData();
              if (
                data &&
                data.root &&
                data.root.props &&
                data.root.props.hubId
              ) {
                const rootHubId = data.root.props.hubId;
                if (rootHubId && rootHubId > 0) {
                  return rootHubId;
                }
              }
            }

            // 5. Last resort: Check component props (but ignore if 0, as it's likely unset)
            if (hubId && hubId > 0) {
              return hubId;
            }

            // If we still don't have a valid hubId, return 0 (will trigger retry logic)
            return 0;
          }

          const hubIdToUse = getHubId();
          const cacheKey =
            hubIdToUse > 0
              ? `${hubIdToUse}_${count || DEFAULT_FEATURED_PROJECTS_COUNT}`
              : null;

          // Check cache first
          const cachedData =
            cacheKey && window._featuredProjectsCache[cacheKey];
          const [projects, setProjects] = React.useState(
            cachedData?.projects || []
          );
          const [loading, setLoading] = React.useState(
            !cachedData && hubIdToUse > 0
          );
          const [error, setError] = React.useState(cachedData?.error || null);

          const timeoutRef = React.useRef(null);
          const retryCountRef = React.useRef(0);

          React.useEffect(() => {
            // Re-check cache in case it was updated
            const currentCachedData =
              cacheKey && window._featuredProjectsCache[cacheKey];

            // If we have cached data, use it
            if (currentCachedData) {
              setProjects(currentCachedData.projects || []);
              setLoading(false);
              setError(currentCachedData.error || null);
              return;
            }

            // If no hubId, don't fetch
            if (!hubIdToUse || hubIdToUse <= 0 || !cacheKey) {
              setLoading(false);
              setError(
                "Hub ID not found. Please ensure you are viewing this content on a hub page."
              );
              return;
            }

            // Check if fetch is already in progress
            if (window._featuredProjectsFetching.has(cacheKey)) {
              // Wait for the fetch to complete by listening to cache updates
              if (!window._featuredProjectsListeners[cacheKey]) {
                window._featuredProjectsListeners[cacheKey] = [];
              }

              // eslint-disable-next-line func-style
              const listener = () => {
                const cached = window._featuredProjectsCache[cacheKey];
                if (cached) {
                  setProjects(cached.projects || []);
                  setLoading(false);
                  setError(cached.error || null);
                }
              };

              window._featuredProjectsListeners[cacheKey].push(listener);

              return () => {
                const listeners = window._featuredProjectsListeners[cacheKey];
                if (listeners) {
                  const index = listeners.indexOf(listener);
                  if (index > -1) {
                    listeners.splice(index, 1);
                  }
                }
              };
            }

            // Mark as fetching
            window._featuredProjectsFetching.add(cacheKey);
            setLoading(true);

            const maxRetries = 5;
            const retryDelay = 200;

            function tryFetch() {
              const currentHubId = getHubId();

              if (!currentHubId || currentHubId <= 0) {
                if (retryCountRef.current < maxRetries) {
                  retryCountRef.current++;
                  timeoutRef.current = setTimeout(tryFetch, retryDelay);
                  return;
                }
                const errorMsg =
                  "Hub ID not found. Please ensure you are viewing this content on a hub page.";
                setError(errorMsg);
                setLoading(false);
                window._featuredProjectsFetching.delete(cacheKey);

                // Cache the error
                if (cacheKey) {
                  window._featuredProjectsCache[cacheKey] = {
                    projects: [],
                    error: errorMsg
                  };
                  // Notify listeners
                  if (window._featuredProjectsListeners[cacheKey]) {
                    window._featuredProjectsListeners[cacheKey].forEach(
                      (listener) => listener()
                    );
                  }
                }
                return;
              }

              fetch(
                `/HubContentTab/GetFeaturedProjects?hubId=${currentHubId}&count=${
                  count || DEFAULT_FEATURED_PROJECTS_COUNT
                }`
              )
                .then((response) => {
                  if (!response.ok) {
                    throw new Error("Failed to fetch projects");
                  }
                  return response.json();
                })
                .then((data) => {
                  if (data.error) {
                    throw new Error(data.error);
                  }
                  const projectsData = data.projects || [];

                  // Cache the result
                  if (cacheKey) {
                    window._featuredProjectsCache[cacheKey] = {
                      projects: projectsData,
                      error: null
                    };
                  }

                  // Update state
                  setProjects(projectsData);
                  setLoading(false);
                  setError(null);

                  // Remove from fetching set
                  window._featuredProjectsFetching.delete(cacheKey);

                  // Notify all listeners
                  if (cacheKey && window._featuredProjectsListeners[cacheKey]) {
                    window._featuredProjectsListeners[cacheKey].forEach(
                      (listener) => listener()
                    );
                  }
                })
                .catch((err) => {
                  const errorMsg = err.message;

                  // Cache the error
                  if (cacheKey) {
                    window._featuredProjectsCache[cacheKey] = {
                      projects: [],
                      error: errorMsg
                    };
                  }

                  setError(errorMsg);
                  setLoading(false);
                  window._featuredProjectsFetching.delete(cacheKey);

                  // Notify listeners
                  if (cacheKey && window._featuredProjectsListeners[cacheKey]) {
                    window._featuredProjectsListeners[cacheKey].forEach(
                      (listener) => listener()
                    );
                  }
                });
            }

            tryFetch();

            // Cleanup
            return () => {
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
              }
            };
          }, [cacheKey, count]); // Depend on cacheKey and count

          /**
           * Do not emit synthetic window resize events here; the admin shell listens
           * to them and it caused the modal height to oscillate when data refreshed.
           */
          if (loading) {
            // Add min-height to prevent flash - approximate height of 3 project cards
            return React.createElement(
              "div",
              {
                className: "text-center p-5",
                style: { minHeight: "600px" }
              },
              React.createElement("i", { className: "fas fa-spinner fa-spin" }),
              " Loading projects..."
            );
          }

          if (error) {
            return React.createElement(
              "div",
              { className: "alert alert-danger" },
              `Error loading projects: ${error}`
            );
          }

          if (projects.length === 0) {
            return React.createElement(
              "div",
              { className: "p-4" },
              React.createElement(
                "p",
                { className: "fs-6" },
                "There are no upcoming surveys or projects at this time."
              )
            );
          }

          // Use component props, fallback to hub settings from window.modelData, then defaults
          const columns =
            typeof projectListingColumns !== "undefined" &&
            projectListingColumns !== null
              ? projectListingColumns
              : window.modelData &&
                window.modelData.hub &&
                window.modelData.hub.projectListingColumns
              ? window.modelData.hub.projectListingColumns
              : 1;
          const aspectRatio =
            typeof projectImageAspectRatio !== "undefined" &&
            projectImageAspectRatio !== null
              ? projectImageAspectRatio
              : window.modelData &&
                window.modelData.hub &&
                typeof window.modelData.hub.projectImageAspectRatio !==
                  "undefined"
              ? window.modelData.hub.projectImageAspectRatio
              : DEFAULT_ASPECT_RATIO;

          return React.createElement(
            "section",
            {
              id: "survey-tab-featured-projects-new",
              className: "featured-projects-container"
            },
            // Was a <p>: a visually-styled section title with no heading
            // semantics, which also left the project names below it orphaned.
            title &&
              React.createElement(
                sectionLevel,
                {
                  className: "featured-projects page-title-h3"
                },
                React.createElement("span", {}, title)
              ),
            React.createElement(
              "div",
              {
                className: columns > 1 ? "row g-3" : ""
              },
              projects.map((project, idx) =>
                React.createElement(ProjectCard, {
                  key: project.id || idx,
                  project: project,
                  showProjectLogo: showProjectLogo,
                  columns: columns,
                  aspectRatio: aspectRatio
                })
              )
            ),
            showViewAllButton &&
              React.createElement(
                "div",
                {
                  className: "d-flex justify-content-end mt-3",
                  style: puck ? { marginTop: "1rem" } : {}
                },
                React.createElement(
                  "button",
                  {
                    className:
                      "btn btn-primary pi-btn-primary btn-s-md btn-transparent btn-borderless pull-right switchTabs",
                    "data-rel": "surveys"
                  },
                  "View all surveys & projects ",
                  React.createElement("span", {
                    className: "fas fa-chevron-right"
                  })
                )
              ),
            React.createElement("div", { className: "clearfix" })
          );
        }

        return React.createElement(FeaturedProjectsComponent);
      }
    },
    CommunityQA: {
      label: "Community Q&A",
      fields: {
        qaInstanceId: {
          type: "custom",
          label: "Q&A instance",
          render: ({ field, value, onChange }) => {
            // The customer's Community Q&A instances (max one per project), fetched from
            // the admin endpoint. Instances are CREATED by adding a "Community Q&A"
            // question to a project in its Page & Survey builder — never from here.
            function getCustomerId() {
              if (window.modelData) {
                if (window.modelData.customerId > 0)
                  return window.modelData.customerId;
                if (window.modelData.custId > 0) return window.modelData.custId;
                if (window.modelData.hub) {
                  const hub = window.modelData.hub;
                  if (hub.customerId > 0) return hub.customerId;
                  if (hub.CustomerId > 0) return hub.CustomerId;
                }
              }
              if (window.cz && window.cz.custId > 0) return window.cz.custId;
              return 0;
            }

            const [instances, setInstances] = React.useState(null);
            const [loadFailed, setLoadFailed] = React.useState(false);
            const customerId = getCustomerId();

            React.useEffect(
              function () {
                if (!customerId) {
                  setInstances([]);
                  return;
                }
                // Auth and HTTP go through the shared client so this field gets the
                // proactive refresh and the 401-retry-after-refresh path (ARCH-001).
                // X-Tenant-ID is overridden because the hub builder resolves the customer
                // from modelData, which can differ from the ambient tenant the client sends.
                coreApiClient
                  .get("/api/v1/qa/instances?customerId=" + customerId, {
                    headers: { "X-Tenant-ID": String(customerId) }
                  })
                  .then(function (payload) {
                    // Core API wraps the payload in an ApiResponse envelope.
                    var data = (payload && payload.data) || payload || {};
                    setInstances(data.instances || []);
                  })
                  .catch(function () {
                    setInstances([]);
                    setLoadFailed(true);
                  });
              },
              [customerId]
            );

            const selected =
              instances &&
              instances.find(function (inst) {
                return inst.pollId === value;
              });

            return React.createElement(
              "div",
              { className: "puck-field" },
              React.createElement(
                "label",
                {
                  className: "puck-field-label",
                  htmlFor: "pi-qa-instance-select"
                },
                field.label
              ),
              React.createElement(
                "select",
                {
                  id: "pi-qa-instance-select",
                  className: "form-control",
                  value: value || 0,
                  onChange: function (e) {
                    onChange(parseInt(e.target.value, 10) || 0);
                  }
                },
                React.createElement(
                  "option",
                  { value: 0 },
                  instances === null ? "Loading…" : "Choose a Q&A…"
                ),
                (instances || []).map(function (inst) {
                  return React.createElement(
                    "option",
                    { key: inst.pollId, value: inst.pollId },
                    inst.projectName + " — Community Q&A"
                  );
                })
              ),
              selected &&
                React.createElement(
                  "small",
                  { className: "form-text" },
                  React.createElement(
                    "a",
                    {
                      href:
                        "/Customer/Index/" +
                        customerId +
                        "/" +
                        selected.projectId +
                        "?activetab=qa",
                      target: "_blank",
                      rel: "noopener noreferrer"
                    },
                    "Manage questions, categories & responders"
                  )
                ),
              instances !== null &&
                instances.length === 0 &&
                React.createElement(
                  "small",
                  { className: "form-text text-warning" },
                  loadFailed
                    ? "Couldn't load Q&A instances. Refresh and try again."
                    : 'No Community Q&A exists yet. Add a "Community Q&A" question to a project in its Page & Survey builder, then pick it here.'
                )
            );
          }
        },
        heading: {
          type: "text",
          label: "Heading",
          defaultValue: "Community questions"
        },
        intro: {
          type: "textarea",
          label: "Intro / description"
        },
        showSearch: {
          type: "radio",
          label: "Show search",
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false }
          ]
        },
        showStatusFilter: {
          type: "radio",
          label: "Show status filter",
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false }
          ]
        },
        showCategoryFilter: {
          type: "radio",
          label: "Show category filter",
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false }
          ]
        },
        defaultSort: {
          type: "select",
          label: "Default sort",
          options: [
            { label: "Most upvoted", value: "top" },
            { label: "Newest", value: "new" }
          ]
        },
        allowSubmission: {
          type: "radio",
          label: "Allow question submission",
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false }
          ]
        }
      },
      defaultProps: {
        qaInstanceId: 0,
        heading: "Community questions",
        intro: "",
        showSearch: true,
        showStatusFilter: true,
        showCategoryFilter: true,
        defaultSort: "top",
        allowSubmission: true
      },
      render: function ({
        qaInstanceId,
        heading,
        intro,
        showSearch,
        showStatusFilter,
        showCategoryFilter,
        defaultSort,
        allowSubmission,
        puck
      }) {
        const editing =
          !!(puck && puck.isEditing) || window.__puckEditing === true;
        const wrapperRef = React.useRef(null);
        // JSON.stringify keeps free-text props ('|' etc.) from producing ambiguous keys.
        const composite = JSON.stringify([
          qaInstanceId,
          heading,
          intro,
          showSearch,
          showStatusFilter,
          showCategoryFilter,
          defaultSort,
          allowSubmission
        ]);

        React.useEffect(
          function () {
            if (!editing && qaInstanceId > 0 && wrapperRef.current) {
              piQaInit(wrapperRef.current);
            }
          },
          [composite, editing]
        );

        if (!qaInstanceId || qaInstanceId < 1) {
          return React.createElement(
            "div",
            { className: "pi-qa__placeholder" },
            React.createElement("i", {
              className: "fas fa-comments pi-qa__placeholder-icon",
              "aria-hidden": "true"
            }),
            React.createElement(
              "p",
              null,
              "Choose a Q&A instance in the settings panel."
            )
          );
        }

        if (editing) {
          // Static, fetch-free preview for the editor canvas.
          return React.createElement(
            "div",
            { className: "pi-qa pi-qa--preview" },
            React.createElement(
              "div",
              { className: "pi-qa__card" },
              React.createElement(
                "div",
                { className: "pi-qa__head" },
                React.createElement(
                  "h2",
                  { className: "pi-qa__heading" },
                  heading || "Community questions"
                ),
                intro
                  ? React.createElement(
                      "p",
                      { className: "pi-qa__intro" },
                      intro
                    )
                  : null
              ),
              React.createElement(
                "div",
                { className: "pi-qa__panel pi-qa__panel--show" },
                React.createElement(
                  "div",
                  { className: "pi-qa__controls" },
                  showSearch !== false &&
                    React.createElement(
                      "span",
                      { className: "pi-qa__pill pi-qa__pill--open" },
                      "Search questions…"
                    ),
                  showStatusFilter !== false &&
                    React.createElement(
                      "span",
                      { className: "pi-qa__pill pi-qa__pill--open" },
                      "Status ▾"
                    ),
                  showCategoryFilter !== false &&
                    React.createElement(
                      "span",
                      { className: "pi-qa__pill pi-qa__pill--open" },
                      "Category ▾"
                    ),
                  React.createElement(
                    "span",
                    { className: "pi-qa__pill pi-qa__pill--open" },
                    defaultSort === "new" ? "Newest ▾" : "Most upvoted ▾"
                  ),
                  allowSubmission !== false &&
                    React.createElement(
                      "span",
                      { className: "pi-qa__pill pi-qa__pill--answered" },
                      "+ Ask a question"
                    )
                ),
                [
                  "Example resident question shown here",
                  "Another community question",
                  "Answered questions show the official answer"
                ].map(function (text, index) {
                  return React.createElement(
                    "div",
                    { key: index, className: "pi-qa__row" },
                    React.createElement(
                      "div",
                      { className: "pi-qa__row-main" },
                      React.createElement(
                        "span",
                        { className: "pi-qa__pill pi-qa__pill--open" },
                        12 - index * 4 + " votes"
                      ),
                      React.createElement(
                        "p",
                        { className: "pi-qa__qtext" },
                        text
                      )
                    )
                  );
                })
              )
            )
          );
        }

        return React.createElement("div", {
          key: composite,
          ref: wrapperRef,
          className: "community-qa-outer",
          "data-pi-qa": "",
          "data-qa-poll": qaInstanceId,
          "data-qa-heading": heading || "",
          "data-qa-intro": intro || "",
          "data-qa-search": showSearch === false ? "0" : "1",
          "data-qa-status": showStatusFilter === false ? "0" : "1",
          "data-qa-cat": showCategoryFilter === false ? "0" : "1",
          "data-qa-sort": defaultSort === "new" ? "new" : "top",
          "data-qa-submit": allowSubmission === false ? "0" : "1"
        });
      }
    },
    UpcomingEvents: {
      label: "Upcoming Meetings",
      fields: {
        title: {
          type: "text",
          label: "Section Title",
          defaultValue: "Upcoming Events"
        },
        headingLevel: headingLevelField("Section Title Level"),
        count: {
          type: "number",
          label: "Number of Events",
          defaultValue: DEFAULT_EVENTS_COUNT,
          min: 1,
          max: MAX_EVENTS_COUNT
        },
        showViewAllButton: {
          type: "radio",
          label: "Show 'View All' Button",
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false }
          ]
        },
        layout: {
          type: "select",
          label: "Layout",
          options: [
            { label: "Card grid", value: "card" },
            { label: "List (date chip)", value: "list" }
          ]
        },
        hubId: {
          type: "custom",
          label: "# Hub ID (auto-detected)",
          render: ({ field, value, onChange }) => {
            // Auto-detect hubId from window.modelData (read-only display only)
            function getAutoHubId() {
              if (window.modelData && window.modelData.hubId) {
                const id = window.modelData.hubId;
                if (id && id > 0) {
                  return id;
                }
              }
              if (window.modelData && window.modelData.hub) {
                const hub = window.modelData.hub;
                if (
                  typeof hub.id !== "undefined" &&
                  hub.id !== null &&
                  hub.id > 0
                ) {
                  return hub.id;
                }
                if (
                  typeof hub.Id !== "undefined" &&
                  hub.Id !== null &&
                  hub.Id > 0
                ) {
                  return hub.Id;
                }
              }
              return value || 0;
            }

            const autoHubId = getAutoHubId();

            // Only update once on mount if we have a valid hubId and the value is 0 or unset
            const hasUpdatedRef = React.useRef(false);
            const initialValueRef = React.useRef(value);

            React.useEffect(function () {
              if (
                !hasUpdatedRef.current &&
                autoHubId > 0 &&
                (value === 0 ||
                  value === null ||
                  typeof value === "undefined") &&
                (initialValueRef.current === 0 ||
                  initialValueRef.current === null ||
                  typeof initialValueRef.current === "undefined")
              ) {
                onChange(autoHubId);
                hasUpdatedRef.current = true;
              }
            }, []);

            return React.createElement(
              "div",
              { className: "puck-field" },
              React.createElement(
                "label",
                { className: "puck-field-label" },
                field.label
              ),
              React.createElement("input", {
                type: "text",
                value: autoHubId > 0 ? autoHubId : "Auto-detecting...",
                className: "form-control",
                readOnly: true,
                disabled: true,
                style: { backgroundColor: "#e9ecef", cursor: "not-allowed" }
              }),
              autoHubId === 0 &&
                React.createElement(
                  "small",
                  {
                    className: "form-text text-warning"
                  },
                  "Hub ID will be auto-detected when available"
                )
            );
          }
        }
      },
      defaultProps: {
        title: "Upcoming Events",
        headingLevel: "h3",
        count: DEFAULT_EVENTS_COUNT,
        showViewAllButton: true,
        layout: "card"
      },
      render: function ({
        title,
        headingLevel,
        count,
        showViewAllButton,
        hubId,
        layout,
        puck
      }) {
        const sectionLevel = headingLevel || "h3";
        // Card titles sit under the section title; derive so the outline never skips.
        const cardLevel = subHeadingLevel(sectionLevel);
        // EventCard component for individual events
        function EventCard({ event: eventData, layout }) {
          // Format date for display
          function formatEventDate(eventStartUtc, timeZoneId) {
            if (!eventStartUtc) {
              return "";
            }
            try {
              const date = new Date(eventStartUtc);
              const options = {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
                timeZone: timeZoneId || "UTC"
              };
              return date.toLocaleDateString("en-US", options);
            } catch (e) {
              return "";
            }
          }

          // Format description (strip HTML, decode entities, truncate).
          // The description arrives as HTML; rendering it as a React text
          // node requires decoded text or entities like &nbsp; show literally.
          function formatDescription(description) {
            if (!description) {
              return "";
            }
            const text = htmlToPlainText(description);
            // Truncate to 150 characters
            return text.length > DESCRIPTION_TRUNCATE_LENGTH
              ? text.substring(0, DESCRIPTION_TRUNCATE_LENGTH) + "..."
              : text;
          }

          const eventDate = formatEventDate(
            eventData.eventStartUtc,
            eventData.timeZoneId
          );
          const eventDescription = formatDescription(eventData.description);

          if (layout === "list") {
            var _d = eventData.eventStartUtc
              ? new Date(eventData.eventStartUtc)
              : null;
            function _fmt(dt, opts) {
              if (!dt) return "";
              try {
                return dt.toLocaleString(
                  "en-US",
                  Object.assign({}, opts, {
                    timeZone: eventData.timeZoneId || "UTC"
                  })
                );
              } catch (e) {
                try {
                  return dt.toLocaleString("en-US", opts);
                } catch (e2) {
                  return "";
                }
              }
            }
            var _mon = _fmt(_d, { month: "short" }).toUpperCase();
            var _day = _fmt(_d, { day: "numeric" });
            var _when = _fmt(_d, {
              weekday: "short",
              month: "short",
              day: "numeric",
              year: "numeric",
              hour: "numeric",
              minute: "2-digit"
            });
            var _name = eventData.name || "Event";
            var _rawUrl =
              typeof eventData.publicUrl === "string"
                ? eventData.publicUrl.trim()
                : "";
            // Allow only absolute http(s) or a genuine same-origin path. Reject
            // protocol-relative ("//evil.com") and backslash forms ("/\\evil",
            // which browsers normalize to "//") so a DB-sourced publicUrl can't
            // become a same-tab open-redirect / phishing vector.
            var _safeUrl =
              /^https?:\/\//i.test(_rawUrl) || /^\/(?![\/\\])/.test(_rawUrl)
                ? _rawUrl
                : "#";
            return React.createElement(
              "div",
              { className: "card event-grid event-list shadow-none" },
              React.createElement(
                "div",
                { className: "event-date-chip", "aria-hidden": "true" },
                React.createElement(
                  "span",
                  { className: "event-date-mon" },
                  _mon
                ),
                React.createElement(
                  "span",
                  { className: "event-date-day" },
                  _day
                )
              ),
              React.createElement(
                "div",
                { className: "card-body" },
                React.createElement("h4", { className: "card-title" }, _name),
                _when &&
                  React.createElement(
                    "div",
                    { className: "event-when" },
                    _when
                  ),
                eventDescription &&
                  React.createElement(
                    "p",
                    { className: "card-text card-event-description" },
                    eventDescription
                  ),
                React.createElement(
                  "div",
                  { className: "button-container" },
                  React.createElement(
                    "a",
                    {
                      // same-tab internal navigation (matches the card branch);
                      // no target=_blank, so no rel needed and Referer is preserved.
                      href: _safeUrl,
                      className:
                        "btn btn-primary pi-btn-primary btn-sm view-events-grid",
                      "aria-label": "View Details for " + _name
                    },
                    "View Details ",
                    React.createElement("span", {
                      className: "fas fa-arrow-right",
                      "aria-hidden": "true"
                    })
                  )
                )
              )
            );
          }

          return React.createElement(
            "div",
            {
              className: "card event-grid hvr-grow shadow-none",
              "data-bs-url": eventData.publicUrl,
              style: { cursor: "pointer" },
              onClick: function () {
                if (eventData.publicUrl) {
                  window.location.href = eventData.publicUrl;
                }
              }
            },
            eventData.projectFeaturedImageUrl &&
              React.createElement("div", {
                className: "event-card-img-top",
                style: {
                  backgroundImage: `url(${eventData.projectFeaturedImageUrl})`,
                  height: "200px",
                  backgroundSize: "cover",
                  backgroundPosition: "center"
                },
                role: "img",
                "aria-label": `Featured background image for ${eventData.name}`
              }),
            React.createElement(
              "div",
              { className: "card-body" },
              React.createElement(
                cardLevel,
                // `h5` utility keeps the original type scale after the tag change.
                { className: "card-title h5" },
                eventData.name
              ),
              // A date is not a section heading; keep the h6 type scale but drop
              // the heading semantics so it stays out of the document outline.
              eventDate &&
                React.createElement(
                  "div",
                  { className: "card-subtitle mb-2 h6" },
                  eventDate
                ),
              eventDescription &&
                React.createElement(
                  "p",
                  {
                    className: "card-text card-event-description mb-5"
                  },
                  eventDescription
                ),
              React.createElement(
                "div",
                { className: "button-container" },
                React.createElement(
                  "a",
                  {
                    href: eventData.publicUrl,
                    className:
                      "btn btn-primary pi-btn-primary btn-sm pi-btn-sm view-events-grid",
                    "aria-label": `View Details for ${eventData.name}`
                  },
                  "View Details ",
                  React.createElement("span", {
                    className: "fas fa-arrow-right",
                    "aria-hidden": "true"
                  })
                )
              )
            )
          );
        }

        // UpcomingEvents component
        function UpcomingEventsComponent() {
          // Initialize global cache for shared data
          if (!window._upcomingEventsCache) {
            window._upcomingEventsCache = {};
          }
          if (!window._upcomingEventsFetching) {
            window._upcomingEventsFetching = new Set();
          }
          if (!window._upcomingEventsListeners) {
            window._upcomingEventsListeners = {};
          }

          // Get hubId from multiple sources with fallbacks
          function getHubId() {
            if (window.modelData && window.modelData.hubId) {
              const id = window.modelData.hubId;
              if (id && id > 0) {
                return id;
              }
            }
            if (window.modelData && window.modelData.hub) {
              const hub = window.modelData.hub;
              if (
                typeof hub.id !== "undefined" &&
                hub.id !== null &&
                hub.id > 0
              ) {
                return hub.id;
              }
              if (
                typeof hub.Id !== "undefined" &&
                hub.Id !== null &&
                hub.Id > 0
              ) {
                return hub.Id;
              }
            }
            if (puck && puck.rootProps) {
              const rootHubId = puck.rootProps.hubId;
              if (rootHubId && rootHubId > 0) {
                return rootHubId;
              }
            }
            if (window.getPuckEditorData) {
              try {
                const editorData = window.getPuckEditorData();
                if (
                  editorData &&
                  editorData.root &&
                  editorData.root.props &&
                  editorData.root.props.hubId
                ) {
                  const rootHubId = editorData.root.props.hubId;
                  if (rootHubId && rootHubId > 0) {
                    return rootHubId;
                  }
                }
              } catch (e) {
                // Error getting Puck editor data
              }
            }
            return hubId || 0;
          }

          const [currentHubId, setCurrentHubId] = React.useState(function () {
            // Get initial hubId
            if (window.modelData && window.modelData.hubId) {
              const id = window.modelData.hubId;
              if (id && id > 0) {
                return id;
              }
            }
            if (window.modelData && window.modelData.hub) {
              const hub = window.modelData.hub;
              if (
                typeof hub.id !== "undefined" &&
                hub.id !== null &&
                hub.id > 0
              ) {
                return hub.id;
              }
              if (
                typeof hub.Id !== "undefined" &&
                hub.Id !== null &&
                hub.Id > 0
              ) {
                return hub.Id;
              }
            }
            if (puck && puck.rootProps) {
              const rootHubId = puck.rootProps.hubId;
              if (rootHubId && rootHubId > 0) {
                return rootHubId;
              }
            }
            if (window.getPuckEditorData) {
              try {
                const editorData = window.getPuckEditorData();
                if (
                  editorData &&
                  editorData.root &&
                  editorData.root.props &&
                  editorData.root.props.hubId
                ) {
                  const rootHubId = editorData.root.props.hubId;
                  if (rootHubId && rootHubId > 0) {
                    return rootHubId;
                  }
                }
              } catch (e) {
                // Ignore errors
              }
            }
            return hubId || 0;
          });

          const cacheKey =
            currentHubId > 0
              ? `events_${currentHubId}_${count || DEFAULT_EVENTS_COUNT}`
              : null;

          const [events, setEvents] = React.useState([]);
          const [loading, setLoading] = React.useState(true);
          const [error, setError] = React.useState(null);
          const timeoutRef = React.useRef(null);

          // Update hubId when it becomes available
          React.useEffect(
            function () {
              function checkHubId() {
                const newHubId = getHubId();
                if (newHubId > 0 && newHubId !== currentHubId) {
                  setCurrentHubId(newHubId);
                }
              }

              // Check immediately
              checkHubId();

              // Listen for data changes
              function handleDataChange() {
                checkHubId();
              }

              document.addEventListener("puck:dataChange", handleDataChange);

              // Also check periodically in case window.modelData loads asynchronously
              const intervalId = setInterval(
                checkHubId,
                HUB_ID_CHECK_INTERVAL_MS
              );

              return function () {
                document.removeEventListener(
                  "puck:dataChange",
                  handleDataChange
                );
                clearInterval(intervalId);
              };
            },
            [currentHubId]
          );

          React.useEffect(
            function () {
              if (currentHubId <= 0) {
                // Don't set error immediately - wait a bit for hubId to become available
                const timeoutId = setTimeout(function () {
                  if (currentHubId <= 0) {
                    setLoading(false);
                    setError(
                      "Hub ID not available. Please ensure you are viewing this content on a hub page."
                    );
                  }
                }, HUB_ID_TIMEOUT_MS);
                return function () {
                  clearTimeout(timeoutId);
                };
              }

              // Check cache first
              const cachedData =
                cacheKey && window._upcomingEventsCache[cacheKey];
              if (cachedData) {
                setEvents(cachedData.events || []);
                setLoading(false);
                setError(cachedData.error || null);

                // Notify listeners
                if (window._upcomingEventsListeners[cacheKey]) {
                  window._upcomingEventsListeners[cacheKey].forEach(function (
                    listener
                  ) {
                    listener();
                  });
                }
                return;
              }

              // Check if already fetching
              if (window._upcomingEventsFetching.has(cacheKey)) {
                // Wait for fetch to complete
                if (!window._upcomingEventsListeners[cacheKey]) {
                  window._upcomingEventsListeners[cacheKey] = [];
                }

                // eslint-disable-next-line func-style
                const listener = () => {
                  const cached = window._upcomingEventsCache[cacheKey];
                  if (cached) {
                    setEvents(cached.events || []);
                    setLoading(false);
                    setError(cached.error || null);
                  }
                };

                window._upcomingEventsListeners[cacheKey].push(listener);

                // Set timeout to prevent infinite waiting
                timeoutRef.current = setTimeout(function () {
                  const listeners = window._upcomingEventsListeners[cacheKey];
                  if (listeners) {
                    const index = listeners.indexOf(listener);
                    if (index > -1) {
                      listeners.splice(index, 1);
                    }
                  }
                  setLoading(false);
                  setError("Timeout waiting for events");
                }, EVENTS_FETCH_TIMEOUT_MS);

                return;
              }

              // Start fetching
              setLoading(true);
              window._upcomingEventsFetching.add(cacheKey);

              function tryFetch() {
                const url = `/HubContentTab/GetUpcomingEvents?hubId=${currentHubId}&count=${
                  count || DEFAULT_EVENTS_COUNT
                }`;

                fetch(url)
                  .then((response) => {
                    if (!response.ok) {
                      throw new Error(
                        `Failed to fetch events: ${response.status} ${response.statusText}`
                      );
                    }
                    return response.json();
                  })
                  .then((data) => {
                    if (data.error) {
                      throw new Error(data.error);
                    }
                    const eventsData = data.events || [];

                    // Cache the result
                    if (cacheKey) {
                      window._upcomingEventsCache[cacheKey] = {
                        events: eventsData,
                        error: null
                      };
                    }

                    // Update state
                    setEvents(eventsData);
                    setLoading(false);
                    setError(null);

                    // Remove from fetching set
                    window._upcomingEventsFetching.delete(cacheKey);

                    // Notify all listeners
                    if (cacheKey && window._upcomingEventsListeners[cacheKey]) {
                      window._upcomingEventsListeners[cacheKey].forEach(
                        (listener) => listener()
                      );
                    }
                  })
                  .catch((err) => {
                    const errorMsg = err.message || "Unknown error occurred";

                    // Cache the error
                    if (cacheKey) {
                      window._upcomingEventsCache[cacheKey] = {
                        events: [],
                        error: errorMsg
                      };
                    }

                    setError(errorMsg);
                    setLoading(false);
                    window._upcomingEventsFetching.delete(cacheKey);

                    // Notify listeners
                    if (cacheKey && window._upcomingEventsListeners[cacheKey]) {
                      window._upcomingEventsListeners[cacheKey].forEach(
                        (listener) => listener()
                      );
                    }
                  });
              }

              tryFetch();

              // Cleanup
              return () => {
                if (timeoutRef.current) {
                  clearTimeout(timeoutRef.current);
                  timeoutRef.current = null;
                }
              };
            },
            [currentHubId, cacheKey, count]
          );

          // Likewise, skip synthetic resize events so the editor doesn't thrash.
          if (loading) {
            // Add min-height to prevent flash - approximate height of 3-5 event cards
            return React.createElement(
              "div",
              {
                className: "text-center p-5",
                style: { minHeight: "800px" }
              },
              React.createElement("i", { className: "fas fa-spinner fa-spin" }),
              " Loading events..."
            );
          }

          if (error) {
            return React.createElement(
              "div",
              { className: "alert alert-danger" },
              `Error loading events: ${error}`
            );
          }

          if (events.length === 0) {
            return React.createElement(
              "div",
              { className: "p-4" },
              React.createElement(
                "p",
                { className: "fs-6" },
                "There are no upcoming events at this time."
              )
            );
          }

          return React.createElement(
            "section",
            {
              id: "upcoming-events-container",
              className: "upcoming-events-container"
            },
            title &&
              React.createElement(
                sectionLevel,
                {
                  className: "page-title-h3"
                },
                title
              ),
            React.createElement(
              "div",
              {
                // Drive the list stacking by an explicit class rather than relying
                // solely on CSS :has() (unsupported in some kiosk/embedded webviews).
                className:
                  layout === "list"
                    ? "card-deck flex-column gap-3 layout-list"
                    : "card-deck flex-column flex-md-row gap-4"
              },
              events.map(function (eventItem, idx) {
                return React.createElement(EventCard, {
                  key: eventItem.id || idx,
                  event: eventItem,
                  layout: layout
                });
              }),
              showViewAllButton &&
                React.createElement(
                  "div",
                  {
                    className: "d-flex justify-content-end mt-3",
                    style: puck ? { marginTop: "1rem" } : {}
                  },
                  React.createElement(
                    "button",
                    {
                      className:
                        "btn btn-primary pi-btn-primary btn-s-md btn-transparent btn-borderless pull-right switchTabs",
                      "data-rel": "events"
                    },
                    "View all meetings & events ",
                    React.createElement("span", {
                      className: "fas fa-chevron-right"
                    })
                  )
                ),
              React.createElement("div", { className: "clearfix" })
            )
          );
        }

        return React.createElement(UpcomingEventsComponent);
      }
    },
    WelcomeBlock: createWelcomeBlock(RichTextField),
    SignupBlock: {
      label: "Signup Block",
      fields: {
        title: {
          type: "text",
          label: "Title",
          defaultValue: "Get updates"
        },
        headingLevel: headingLevelField("Title Level"),
        showTextMessage: {
          type: "radio",
          label: "Show Text Message Option",
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false }
          ]
        },
        emailLabel: {
          type: "text",
          label: "Email Label",
          defaultValue: "Via email"
        },
        phoneLabel: {
          type: "text",
          label: "Phone Label",
          defaultValue: "Via text message"
        },
        buttonText: {
          type: "text",
          label: "Button Text",
          defaultValue: "Sign up"
        },
        successMessage: {
          type: "text",
          label: "Success Message",
          defaultValue: "You have been subscribed successfully!"
        },
        errorMessage: {
          type: "text",
          label: "Error Message",
          defaultValue: "An error occurred. Please try again."
        },
        hubId: {
          type: "custom",
          label: "# Hub ID (auto-detected)",
          render: ({ field, value, onChange }) => {
            function getAutoHubId() {
              if (window.modelData && window.modelData.hubId) {
                const id = window.modelData.hubId;
                if (id && id > 0) {
                  return id;
                }
              }
              if (window.modelData && window.modelData.hub) {
                const hub = window.modelData.hub;
                if (
                  typeof hub.id !== "undefined" &&
                  hub.id !== null &&
                  hub.id > 0
                ) {
                  return hub.id;
                }
                if (
                  typeof hub.Id !== "undefined" &&
                  hub.Id !== null &&
                  hub.Id > 0
                ) {
                  return hub.Id;
                }
              }
              return value || 0;
            }

            const autoHubId = getAutoHubId();
            const hasUpdatedRef = React.useRef(false);

            React.useEffect(() => {
              if (
                !hasUpdatedRef.current &&
                autoHubId > 0 &&
                autoHubId !== value
              ) {
                onChange(autoHubId);
                hasUpdatedRef.current = true;
              }
            }, [autoHubId, value, onChange]);

            return React.createElement(
              "div",
              { className: "puck-field" },
              React.createElement(
                "label",
                { className: "puck-field-label" },
                field.label
              ),
              React.createElement("input", {
                type: "text",
                value: autoHubId > 0 ? autoHubId : "Auto-detecting...",
                className: "form-control",
                readOnly: true,
                disabled: true,
                style: { backgroundColor: "#e9ecef", cursor: "not-allowed" }
              }),
              autoHubId === 0 &&
                React.createElement(
                  "small",
                  {
                    className: "form-text text-warning"
                  },
                  "Hub ID will be auto-detected when available"
                )
            );
          }
        },
        projectId: {
          type: "custom",
          label: "Target List (optional)",
          render: ({ field, value, onChange }) => {
            const [projects, setProjects] = React.useState([]);
            const [loading, setLoading] = React.useState(false);

            function getHubId() {
              if (window.modelData && window.modelData.hubId) {
                const id = window.modelData.hubId;
                if (id && id > 0) {
                  return id;
                }
              }
              if (window.modelData && window.modelData.hub) {
                const hub = window.modelData.hub;
                if (
                  typeof hub.id !== "undefined" &&
                  hub.id !== null &&
                  hub.id > 0
                ) {
                  return hub.id;
                }
                if (
                  typeof hub.Id !== "undefined" &&
                  hub.Id !== null &&
                  hub.Id > 0
                ) {
                  return hub.Id;
                }
              }
              if (window.getPuckEditorData) {
                const data = window.getPuckEditorData();
                if (
                  data &&
                  data.root &&
                  data.root.props &&
                  data.root.props.hubId
                ) {
                  return data.root.props.hubId;
                }
              }
              return 0;
            }

            React.useEffect(() => {
              const hubId = getHubId();
              if (hubId > 0) {
                setLoading(true);
                fetch(`/HubContentTab/GetSignupEligibleProjects?hubId=${hubId}`)
                  .then((response) => response.json())
                  .then((data) => {
                    if (Array.isArray(data)) {
                      setProjects(data);
                    }
                    setLoading(false);
                  })
                  .catch(() => {
                    setLoading(false);
                  });
              }
            }, []);

            return React.createElement(
              "div",
              { className: "puck-field" },
              React.createElement(
                "label",
                { className: "puck-field-label" },
                field.label
              ),
              React.createElement(
                "select",
                {
                  value: value || "",
                  onChange: (e) =>
                    onChange(e.target.value ? parseInt(e.target.value) : null),
                  className: "form-control",
                  disabled: loading
                },
                React.createElement(
                  "option",
                  { value: "" },
                  "Default engagement hub list"
                ),
                loading &&
                  React.createElement(
                    "option",
                    { disabled: true },
                    "Loading..."
                  ),
                !loading &&
                  projects.map((project) =>
                    React.createElement(
                      "option",
                      {
                        key: project.id,
                        value: project.id
                      },
                      project.isCommunicationsTopic
                        ? `[Topic] ${project.name}`
                        : `[Project] ${project.name}`
                    )
                  )
              ),
              React.createElement(
                "small",
                {
                  className: "form-text text-muted"
                },
                "Select a specific project or communications topic, or use the default hub list"
              )
            );
          }
        }
      },
      defaultProps: {
        title: "Get updates",
        headingLevel: "h3",
        showTextMessage: true,
        emailLabel: "Via email",
        phoneLabel: "Via text message",
        buttonText: "Sign up",
        successMessage: "You have been subscribed successfully!",
        errorMessage: "An error occurred. Please try again.",
        hubId: 0,
        projectId: null
      },
      render: function ({
        title,
        headingLevel,
        showTextMessage,
        emailLabel,
        phoneLabel,
        buttonText,
        successMessage,
        errorMessage,
        hubId,
        projectId,
        puck
      }) {
        const [email, setEmail] = React.useState("");
        const [phone, setPhone] = React.useState("");
        const [isSubmitting, setIsSubmitting] = React.useState(false);
        const [submitStatus, setSubmitStatus] = React.useState(null); // null, 'success', 'error'
        const [statusMessage, setStatusMessage] = React.useState("");

        function getHubIdToUse() {
          if (hubId && hubId > 0) {
            return hubId;
          }
          if (puck && puck.rootProps && puck.rootProps.hubId) {
            return puck.rootProps.hubId;
          }
          if (window.modelData && window.modelData.hubId) {
            return window.modelData.hubId;
          }
          if (window.modelData && window.modelData.hub) {
            const hub = window.modelData.hub;
            if (
              typeof hub.id !== "undefined" &&
              hub.id !== null &&
              hub.id > 0
            ) {
              return hub.id;
            }
            if (
              typeof hub.Id !== "undefined" &&
              hub.Id !== null &&
              hub.Id > 0
            ) {
              return hub.Id;
            }
          }
          return 0;
        }

        const handleSubmit = React.useCallback(
          (e) => {
            e.preventDefault();

            const hubIdToUse = getHubIdToUse();
            if (hubIdToUse <= 0) {
              setSubmitStatus("error");
              setStatusMessage(
                "Hub ID not found. Please ensure you are viewing this content on a hub page."
              );
              return;
            }

            if (!email.trim() && !phone.trim()) {
              setSubmitStatus("error");
              setStatusMessage(
                "Please provide either an email address or phone number"
              );
              return;
            }

            setIsSubmitting(true);
            setSubmitStatus(null);

            const formData = new FormData();
            formData.append("hubId", hubIdToUse);
            if (email.trim()) {
              formData.append("email", email.trim());
            }
            if (phone.trim()) {
              formData.append("phone", phone.trim());
            }
            if (projectId && projectId > 0) {
              formData.append("projectId", projectId);
            }

            fetch("/Emails/SubscribeToHub", {
              method: "POST",
              body: formData,
              headers: {
                "X-Requested-With": "XMLHttpRequest"
              }
            })
              .then((response) => response.json())
              .then((data) => {
                setIsSubmitting(false);
                if (data.result === "success") {
                  setSubmitStatus("success");
                  setStatusMessage(data.message || successMessage);
                  setEmail("");
                  setPhone("");
                } else {
                  setSubmitStatus("error");
                  setStatusMessage(data.message || errorMessage);
                }
              })
              .catch(() => {
                setIsSubmitting(false);
                setSubmitStatus("error");
                setStatusMessage(errorMessage);
              });
          },
          [email, phone, successMessage, errorMessage]
        );

        return React.createElement(
          "div",
          { className: "signup" },
          title &&
            title.trim() &&
            React.createElement(
              headingLevel || "h3",
              {
                style: { fontSize: "24px", marginBottom: "1rem" }
              },
              title
            ),
          React.createElement(
            "form",
            {
              className: "signupUpdates m-b",
              onSubmit: handleSubmit,
              "data-parsley-validate": "parsley",
              noValidate: true
            },
            React.createElement(
              "p",
              {
                style: { fontSize: "16px", marginBottom: "0.5rem" }
              },
              React.createElement("img", {
                src: "/Content/Images/mail.svg",
                className: "m-r-sm",
                alt: "email icon",
                style: { marginRight: "0.5rem" }
              }),
              emailLabel || "Via email"
            ),
            React.createElement("input", {
              type: "email",
              name: "email",
              value: email,
              onChange: (e) => setEmail(e.target.value),
              "data-parsley-type": "email",
              // AB#9554: descriptive message so AT users know the expected format (WCAG 3.3.3 AA).
              "data-parsley-type-message":
                "Please enter a valid email address (e.g. name@example.com)",
              className: "textbox email form-control",
              placeholder: "Your email",
              "aria-label": "Your email",
              disabled: isSubmitting,
              style: { marginBottom: "1rem" }
            }),
            showTextMessage &&
              React.createElement(
                React.Fragment,
                {},
                React.createElement(
                  "p",
                  {
                    className: "or-separator",
                    style: {
                      fontSize: "16px",
                      marginBottom: "0.5rem",
                      marginTop: "1rem"
                    }
                  },
                  "AND | OR"
                ),
                React.createElement(
                  "p",
                  {
                    style: { fontSize: "16px", marginBottom: "0.5rem" }
                  },
                  React.createElement("img", {
                    src: "/Content/Images/smartphone.svg",
                    className: "m-r-sm",
                    alt: "smartphone icon",
                    style: { marginRight: "0.5rem" }
                  }),
                  phoneLabel || "Via text message"
                ),
                React.createElement("input", {
                  type: "text",
                  name: "phone",
                  value: phone,
                  onChange: (e) => setPhone(e.target.value),
                  "data-parsley-type": "digits",
                  "data-parsley-minlength": "10",
                  "data-parsley-pattern": "^[\\d\\+\\-\\.\\(\\)\\/\\s]*$",
                  // AB#9554: descriptive messages for each phone rule (WCAG 3.3.3 AA).
                  "data-parsley-type-message":
                    "Please enter your phone number using digits only (e.g. 5551234567)",
                  "data-parsley-minlength-message":
                    "Please enter a phone number with at least 10 digits",
                  "data-parsley-pattern-message":
                    "Please enter a valid phone number (digits, spaces, and + - . ( ) / are allowed)",
                  className: "textbox phone form-control",
                  placeholder: "Your phone number",
                  "aria-label": "Your phone number",
                  disabled: isSubmitting,
                  style: { marginBottom: "1rem" }
                })
              ),
            submitStatus === "success" &&
              React.createElement(
                "div",
                {
                  className: "success-msg alert alert-success",
                  // AB#9553: role=status => polite announcement of the confirmation (WCAG 4.1.3 AA).
                  role: "status",
                  "aria-live": "polite",
                  style: { display: "block", marginBottom: "1rem" }
                },
                statusMessage || successMessage
              ),
            submitStatus === "error" &&
              React.createElement(
                "div",
                {
                  className: "error-msg alert alert-danger",
                  // AB#9553: role=alert => assertive announcement of the error (WCAG 4.1.3 AA).
                  role: "alert",
                  style: { display: "block", marginBottom: "1rem" }
                },
                statusMessage || errorMessage
              ),
            React.createElement(
              "button",
              {
                type: "submit",
                className: "btn btn-primary pi-btn-primary signupBtn validate",
                disabled: isSubmitting,
                style: { marginBottom: "1rem" }
              },
              isSubmitting ? "Subscribing..." : buttonText || "Sign up"
            ),
            React.createElement("div", { style: { clear: "both" } })
          ),
          React.createElement("div", { className: "clearfix" })
        );
      }
    }
  }
};

// Store references
let editorRoot = null;
let currentData = {
  content: [],
  root: { props: {} }
};

// Initialize Puck Editor
window.initializePuckEditor = function (contentData) {
  const container = document.getElementById("puck-editor-container");
  if (!container) {
    return;
  }

  // Cleanup existing editor first
  if (editorRoot) {
    try {
      editorRoot.unmount();
    } catch (e) {
      // Error unmounting existing editor
    }
    editorRoot = null;
  }

  // Clear existing content
  container.innerHTML = "";

  try {
    // Parse content data or use default structure
    let data = {
      content: [],
      root: { props: {} }
    };

    if (contentData) {
      if (typeof contentData === "string") {
        try {
          const parsed = JSON.parse(contentData);
          if (parsed && typeof parsed === "object") {
            data = parsed;
          }
        } catch (e) {
          // Invalid JSON data, starting with empty editor
        }
      } else if (typeof contentData === "object" && contentData !== null) {
        data = contentData;
      }
    }

    // Ensure data has the required structure
    if (!data.content) {
      data.content = [];
    }
    if (!data.root) {
      data.root = { props: {} };
    }
    if (!data.root.props) {
      data.root.props = {};
    }

    // The Puck editor keys canvas items by props.id. Content created through
    // the editor always has ids, but content written by scripts, imports, or
    // older tooling may not — and without unique ids Puck renders every item
    // as a duplicate of the last one and click-to-select breaks. Inject a
    // stable id for any item that lacks one before handing data to <Puck>.
    if (Array.isArray(data.content)) {
      data.content.forEach(function (item, index) {
        if (!item.props) {
          item.props = {};
        }
        if (!item.props.id) {
          item.props.id = (item.type || "Component") + "-imported-" + index;
        }
      });
    }

    // Ensure hubId is always set in root props from window.modelData if available
    if (!data.root.props.hubId || data.root.props.hubId === 0) {
      // Try to get hubId from window.modelData
      if (window.modelData) {
        if (window.modelData.hubId) {
          data.root.props.hubId = window.modelData.hubId;
        } else if (window.modelData.hub) {
          const hub = window.modelData.hub;
          if (typeof hub.id !== "undefined" && hub.id !== null) {
            data.root.props.hubId = hub.id;
          } else if (typeof hub.Id !== "undefined" && hub.Id !== null) {
            data.root.props.hubId = hub.Id;
          }
        }
      }
    }

    currentData = data;

    // Deterministic editor marker: data-bound widgets (e.g. CommunityQA) render static
    // previews with zero fetches while editing. Set before the editor mounts so component
    // render() sees it on first paint (belt-and-suspenders alongside puck.isEditing).
    window.__puckEditing = true;

    // Create React root and render Puck editor
    editorRoot = ReactDOM.createRoot(container);

    editorRoot.render(
      React.createElement(Puck, {
        key: Date.now(), // Force React to remount the component with new data
        config: puckConfig,
        data: data,
        onPublish: function (newData) {
          // Ensure hubId is maintained in root props
          if (!newData.root.props.hubId || newData.root.props.hubId === 0) {
            if (window.modelData && window.modelData.hubId) {
              newData.root.props.hubId = window.modelData.hubId;
            } else if (window.modelData && window.modelData.hub) {
              const hub = window.modelData.hub;
              if (typeof hub.id !== "undefined" && hub.id !== null) {
                newData.root.props.hubId = hub.id;
              } else if (typeof hub.Id !== "undefined" && hub.Id !== null) {
                newData.root.props.hubId = hub.Id;
              }
            }
          }
          currentData = newData;
        },
        onChange: function (newData) {
          // Ensure hubId is maintained in root props
          if (!newData.root.props.hubId || newData.root.props.hubId === 0) {
            if (window.modelData && window.modelData.hubId) {
              newData.root.props.hubId = window.modelData.hubId;
            } else if (window.modelData && window.modelData.hub) {
              const hub = window.modelData.hub;
              if (typeof hub.id !== "undefined" && hub.id !== null) {
                newData.root.props.hubId = hub.id;
              } else if (typeof hub.Id !== "undefined" && hub.Id !== null) {
                newData.root.props.hubId = hub.Id;
              }
            }
          }
          currentData = newData;
          // Fire a custom event for real-time updates in other parts of the UI
          document.dispatchEvent(
            new CustomEvent("puck:dataChange", { detail: { data: newData } })
          );
        },
        headerPath: null,
        viewports: [
          {
            width: MOBILE_VIEWPORT_WIDTH,
            height: "auto",
            label: "Mobile",
            icon: React.createElement("i", { className: "fas fa-mobile-alt" })
          },
          {
            width: TABLET_VIEWPORT_WIDTH,
            height: "auto",
            label: "Tablet",
            icon: React.createElement("i", { className: "fas fa-tablet-alt" })
          },
          {
            width: DESKTOP_VIEWPORT_WIDTH,
            height: "auto",
            label: "Desktop",
            icon: React.createElement("i", { className: "fas fa-desktop" })
          }
        ],
        iframe: {
          enabled: true,
          waitForStyles: true
        },
        initialViewport: {
          width: DESKTOP_VIEWPORT_WIDTH,
          height: "auto"
        },
        // Override UI components to customize labels
        overrides: {
          headerActions: () => null, // Hide the publish button
          header: () => null // Hide the entire header
        }
      })
    );
  } catch (error) {
    container.innerHTML = '<p class="text-danger">Error loading editor.</p>';
  }
};

// Get current editor data
window.getPuckEditorData = function () {
  return currentData;
};

// Cleanup function
window.cleanupPuckEditor = function () {
  if (editorRoot) {
    editorRoot.unmount();
    editorRoot = null;
    currentData = {
      content: [],
      root: { props: {} }
    };
  }

  // Clean up message handler
  const container = document.getElementById("puck-editor-container");
  if (container && container._messageHandler) {
    window.removeEventListener("message", container._messageHandler);
    container._messageHandler = null;
  }
};

// Render Puck content (for display)
window.renderPuckContent = function (container, data) {
  if (!container) {
    return;
  }

  try {
    let parsedData = {
      content: [],
      root: { props: {} }
    };

    if (data) {
      // Trim whitespace to handle edge cases
      const trimmedData = typeof data === "string" ? data.trim() : data;

      if (typeof trimmedData === "string") {
        // Handle empty or whitespace-only strings
        if (trimmedData === "" || trimmedData === "{}") {
          // Use default empty structure
          parsedData = {
            content: [],
            root: { props: {} }
          };
        } else {
          const parsed = JSON.parse(trimmedData);
          if (parsed && typeof parsed === "object") {
            parsedData = parsed;
          }
        }
      } else if (typeof trimmedData === "object") {
        parsedData = trimmedData;
      }
    }

    // Ensure data has the required structure
    if (!parsedData.content) {
      parsedData.content = [];
    }
    if (!parsedData.root) {
      parsedData.root = { props: {} };
    }
    if (!parsedData.root.props) {
      parsedData.root.props = {};
    }

    const root = ReactDOM.createRoot(container);

    root.render(
      React.createElement(Render, {
        config: puckConfig,
        data: parsedData
      })
    );
  } catch (error) {
    container.innerHTML = "<p>Error displaying content.</p>";
  }
};

// Auto-render on page load
document.addEventListener("DOMContentLoaded", function () {
  // Function to render inline content from data-puck-content attribute
  function renderInlineContent(container) {
    const contentData = container.getAttribute("data-puck-content");
    const isLoaded = container.getAttribute("data-content-loaded") === "true";

    if (isLoaded) {
      return;
    }

    // Mark as loaded to prevent duplicate rendering
    container.setAttribute("data-content-loaded", "true");

    if (contentData) {
      try {
        window.renderPuckContent(container, contentData);
      } catch (e) {
        container.innerHTML =
          '<p class="text-danger p-3">Error loading content.</p>';
      }
    }
  }

  // Cache for content tabs data
  let contentTabsCache = null;

  // Function to load all content tabs once
  function loadAllContentTabs() {
    if (contentTabsCache) {
      return Promise.resolve(contentTabsCache);
    }

    const engagementHubId =
      window.modelData && window.modelData.hub && window.modelData.hub.id;
    if (!engagementHubId) {
      return Promise.reject(new Error("No engagement hub ID found"));
    }

    return fetch(
      "/HubContentTab/GetPublicTabs?engagementHubId=" + engagementHubId
    )
      .then((response) => response.json())
      .then((data) => {
        // Cache the tabs data
        contentTabsCache = data || [];
        return contentTabsCache;
      });
  }

  // Function to load content tab data via AJAX
  function loadContentTab(container) {
    const tabId = parseInt(container.getAttribute("data-content-tab-id"));
    const isLoaded = container.getAttribute("data-content-loaded") === "true";

    if (!tabId || isLoaded) {
      return;
    }

    // Mark as loaded to prevent duplicate requests
    container.setAttribute("data-content-loaded", "true");

    // Fetch all tabs and find the one we need
    loadAllContentTabs()
      .then(function (tabs) {
        const tab = tabs.find(function (t) {
          return t.id === tabId;
        });

        if (tab && tab.contentData) {
          try {
            const contentData =
              typeof tab.contentData === "string"
                ? JSON.parse(tab.contentData)
                : tab.contentData;
            window.renderPuckContent(container, contentData);
          } catch (e) {
            container.innerHTML =
              '<p class="text-danger p-3">Error loading content.</p>';
          }
        } else {
          // No content, show empty state
          container.innerHTML =
            '<p class="text-muted p-3">No content available.</p>';
        }
      })
      .catch(function () {
        container.innerHTML =
          '<p class="text-danger p-3">Failed to load content.</p>';
      });
  }

  // Helper function to load content for a specific tab pane
  function loadContentForPane(paneElement) {
    if (!paneElement) {
      return;
    }

    /**
     * Check for inline content first
     */
    const inlineContainer = paneElement.querySelector(
      ".content-tab-container[data-puck-content]"
    );
    if (inlineContainer) {
      renderInlineContent(inlineContainer);
    } else {
      /**
       * Fall back to AJAX loading
       */
      const container = paneElement.querySelector(
        ".content-tab-container[data-content-tab-id]"
      );
      if (container) {
        loadContentTab(container);
      }
    }
  }

  // Function to load content based on URL hash
  function loadContentFromHash() {
    const hash = window.location.hash;
    if (hash && hash.length > 1) {
      // Try to find the tab pane by hash (could be slug or content-{id})
      const targetElement = document.querySelector(hash);
      if (targetElement && targetElement.classList.contains("tab-pane")) {
        loadContentForPane(targetElement);
      } else {
        /*
         * If not found by hash, try to find by data-tab-slug attribute
         * This handles cases where the hash might be a slug that matches a container's slug
         */
        const containerBySlug = document.querySelector(
          `.content-tab-container[data-tab-slug="${hash.substring(1)}"]`
        );
        if (containerBySlug) {
          const pane = containerBySlug.closest(".tab-pane");
          if (pane) {
            loadContentForPane(pane);
          }
        }
      }
    }
  }

  // Render inline content that's initially visible (data-puck-content approach)
  const visibleInlineContainers = document.querySelectorAll(
    ".tab-pane.active .content-tab-container[data-puck-content]"
  );
  visibleInlineContainers.forEach(renderInlineContent);

  // Load content tabs that are initially visible (AJAX approach)
  const visibleContainers = document.querySelectorAll(
    ".tab-pane.active .content-tab-container[data-content-tab-id]"
  );
  visibleContainers.forEach(loadContentTab);

  // Also check URL hash for content tabs (in case tab isn't marked active yet)
  loadContentFromHash();

  /**
   * Also check after a short delay to catch tabs activated programmatically (like from Hub.js initializeDefaultTab)
   */
  setTimeout(function () {
    loadContentFromHash();
    /**
     * Also check for any active tab panes that might have been activated
     */
    const activePanes = document.querySelectorAll(".tab-pane.active");
    activePanes.forEach(function (pane) {
      loadContentForPane(pane);
    });
  }, TAB_LOAD_DELAY_MS);

  // Listen for hash changes (when user navigates via URL)
  window.addEventListener("hashchange", function () {
    loadContentFromHash();
  });

  // Listen for tab changes to load content on demand
  const tabElements = document.querySelectorAll('a[data-bs-toggle="tab"]');
  tabElements.forEach(function (tab) {
    tab.addEventListener("shown.bs.tab", function (e) {
      const targetId = e.target.getAttribute("href");
      if (targetId && targetId.startsWith("#")) {
        const targetElement = document.querySelector(targetId);
        if (targetElement) {
          loadContentForPane(targetElement);
        }
      }
    });
  });

  /*
   * Also listen for when tabs are shown via showTab() function (used by Hub.js)
   * This handles the case where tabs are activated programmatically before Bootstrap events fire
   */
  const observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (
        mutation.type === "attributes" &&
        mutation.attributeName === "class"
      ) {
        const target = mutation.target;
        if (
          target.classList.contains("tab-pane") &&
          target.classList.contains("active")
        ) {
          // Small delay to ensure tab is fully activated
          setTimeout(() => {
            loadContentForPane(target);
          }, TAB_ACTIVATION_DELAY_MS);
        }
      }
    });
  });

  // Observe all tab panes for class changes
  const tabPanes = document.querySelectorAll(".tab-pane");
  tabPanes.forEach((pane) => {
    observer.observe(pane, { attributes: true, attributeFilter: ["class"] });
  });

  // Handle modal cleanup
  const modal = document.getElementById("content-tab-modal");
  if (modal) {
    modal.addEventListener("hidden.bs.modal", window.cleanupPuckEditor);
  }
});
